'use strict';

const libPictView = require('pict-view');
const libAvatarHelper = require('./AvatarHelper.js');
const libLoopMarkdown = require('../LoopMarkdown.js');
const libMarkdownRender = require('./MarkdownRender.js');

// The module's bundled built-in sticker shapes, shown read-only in the Stickers manager.
let _BUILTIN_STICKERS = [];
try { _BUILTIN_STICKERS = require('pict-section-moodboard/source/sources/StickerSource-Base.js').BUILTIN_STICKERS || []; }
catch (pError) { _BUILTIN_STICKERS = []; }

// Build-time version stamp (generated by build/stamp-version.js; gitignored). Shown in Appearance.
let _Version = { Version: '0.0.0', Commit: 'dev', BuildDate: '' };
try { _Version = require('../Plansheet-Version.js'); } catch (pError) { /* not stamped */ }
const _VersionLabel = (function ()
{
	let tmpLabel = 'Plansheet v' + (_Version.Version || '0.0.0');
	let tmpExtra = [];
	if (_Version.Commit && _Version.Commit !== 'dev' && _Version.Commit !== 'unknown') { tmpExtra.push(_Version.Commit); }
	if (_Version.BuildDate) { tmpExtra.push(_Version.BuildDate); }
	if (tmpExtra.length) { tmpLabel += ' (' + tmpExtra.join(', ') + ')'; }
	return tmpLabel;
})();

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const _MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function _formatDate(pTimestamp)
{
	if (!pTimestamp) { return ''; }
	let tmpDate = new Date(pTimestamp);
	if (isNaN(tmpDate.getTime())) { return ''; }
	return _MONTHS[tmpDate.getMonth()] + ' ' + tmpDate.getDate() + ', ' + tmpDate.getFullYear();
}

// The shortest the top-bar crop band gets, in board units. A card lands on the canvas 120 tall, so a band
// under about this leaves the author's first sticker hanging out of the frame (WI #360).
const _TOPBAR_MIN_BAND = 200;

const _KNOWN_SECTIONS = { profile: 1, about: 1, appearance: 1, account: 1, notifications: 1, tokens: 1, connections: 1, plansheets: 1, team: 1, roles: 1, org: 1, topbar: 1, docsync: 1, draftingkit: 1, messages: 1, stickers: 1, lanes: 1 };

class PictViewUserSettings extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._activeSection = 'profile';
		this._tokensRequested = false;
		this._lastRevealedToken = '';
		this._agentTokenIDUser = 0;
		this._teamLoaded = false;
		this._rolesLoaded = false;
		this._orgLoaded = false;
		this._docsyncLoaded = false;
		this.pict.AppData.TokenSettings = this.pict.AppData.TokenSettings || { Rows: [], EmptySlot: [] };
	}

	_can(pKey)
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		return !!(tmpUser.IsPlatformAdmin || (Array.isArray(tmpUser.Entitlements) && tmpUser.Entitlements.indexOf(pKey) >= 0));
	}

	_profile()
	{
		return (this.pict.AppData.Account && this.pict.AppData.Account.Profile) || {};
	}

	// ── section content builders ─────────────────────────────────────────────
	// The photo block is rebuilt on its own (id="us-photo-block") after a change,
	// so uploading a photo does not discard unsaved edits in the fields below it.
	_photoBlockHTML()
	{
		let tmpP = this._profile();
		let tmpHasPhoto = !!tmpP.PhotoHash;
		return `
		<span class="cst-set-photo-preview">${libAvatarHelper.avatarHTML(tmpP, { Size: 88 })}</span>
		<div class="cst-set-photo-actions">
			<div class="cst-set-photo-btns">
				<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].pickPhoto()">${tmpHasPhoto ? 'Change photo' : 'Upload photo'}</button>
				${tmpHasPhoto ? `<button class="cst-set-photo-remove" onclick="_Pict.views['Plansheet-UserSettings'].removePhoto()">Remove</button>` : ''}
			</div>
			<span class="cst-set-photo-hint">JPEG or PNG, up to 8 MB. We crop a square avatar from it.</span>
			<span id="us-photo-msg" class="cst-set-msg"></span>
		</div>
		<input id="us-photo-input" type="file" accept="image/*" style="display:none" onchange="_Pict.views['Plansheet-UserSettings'].onPhotoPicked(this)" />`;
	}

	_section_profile()
	{
		let tmpP = this._profile();
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Profile</h2>
	<p class="cst-set-sub">How you appear to your team.</p>
	<div class="cst-set-photo" id="us-photo-block">${this._photoBlockHTML()}</div>
	<label class="cst-set-field"><span>Display name</span>
		<input id="us-DisplayName" class="cst-set-input" type="text" maxlength="128" value="${_escapeHTML(tmpP.DisplayName || '')}" /></label>
	<label class="cst-set-field"><span>Title</span>
		<input id="us-Title" class="cst-set-input" type="text" maxlength="128" placeholder="e.g. Engineer" value="${_escapeHTML(tmpP.Title || '')}" /></label>
	<label class="cst-set-field"><span>Color</span>
		<span class="cst-set-color-row"><input id="us-Color" class="cst-set-color" type="color" value="${_escapeHTML(tmpP.Color || '#3b6fd4')}" /><span class="cst-set-color-hint">Used for your avatar and identity across the app.</span></span></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveProfile()">Save profile</button>
		<span id="us-profile-msg" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	_section_about()
	{
		return `
<div class="cst-set-section cst-set-section-wide">
	<h2 class="cst-set-h">About Me</h2>
	<p class="cst-set-sub">A rich profile your team can read. Markdown with images, diagrams, and math; the pane on the right previews it.</p>
	<div class="cst-set-about-host" id="AboutMe-EditorHost"></div>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveAbout()">Save About Me</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].viewProfile()">View my profile</button>
		<span id="us-about-msg" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	// ── Appearance (theme / color mode / text size) ──────────────────────────
	// The controls are pict-section-theme views, mounted after the HTML lands (see _mountAppearance).
	// pict-section-theme persists the choices to localStorage, so they save locally for now (a per-user
	// server-side preference is a later step).
	_section_appearance()
	{
		let tmpNavHidden = false;
		try { tmpNavHidden = (typeof localStorage !== 'undefined') && localStorage.getItem('plansheet.nav.hidden') === '1'; } catch (pErr) { tmpNavHidden = false; }
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Appearance</h2>
	<p class="cst-set-sub">Theme, color mode, and text size. These are saved on this device.</p>
	<div class="cst-set-appearance" id="Settings-Appearance-Theme"></div>
	${this._sidebarTabsPanel()}
	<label class="cst-set-navtoggle">
		<input type="checkbox" ${tmpNavHidden ? 'checked' : ''} onchange="_Pict.PictApplication.setNavHidden(this.checked)">
		<span class="cst-set-navtoggle-txt"><strong>Hide the left navigation</strong><span class="cst-set-sub">Lean on the launcher (the 9-square button or Cmd+K) and the keyboard. Saved on this device.</span></span>
	</label>
	<div class="cst-set-appearance-footer">${_escapeHTML(_VersionLabel)}</div>
</div>`;
	}

	// ── Sidebar tabs (which Notebook tabs show, and which opens first) ────────
	// The catalog and the stored prefs are owned by the Notebook view; we only render the controls here
	// and hand changes back to it. "Show" is a checkbox per tab; "Default" is a radio across the row set.
	// A tab's Default radio is disabled while it is hidden. onSidebarTabsChanged reads the DOM and writes.
	_sidebarTabsPanel()
	{
		let tmpNotebook = this.pict.views['Plansheet-Notebook'];
		if (!tmpNotebook || typeof tmpNotebook.tabCatalog !== 'function') { return ''; }
		let tmpCatalog = tmpNotebook.tabCatalog();
		let tmpPrefs = tmpNotebook.getTabPrefs();
		let tmpEnabled = tmpPrefs.Enabled || {};
		// Mirror the Notebook's own default resolution so the radio matches what actually opens.
		let tmpDefault = tmpPrefs.Default;
		if (!tmpDefault || tmpEnabled[tmpDefault] === false) { tmpDefault = 'navigation'; }
		if (tmpEnabled[tmpDefault] === false) { let tmpFirst = tmpCatalog.filter((pT) => tmpEnabled[pT.Key] !== false)[0]; tmpDefault = tmpFirst ? tmpFirst.Key : tmpCatalog[0].Key; }
		let tmpRows = tmpCatalog.map((pTab) =>
		{
			let tmpOn = tmpEnabled[pTab.Key] !== false;
			return `
		<div class="cst-set-tabrow">
			<label class="cst-set-tabrow-show">
				<input type="checkbox" data-tab="${_escapeHTML(pTab.Key)}" ${tmpOn ? 'checked' : ''} onchange="_Pict.views['Plansheet-UserSettings'].onSidebarTabsChanged()">
				<span class="cst-set-tabrow-ico">${pTab.Icon}</span>
				<span class="cst-set-tabrow-lbl">${_escapeHTML(pTab.Label)}</span>
			</label>
			<label class="cst-set-tabrow-def" title="Open this tab first">
				<input type="radio" name="ps-default-tab" data-tab="${_escapeHTML(pTab.Key)}" ${pTab.Key === tmpDefault ? 'checked' : ''} ${tmpOn ? '' : 'disabled'} onchange="_Pict.views['Plansheet-UserSettings'].onSidebarTabsChanged()">
				<span>Default</span>
			</label>
		</div>`;
		}).join('');
		return `
	<div class="cst-set-tabs" id="Settings-Sidebar-Tabs">
		<h3 class="cst-set-h3">Sidebar tabs</h3>
		<p class="cst-set-sub">Pick which tabs the left sidebar shows, and which one opens first. Saved on this device.</p>
		<div class="cst-set-tabs-list">${tmpRows}</div>
	</div>`;
	}

	// Read every row's checkbox and the selected default radio, enforce "at least one shown", and hand
	// the new prefs to the Notebook (which persists and repaints the rail). Then reconcile the radios'
	// enabled/disabled state in place so a just-hidden tab can no longer be the default.
	onSidebarTabsChanged()
	{
		if (typeof document === 'undefined') { return; }
		let tmpNotebook = this.pict.views['Plansheet-Notebook'];
		if (!tmpNotebook || typeof tmpNotebook.setTabPrefs !== 'function') { return; }
		let tmpRoot = document.getElementById('Settings-Sidebar-Tabs');
		if (!tmpRoot) { return; }
		let tmpChecks = Array.prototype.slice.call(tmpRoot.querySelectorAll('.cst-set-tabrow-show input[type=checkbox]'));
		let tmpEnabled = {};
		let tmpAnyOn = false;
		tmpChecks.forEach((pBox) => { tmpEnabled[pBox.getAttribute('data-tab')] = !!pBox.checked; if (pBox.checked) { tmpAnyOn = true; } });
		// Never let the user hide every tab -- snap the box that caused it back on.
		if (!tmpAnyOn)
		{
			let tmpFirst = tmpChecks[0];
			if (tmpFirst) { tmpFirst.checked = true; tmpEnabled[tmpFirst.getAttribute('data-tab')] = true; }
		}
		// Reflect enablement onto the Default radios; move the default off a now-hidden tab.
		let tmpRadios = Array.prototype.slice.call(tmpRoot.querySelectorAll('.cst-set-tabrow-def input[type=radio]'));
		let tmpDefault = '';
		tmpRadios.forEach((pRadio) =>
		{
			let tmpKey = pRadio.getAttribute('data-tab');
			let tmpOn = tmpEnabled[tmpKey] !== false;
			pRadio.disabled = !tmpOn;
			if (!tmpOn && pRadio.checked) { pRadio.checked = false; }
			if (pRadio.checked) { tmpDefault = tmpKey; }
		});
		if (!tmpDefault)
		{
			let tmpFirstOn = tmpRadios.filter((pRadio) => !pRadio.disabled)[0];
			if (tmpFirstOn) { tmpFirstOn.checked = true; tmpDefault = tmpFirstOn.getAttribute('data-tab'); }
		}
		tmpNotebook.setTabPrefs({ Enabled: tmpEnabled, Default: tmpDefault });
	}

	// Mount the theme controls into the Appearance section after its HTML is in place. Re-mounts each
	// time the section is shown (the template wipes the host).
	_mountAppearance()
	{
		let tmpTheme = this.pict.providers && this.pict.providers['Theme-Section'];
		if (tmpTheme && typeof tmpTheme.mount === 'function')
		{
			tmpTheme.mount({ Container: '#Settings-Appearance-Theme', Views: ['Picker', 'ModeToggle', 'ScaleSelect'] });
		}
		else if (typeof document !== 'undefined')
		{
			let tmpHost = document.getElementById('Settings-Appearance-Theme');
			if (tmpHost) { tmpHost.innerHTML = '<div class="cst-set-placeholder">Theme controls are not available in this build.</div>'; }
		}
	}

	// Mount (or remount) the rich About Me editor into its host after the section
	// HTML is in place, seeded from the saved AboutMe. Mirrors PictView-DocEditor.
	_mountAboutEditor()
	{
		let tmpEditor = this.pict.views['Plansheet-AboutMeEditor'];
		let tmpHost = (typeof document !== 'undefined') ? document.getElementById('AboutMe-EditorHost') : null;
		if (!tmpEditor)
		{
			if (tmpHost) { tmpHost.innerHTML = '<div class="cst-set-placeholder">The rich editor is not available in this build.</div>'; }
			return;
		}
		let tmpProfile = this._profile();
		this.pict.AppData.AboutMeEditor = this.pict.AppData.AboutMeEditor || { Segments: [] };
		this.pict.AppData.AboutMeEditor.Segments = [{ Content: (tmpProfile.AboutMe || '') }];
		try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEditor.render();
		tmpEditor.marshalToView();
		if (typeof tmpEditor.setPreviewMode === 'function') { tmpEditor.setPreviewMode('side'); }
	}

	onAboutMeChanged()
	{
		// Clear the "Saved" note once the user edits again.
		this._msg('us-about-msg', '', '');
	}

	saveAbout()
	{
		let tmpEditor = this.pict.views['Plansheet-AboutMeEditor'];
		let tmpContent = (tmpEditor && typeof tmpEditor.getAllContent === 'function') ? tmpEditor.getAllContent('\n\n') : '';
		this._msg('us-about-msg', 'Saving...', '');
		this.pict.providers.UserAPI.updateMe({ AboutMe: tmpContent }).then(() =>
		{
			this._msg('us-about-msg', 'Saved.', 'ok');
			if (this.pict.AppData.Auth && this.pict.AppData.Auth.User) { this.pict.AppData.Auth.User.AboutMe = tmpContent; }
		}).catch((pErr) => this._msg('us-about-msg', pErr.message || 'Could not save.', 'err'));
	}

	viewProfile()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showProfile === 'function') { tmpApp.showProfile(); }
	}

	_section_account()
	{
		let tmpP = this._profile();
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Sign-in</h2>
	<p class="cst-set-sub">Your email and password.</p>
	<label class="cst-set-field"><span>Email</span>
		<input id="us-Email" class="cst-set-input" type="email" value="${_escapeHTML(tmpP.Email || '')}" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].saveEmail()">Update email</button>
		<span id="us-email-msg" class="cst-set-msg"></span>
	</div>
	<hr class="cst-set-rule" />
	<label class="cst-set-field"><span>Current password</span>
		<input id="us-PwCurrent" class="cst-set-input" type="password" autocomplete="current-password" /></label>
	<label class="cst-set-field"><span>New password</span>
		<input id="us-PwNew" class="cst-set-input" type="password" autocomplete="new-password" placeholder="at least 8 characters" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].savePassword()">Change password</button>
		<span id="us-pw-msg" class="cst-set-msg"></span>
	</div>
</div>
${this._twoFactorSection()}`;
	}

	// ── Two-step verification (2FA) ───────────────────────────────────────────
	_twoFactorSection()
	{
		let tmpState = this._twoFactor;
		let tmpFlow = this._twoFactorFlow || {};
		let tmpInner;
		if (!tmpState)
		{
			tmpInner = `<div class="cst-set-placeholder">Loading...</div>`;
		}
		else if (!tmpState.Available)
		{
			let tmpWhy = (tmpState.Reason === 'oauth')
				? 'You sign in through a connected provider, which carries its own protection. Two-step verification covers password sign-in.'
				: 'Two-step verification is available on account-based logins.';
			tmpInner = `<p class="cst-set-sub">${_escapeHTML(tmpWhy)}</p>`;
		}
		else if (tmpState.Enabled)
		{
			let tmpVia = (tmpState.Channel === 'sms') ? 'a text message' : 'email';
			tmpInner = `
	<p class="cst-set-sub"><span class="cst-set-pill-ok">On</span> &nbsp;We send a code by ${tmpVia} each time you sign in with a password.</p>
	${this._recoveryCodesHTML(tmpState)}
	<hr class="cst-set-rule" />
	<label class="cst-set-field"><span>Confirm your password to turn it off</span>
		<input id="us-2fa-offpw" class="cst-set-input" type="password" autocomplete="current-password" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].disableTwoFactor()">Turn off two-step verification</button>
		<span id="us-2fa-msg" class="cst-set-msg"></span>
	</div>`;
		}
		else
		{
			tmpInner = this._twoFactorEnrollHTML(tmpState, tmpFlow);
		}
		let tmpOrgNote = (tmpState && tmpState.OrgRequires)
			? `<p class="cst-set-note-warn">Your organization asks members to turn on two-step verification for password sign-in.</p>` : '';
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Two-step verification</h2>
	<p class="cst-set-sub">Add a one-time code to password sign-in. Accounts that use a connected login (Google, GitHub) are already covered and skip this.</p>
	${tmpOrgNote}
	${tmpInner}
</div>`;
	}

	_twoFactorEnrollHTML(pState, pFlow)
	{
		let tmpPhoneArea;
		if (pState.PhoneVerified)
		{
			tmpPhoneArea = `<p class="cst-set-sub">Phone verified: <strong>${_escapeHTML(pState.PhoneMasked || '')}</strong>. You can receive codes by text.</p>`;
		}
		else if (pFlow.PhoneToken)
		{
			tmpPhoneArea = `
	<p class="cst-set-sub">Enter the code we texted to ${_escapeHTML(pFlow.PhoneMasked || 'your phone')}.</p>
	<label class="cst-set-field"><span>Text code</span>
		<input id="us-2fa-phone-code" class="cst-set-input" type="text" inputmode="numeric" maxlength="6" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].verifyPhoneCode()">Verify phone</button>
		<span id="us-2fa-phone-msg" class="cst-set-msg"></span>
	</div>`;
		}
		else
		{
			tmpPhoneArea = `
	<label class="cst-set-field"><span>Phone for text codes (optional)</span>
		<input id="us-2fa-phone" class="cst-set-input" type="tel" autocomplete="tel" placeholder="+1 555 555 1234" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].sendPhoneCode()">Send code</button>
		<span id="us-2fa-phone-msg" class="cst-set-msg"></span>
	</div>`;
		}

		let tmpTurnOn;
		if (pFlow.EnableToken)
		{
			let tmpVia = (pFlow.EnableChannel === 'sms') ? 'text message' : 'email';
			tmpTurnOn = `
	<p class="cst-set-sub">Enter the code we sent by ${tmpVia} to ${_escapeHTML(pFlow.EnableMasked || 'you')}.</p>
	<label class="cst-set-field"><span>Verification code</span>
		<input id="us-2fa-enable-code" class="cst-set-input" type="text" inputmode="numeric" maxlength="6" /></label>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].confirmTwoFactor()">Confirm and turn on</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].cancelTwoFactorEnroll()">Cancel</button>
		<span id="us-2fa-msg" class="cst-set-msg"></span>
	</div>`;
		}
		else
		{
			let tmpSmsBtn = pState.PhoneVerified
				? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].enableTwoFactor('sms')">Use text messages</button>`
				: '';
			tmpTurnOn = `
	<p class="cst-set-sub">We will send a code to confirm, then ask for one each time you sign in with a password.</p>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].enableTwoFactor('email')">Use email${pState.EmailMasked ? ' (' + _escapeHTML(pState.EmailMasked) + ')' : ''}</button>
		${tmpSmsBtn}
		<span id="us-2fa-msg" class="cst-set-msg"></span>
	</div>`;
		}

		return `${tmpPhoneArea}<hr class="cst-set-rule" />${tmpTurnOn}`;
	}

	// Recovery codes (the freshly generated set is held in _twoFactorCodes and shown
	// once; otherwise just the remaining-count + a (re)generate button).
	_recoveryCodesHTML(pState)
	{
		if (this._twoFactorCodes && this._twoFactorCodes.length)
		{
			let tmpList = this._twoFactorCodes.map((pCode) => `<li>${_escapeHTML(pCode)}</li>`).join('');
			return `
	<div class="cst-set-note-warn">Save these now. Each code works once, and they will not be shown again.</div>
	<ul class="cst-set-codes">${tmpList}</ul>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].copyRecoveryCodes()">Copy codes</button>
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].dismissRecoveryCodes()">Done, I saved them</button>
		<span id="us-2fa-rc-msg" class="cst-set-msg"></span>
	</div>`;
		}
		let tmpN = Number(pState.RecoveryCodesRemaining) || 0;
		let tmpChannelWord = (pState.Channel === 'sms') ? 'phone' : 'email';
		let tmpStatus = (tmpN > 0) ? (tmpN + ' recovery code' + (tmpN === 1 ? '' : 's') + ' remaining') : 'You have not generated recovery codes yet';
		return `
	<p class="cst-set-sub">Recovery codes let you sign in if you lose access to your ${tmpChannelWord}. ${tmpStatus}.</p>
	<div class="cst-set-actions">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].generateRecoveryCodes()">${tmpN > 0 ? 'Regenerate recovery codes' : 'Generate recovery codes'}</button>
		<span id="us-2fa-rc-msg" class="cst-set-msg"></span>
	</div>`;
	}

	generateRecoveryCodes()
	{
		this._msg('us-2fa-rc-msg', 'Generating...', '');
		this.pict.providers.UserAPI.generateRecoveryCodes().then((pBody) =>
		{
			this._twoFactorCodes = (pBody && Array.isArray(pBody.Codes)) ? pBody.Codes : null;
			this._rerenderAccount();
		}).catch((pErr) => this._msg('us-2fa-rc-msg', pErr.message || 'Could not generate.', 'err'));
	}

	copyRecoveryCodes()
	{
		let tmpText = (this._twoFactorCodes || []).join('\n');
		if (typeof navigator !== 'undefined' && navigator.clipboard && tmpText)
		{
			navigator.clipboard.writeText(tmpText).then(() => this._msg('us-2fa-rc-msg', 'Copied.', 'ok')).catch(() => this._msg('us-2fa-rc-msg', 'Could not copy.', 'err'));
		}
	}

	dismissRecoveryCodes()
	{
		this._twoFactorCodes = null;
		this._loadTwoFactor();
	}

	_loadTwoFactor()
	{
		this._twoFactorFlow = {};
		this._twoFactorCodes = null;
		if (!this.pict.providers.UserAPI) { return; }
		this.pict.providers.UserAPI.getTwoFactor().then((pState) =>
		{
			this._twoFactor = pState || { Available: false };
			if (this._activeSection === 'account') { this._setContent(this._section_account()); }
		});
	}

	_rerenderAccount()
	{
		if (this._activeSection === 'account') { this._setContent(this._section_account()); }
	}

	// ── Notifications (per-user email opt-outs) ───────────────────────────────
	_section_notifications()
	{
		if (!this._notifPrefs) { return this._loadingSection('Notifications'); }
		let tmpCanSMS = !!this._notifCanSMS;
		let tmpRows = this._notifPrefs.map((pPref) =>
		{
			let tmpEmail = `<label class="cst-notif-toggle"><input type="checkbox" ${pPref.EmailEnabled ? 'checked' : ''} onchange="_Pict.views['Plansheet-UserSettings'].toggleNotificationPref('${_escapeHTML(pPref.EventKey)}','EmailEnabled',this.checked)"/> Email</label>`;
			let tmpSMS = tmpCanSMS ? `<label class="cst-notif-toggle"><input type="checkbox" ${pPref.SMSEnabled ? 'checked' : ''} onchange="_Pict.views['Plansheet-UserSettings'].toggleNotificationPref('${_escapeHTML(pPref.EventKey)}','SMSEnabled',this.checked)"/> Text</label>` : '';
			return `<div class="cst-notif-row"><div class="cst-notif-label">${_escapeHTML(pPref.Label)}</div><div class="cst-notif-toggles">${tmpEmail}${tmpSMS}</div></div>`;
		}).join('');
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Notifications</h2>
	<p class="cst-set-sub">Choose how each change reaches you. These are your own settings; an organization can also turn a type off for everyone.</p>
	${this._smsOptInHTML(tmpCanSMS)}
	<h3 class="cst-set-sh">What to send me</h3>
	${tmpRows || '<div class="cst-set-placeholder">No notification types yet.</div>'}
	<div class="cst-set-actions"><span id="notif-msg" class="cst-set-msg"></span></div>
</div>`;
	}

	// The text-message (SMS) opt-in block: phone entry + the explicit consent language carriers require for
	// A2P 10DLC. Three states -- verified, code-sent, and the entry form -- with the consent shown wherever the
	// user is about to opt in. Verifying a number flips tmpCanSMS, which reveals the per-event Text toggles below.
	_smsOptInHTML(pVerified)
	{
		let tmpConsent = `<p class="cst-notif-consent">By adding and verifying your mobile number, you agree to receive text messages from Plansheet for the alerts you turn on below &mdash; for example sign-in codes and work item or issue updates. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for help.</p>`;
		if (pVerified)
		{
			return `
<div class="cst-notif-sms">
	<h3 class="cst-set-sh">Text messages</h3>
	<p class="cst-set-sub">Text alerts are on for your verified mobile number. Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for help &mdash; then choose which alerts below you want by text.</p>
	<span id="notif-phone-msg" class="cst-set-msg"></span>
</div>`;
		}
		let tmpFlow = this._notifPhoneFlow || {};
		if (tmpFlow.PhoneToken)
		{
			return `
<div class="cst-notif-sms">
	<h3 class="cst-set-sh">Text messages</h3>
	<p class="cst-set-sub">Enter the 6-digit code we texted to ${_escapeHTML(tmpFlow.PhoneMasked || 'your phone')}.</p>
	<div class="cst-set-field-row">
		<input id="notif-phone-code" class="cst-set-input" type="text" inputmode="numeric" maxlength="6" placeholder="123456" />
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].verifyNotifPhoneCode()">Verify</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].cancelNotifPhone()">Cancel</button>
	</div>
	${tmpConsent}
	<span id="notif-phone-msg" class="cst-set-msg"></span>
</div>`;
		}
		return `
<div class="cst-notif-sms">
	<h3 class="cst-set-sh">Text messages (optional)</h3>
	<p class="cst-set-sub">Add a mobile number to also get the alerts you choose by text message.</p>
	<label class="cst-set-field"><span>Mobile number</span><input id="notif-phone" class="cst-set-input" type="tel" autocomplete="tel" placeholder="+1 555 555 1234" /></label>
	${tmpConsent}
	<label class="cst-notif-consent-check"><input type="checkbox" id="notif-sms-consent" onchange="var b=document.getElementById('notif-sms-send'); if (b) { b.disabled = !this.checked; }"/> <span>I agree to receive these text messages from Plansheet. Message and data rates may apply; reply STOP to opt out.</span></label>
	<div class="cst-set-field-row">
		<button id="notif-sms-send" class="cst-set-btn" disabled onclick="_Pict.views['Plansheet-UserSettings'].sendNotifPhoneCode()">Send verification code</button>
	</div>
	<span id="notif-phone-msg" class="cst-set-msg"></span>
</div>`;
	}

	_loadNotifications()
	{
		if (!this.pict.providers.UserAPI) { return; }
		this.pict.providers.UserAPI.getNotificationPrefs().then((pResult) =>
		{
			this._notifPrefs = (pResult && Array.isArray(pResult.Prefs)) ? pResult.Prefs : [];
			this._notifCanSMS = !!(pResult && pResult.CanSMS);
			if (this._activeSection === 'notifications') { this._setContent(this._section_notifications()); }
		});
	}

	toggleNotificationPref(pEventKey, pChannel, pEnabled)
	{
		this._msg('notif-msg', 'Saving...', '');
		let tmpPatch = {};
		tmpPatch[pChannel] = !!pEnabled;
		this.pict.providers.UserAPI.setNotificationPref(pEventKey, tmpPatch)
			.then((pBody) =>
			{
				(this._notifPrefs || []).forEach((pPref) => { if (pPref.EventKey === pEventKey) { pPref.EmailEnabled = !!pBody.EmailEnabled; pPref.SMSEnabled = !!pBody.SMSEnabled; } });
				this._msg('notif-msg', 'Saved.', 'ok');
			})
			.catch((pErr) => this._msg('notif-msg', pErr.message || 'Could not save.', 'err'));
	}

	// Phone opt-in on the Notifications screen (its own small flow so it does not share the 2FA section's
	// state). setPhone texts a code; verifyPhone confirms and flips CanSMS, revealing the Text toggles.
	sendNotifPhoneCode()
	{
		let tmpAgreed = (typeof document !== 'undefined') ? !!((document.getElementById('notif-sms-consent') || {}).checked) : false;
		if (!tmpAgreed) { this._msg('notif-phone-msg', 'Please check the consent box to continue.', 'err'); return; }
		let tmpPhone = (typeof document !== 'undefined') ? (document.getElementById('notif-phone') || {}).value : '';
		if (!tmpPhone) { this._msg('notif-phone-msg', 'Enter a mobile number.', 'err'); return; }
		this._msg('notif-phone-msg', 'Sending...', '');
		this.pict.providers.UserAPI.setPhone(tmpPhone).then((pBody) =>
		{
			this._notifPhoneFlow = { PhoneToken: pBody.ChallengeToken, PhoneMasked: pBody.PhoneMasked };
			if (this._activeSection === 'notifications') { this._setContent(this._section_notifications()); }
		}).catch((pErr) => this._msg('notif-phone-msg', pErr.message || 'Could not send.', 'err'));
	}

	verifyNotifPhoneCode()
	{
		let tmpCode = (typeof document !== 'undefined') ? (document.getElementById('notif-phone-code') || {}).value : '';
		let tmpToken = (this._notifPhoneFlow || {}).PhoneToken;
		this._msg('notif-phone-msg', 'Checking...', '');
		this.pict.providers.UserAPI.verifyPhone(tmpToken, (tmpCode || '').trim()).then(() =>
		{
			this._notifPhoneFlow = {};
			this._loadNotifications();
		}).catch((pErr) => this._msg('notif-phone-msg', pErr.message || 'Incorrect code.', 'err'));
	}

	cancelNotifPhone()
	{
		this._notifPhoneFlow = {};
		if (this._activeSection === 'notifications') { this._setContent(this._section_notifications()); }
	}

	sendPhoneCode()
	{
		let tmpPhone = (typeof document !== 'undefined') ? (document.getElementById('us-2fa-phone') || {}).value : '';
		if (!tmpPhone) { this._msg('us-2fa-phone-msg', 'Enter a phone number.', 'err'); return; }
		this._msg('us-2fa-phone-msg', 'Sending...', '');
		this.pict.providers.UserAPI.setPhone(tmpPhone).then((pBody) =>
		{
			this._twoFactorFlow = Object.assign({}, this._twoFactorFlow, { PhoneToken: pBody.ChallengeToken, PhoneMasked: pBody.PhoneMasked });
			this._rerenderAccount();
		}).catch((pErr) => this._msg('us-2fa-phone-msg', pErr.message || 'Could not send.', 'err'));
	}

	verifyPhoneCode()
	{
		let tmpCode = (typeof document !== 'undefined') ? (document.getElementById('us-2fa-phone-code') || {}).value : '';
		let tmpToken = (this._twoFactorFlow || {}).PhoneToken;
		this._msg('us-2fa-phone-msg', 'Checking...', '');
		this.pict.providers.UserAPI.verifyPhone(tmpToken, (tmpCode || '').trim()).then(() =>
		{
			this._loadTwoFactor();
		}).catch((pErr) => this._msg('us-2fa-phone-msg', pErr.message || 'Incorrect code.', 'err'));
	}

	enableTwoFactor(pChannel)
	{
		this._msg('us-2fa-msg', 'Sending a code...', '');
		this.pict.providers.UserAPI.enableTwoFactor(pChannel).then((pBody) =>
		{
			this._twoFactorFlow = Object.assign({}, this._twoFactorFlow, { EnableToken: pBody.ChallengeToken, EnableChannel: pBody.Channel || pChannel, EnableMasked: pBody.MaskedDestination });
			this._rerenderAccount();
		}).catch((pErr) => this._msg('us-2fa-msg', pErr.message || 'Could not start.', 'err'));
	}

	confirmTwoFactor()
	{
		let tmpCode = (typeof document !== 'undefined') ? (document.getElementById('us-2fa-enable-code') || {}).value : '';
		let tmpToken = (this._twoFactorFlow || {}).EnableToken;
		this._msg('us-2fa-msg', 'Confirming...', '');
		this.pict.providers.UserAPI.confirmTwoFactor(tmpToken, (tmpCode || '').trim()).then(() =>
		{
			this._loadTwoFactor();
		}).catch((pErr) => this._msg('us-2fa-msg', pErr.message || 'Incorrect code.', 'err'));
	}

	cancelTwoFactorEnroll()
	{
		this._twoFactorFlow = {};
		this._rerenderAccount();
	}

	disableTwoFactor()
	{
		let tmpPw = (typeof document !== 'undefined') ? (document.getElementById('us-2fa-offpw') || {}).value : '';
		if (!tmpPw) { this._msg('us-2fa-msg', 'Enter your password.', 'err'); return; }
		this._msg('us-2fa-msg', 'Turning off...', '');
		this.pict.providers.UserAPI.disableTwoFactor(tmpPw).then(() =>
		{
			this._loadTwoFactor();
		}).catch((pErr) => this._msg('us-2fa-msg', pErr.message || 'Could not turn off.', 'err'));
	}

	_section_tokens()
	{
		this._refreshTokens();
		let tmpIsAdmin = !!(this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.IsPlatformAdmin);
		let tmpRows = this.pict.AppData.TokenSettings.Rows;
		let tmpList = tmpRows.length
			? tmpRows.map((pRow) => `
		<div class="cst-set-token-row">
			<div class="cst-set-token-main"><div class="cst-set-token-label">${pRow.Label}</div><div class="cst-set-token-meta">${pRow.CreatedLabel}${pRow.LastUsedLabel}</div></div>
			<button class="cst-set-token-revoke" onclick="_Pict.views['Plansheet-UserSettings'].revokeToken(${pRow.IDUserToken})">Revoke</button>
		</div>`).join('')
			: `<div class="cst-set-placeholder">No tokens yet. Generate one to use the CLI without a password.</div>`;
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">API Tokens</h2>
	<p class="cst-set-sub">Bearer tokens let the plansheet CLI and automation act as you, without a password. The token is shown once when created.</p>
	<div class="cst-set-token-new">
		<input id="us-TokenLabel" class="cst-set-input" type="text" maxlength="128" placeholder="Label (e.g. laptop CLI)" onkeydown="if(event.key==='Enter'){event.preventDefault();_Pict.views['Plansheet-UserSettings'].generateToken();}" />
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].generateToken()">Generate</button>
	</div>
	${tmpList}
	${tmpIsAdmin ? `
	<div style="margin-top:1.5rem;padding-top:1.25rem;border-top:1px solid var(--theme-color-border-default, rgba(128,128,128,0.25));">
		<h2 class="cst-set-h">Mint a token for another user</h2>
		<p class="cst-set-sub">Platform admins only: give an agent user (like the Claude runner, <code>claude@plansheet.io</code>) a bearer token without logging in as it. The token belongs to that user, and is shown once.</p>
		<div class="cst-set-token-new">
			<div id="us-agent-picker-host" style="flex:1;min-width:240px;"></div>
			<input id="us-AgentTokenLabel" class="cst-set-input" type="text" maxlength="128" placeholder="Label (e.g. agent runner)" />
			<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].mintAgentToken()">Mint token</button>
		</div>
	</div>` : ''}
</div>`;
	}

	// -- Connections (which plan sheets a connected app's token may act in) --------------------
	// The authorized set could only ever GROW: creating a plan sheet over a connector widens the calling
	// token across every one of its rows, and nothing ever took one away. So the consent given when a client
	// was first authorized was a floor rather than a ceiling, and no screen even showed what had accumulated.
	// This is that screen, and the only place a set can be narrowed.
	_section_connections()
	{
		this._refreshConnections();
		if (!this._connections) { return this._loadingSection('Connections'); }

		let tmpAvailable = this._connectionsAvailable || [];
		let tmpList = this._connections.length
			? this._connections.map((pConnection) =>
			{
				let tmpChecked = {};
				(pConnection.Plansheets || []).forEach((pPlansheet) => { tmpChecked[pPlansheet.IDCustomer] = true; });
				let tmpBoxes = tmpAvailable.map((pPlansheet) =>
				{
					// The grant's own plan sheet is where the token was issued and where its scope was
					// narrowed. It cannot be removed: a token whose set excluded its own home would point at
					// a tenant it is not authorized for. Revoke is how a connection is removed.
					let tmpIsHome = (pPlansheet.IDCustomer === pConnection.HomeIDCustomer);
					let tmpOn = tmpIsHome || !!tmpChecked[pPlansheet.IDCustomer];
					return `
			<label class="cst-set-check">
				<input type="checkbox" ${tmpOn ? 'checked' : ''} ${tmpIsHome ? 'disabled' : ''}
					onchange="_Pict.views['Plansheet-UserSettings'].toggleConnectionPlansheet(${pConnection.IDOAuthClient}, ${pPlansheet.IDCustomer}, this.checked)" />
				<span>${pPlansheet.Name}${pPlansheet.Code ? ' <code>' + pPlansheet.Code + '</code>' : ''}${tmpIsHome ? ' <em>(created here)</em>' : ''}</span>
			</label>`;
				}).join('');

				return `
		<div class="cst-set-token-row" style="display:block;">
			<div style="display:flex;align-items:baseline;gap:0.75rem;flex-wrap:wrap;">
				<div class="cst-set-token-label">${pConnection.ClientName}</div>
				<div class="cst-set-token-meta">${pConnection.Scope ? 'scope <code>' + pConnection.Scope + '</code>' : 'no scope'}${pConnection.LastUsedAt ? ' &middot; last used ' + String(pConnection.LastUsedAt).slice(0, 10) : ' &middot; never used'}</div>
				<button class="cst-set-token-revoke" style="margin-left:auto;"
					onclick="_Pict.views['Plansheet-UserSettings'].revokeConnection(${pConnection.IDOAuthClient})">Revoke</button>
			</div>
			<div style="margin-top:0.6rem;display:flex;flex-direction:column;gap:0.3rem;">${tmpBoxes}</div>
		</div>`;
			}).join('')
			: '<div class="cst-set-placeholder">No connected apps yet. When you authorize one (an MCP connector, or a node runner), it appears here and you choose which plan sheets it may act in.</div>';

		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Connect Claude</h2>
	<p class="cst-set-sub">Let Claude read and write this plan sheet through a connection you control. The walkthrough below is the same one the API publishes, so a person and a Claude agent are following the same steps.</p>
	<div id="connect-claude-body" class="cst-connect-guide"><div class="cst-set-placeholder">Loading the connection guide...</div></div>
</div>
<div class="cst-set-section">
	<h2 class="cst-set-h">Connections</h2>
	<p class="cst-set-sub">Apps and nodes you have authorized to act as you. A connection can only ever reach the plan sheets ticked here, and only ones you are a member of yourself. Untick to take access away; it applies immediately, to the refresh token as well as the access token.</p>
	<div id="connections-msg" class="cst-set-msg"></div>
	${tmpList}
</div>`;
	}

	// The connections section rebuilds its whole HTML on every data change (toggle, revoke, first load), which
	// recreates the Connect Claude card's container empty. Repaint the guide after each such render so it never
	// vanishes on a toggle. Idempotent; a no-op until the guide has loaded.
	_renderConnections()
	{
		this._setContent(this._section_connections());
		this._paintConnectClaude();
	}

	// The human half of the connect-Claude story: the connect-claude guide from the self-describing API
	// (GET /1.0/API/Guides/connect-claude), rendered as markdown into the card. One fetch, then cached.
	_loadConnectClaudeGuide()
	{
		if (this._connectClaudeGuide) { this._paintConnectClaude(); return; }
		if (!this.pict.providers.UserAPI) { return; }
		this.pict.providers.UserAPI.getApiGuide('connect-claude').then((pGuide) =>
		{
			this._connectClaudeGuide = pGuide || { Body: '' };
			if (this._activeSection === 'connections') { this._paintConnectClaude(); }
		});
	}

	_paintConnectClaude()
	{
		if (typeof document === 'undefined') { return; }
		let tmpTarget = document.getElementById('connect-claude-body');
		if (!tmpTarget || !this._connectClaudeGuide) { return; }
		libMarkdownRender.renderInto(this.pict.providers.Content, tmpTarget, this._connectClaudeGuide.Body || '',
			'<p class="cst-set-placeholder">The connection guide is not available right now.</p>');
	}

	_refreshConnections()
	{
		if (this._connectionsRequested) { return; }
		this._connectionsRequested = true;
		if (!this.pict.providers.UserAPI) { return; }
		this.pict.providers.UserAPI.getOAuthConnections().then((pResult) =>
		{
			this._connections = pResult.Connections || [];
			this._connectionsAvailable = pResult.AvailablePlansheets || [];
			if (this._activeSection === 'connections') { this._renderConnections(); }
		});
	}

	toggleConnectionPlansheet(pIDOAuthClient, pIDCustomer, pEnabled)
	{
		let tmpConnection = (this._connections || []).filter((pRow) => pRow.IDOAuthClient === pIDOAuthClient)[0];
		if (!tmpConnection) { return; }
		this._msg('connections-msg', 'Saving...', '');

		// Send the WHOLE set rather than a delta, so what the screen shows and what the server stores can
		// never drift apart on a dropped request.
		let tmpSet = (tmpConnection.Plansheets || []).map((pPlansheet) => pPlansheet.IDCustomer);
		if (pEnabled) { if (tmpSet.indexOf(pIDCustomer) < 0) { tmpSet.push(pIDCustomer); } }
		else { tmpSet = tmpSet.filter((pID) => pID !== pIDCustomer); }

		this.pict.providers.UserAPI.setOAuthConnectionPlansheets(pIDOAuthClient, tmpSet)
			.then((pBody) =>
			{
				tmpConnection.Plansheets = pBody.Plansheets || [];
				this._msg('connections-msg', 'Saved.', 'ok');
				if (this._activeSection === 'connections') { this._renderConnections(); }
			})
			.catch((pError) =>
			{
				// Re-read from the server rather than leaving a checkbox showing something that was refused.
				this._msg('connections-msg', (pError && pError.message) || 'Could not save.', 'err');
				this._connectionsRequested = false;
				this._refreshConnections();
			});
	}

	revokeConnection(pIDOAuthClient)
	{
		let tmpConnection = (this._connections || []).filter((pRow) => pRow.IDOAuthClient === pIDOAuthClient)[0];
		let tmpName = tmpConnection ? tmpConnection.ClientName : 'this connection';
		if (!confirm('Revoke "' + tmpName + '"? Its tokens stop working immediately and it will have to be authorized again.')) { return; }
		this._msg('connections-msg', 'Revoking...', '');
		this.pict.providers.UserAPI.revokeOAuthConnection(pIDOAuthClient)
			.then(() =>
			{
				this._connections = (this._connections || []).filter((pRow) => pRow.IDOAuthClient !== pIDOAuthClient);
				this._msg('connections-msg', 'Revoked.', 'ok');
				if (this._activeSection === 'connections') { this._renderConnections(); }
			})
			.catch((pError) => this._msg('connections-msg', (pError && pError.message) || 'Could not revoke.', 'err'));
	}


	// ── Plan sheets (per-device default + membership list) ───────────────────
	// The preferred default plansheet is stored in localStorage so it persists
	// across sessions without a schema change. The server already remembers the
	// last-visited plansheet via the LastLoginDate stamp on every switch; this
	// setting lets users pin a hard default that overrides that order.
	_getPinnedCustomer()
	{
		try { return parseInt(localStorage.getItem('plansheet.pinned_customer') || '0', 10) || 0; }
		catch (pErr) { return 0; }
	}

	_setPinnedCustomer(pIDCustomer)
	{
		try
		{
			if (pIDCustomer) { localStorage.setItem('plansheet.pinned_customer', String(pIDCustomer)); }
			else { localStorage.removeItem('plansheet.pinned_customer'); }
		}
		catch (pErr) { /* storage unavailable */ }
	}

	_section_plansheets()
	{
		if (!this._plansheetsLoaded) { this._loadPlansheets(); return this._loadingSection('Plan sheets'); }
		let tmpTenants = this._plansheetTenants || [];
		let tmpPinned = this._getPinnedCustomer();
		let tmpPinnedName = '';
		if (tmpPinned)
		{
			let tmpPinnedRow = tmpTenants.find((pT) => pT.IDCustomer === tmpPinned);
			tmpPinnedName = tmpPinnedRow ? _escapeHTML(tmpPinnedRow.CustomerName || 'Untitled') : 'a plan sheet you no longer belong to';
		}
		let tmpPinNote = tmpPinned
			? `<span class="cst-set-msg cst-set-msg-ok">Pinned to <strong>${tmpPinnedName}</strong> on this device. You will land there on every login.</span>`
			: `<span class="cst-set-sub">Not set. You will land on the plan sheet you visited most recently.</span>`;
		let tmpOpts = tmpTenants.map((pT) =>
			`<option value="${pT.IDCustomer}"${pT.IDCustomer === tmpPinned ? ' selected' : ''}>${_escapeHTML(pT.CustomerName || 'Untitled')} (${_escapeHTML(pT.RoleLabel || 'Member')})</option>`
		).join('');
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Plan sheets</h2>
	<p class="cst-set-sub">You belong to ${tmpTenants.length} plan sheet${tmpTenants.length !== 1 ? 's' : ''}. Plansheet remembers the one you visited most recently and returns you there on your next login. You can pin a specific plan sheet below if you prefer a hard default.</p>
	<div class="cst-set-field" style="margin-top:1rem;">
		<span>Default plan sheet <em style="font-weight:400;color:var(--theme-color-text-secondary,#5b6376)">(this device)</em></span>
		<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
			<select id="us-PinnedCustomer" class="cst-set-input" style="flex:1;min-width:200px;">
				<option value="0">Last visited (automatic)</option>
				${tmpOpts}
			</select>
			<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].savePinnedCustomer()">Save</button>
			${tmpPinned ? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].clearPinnedCustomer()">Clear</button>` : ''}
		</div>
		<div style="margin-top:6px;">${tmpPinNote}</div>
		<span id="us-plansheets-msg" class="cst-set-msg"></span>
	</div>
	<div class="cst-set-divider"></div>
	<h3 class="cst-set-sh">Memberships</h3>
	<div class="cst-ps-settings-list">
		${tmpTenants.length ? tmpTenants.map((pT) => `
		<div class="cst-ps-settings-row${pT.IsCurrent ? ' is-current' : ''}">
			<div class="cst-ps-settings-main">
				<span class="cst-ps-settings-name">${_escapeHTML(pT.CustomerName || 'Untitled')}</span>
				<span class="cst-ps-settings-role">${_escapeHTML(pT.DisplayName || '')} &middot; ${_escapeHTML(pT.RoleLabel || 'Member')}${pT.IsCurrent ? ' &middot; <strong>active</strong>' : ''}</span>
			</div>
			${!pT.IsCurrent ? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].switchToSheet(${pT.IDCustomer})">Switch</button>` : ''}
		</div>`).join('') : '<div class="cst-set-placeholder">No plan sheets found.</div>'}
	</div>
</div>`;
	}

	_loadPlansheets()
	{
		this._plansheetsLoaded = false;
		let tmpClient = require('../providers/FrameworkClient.js');
		tmpClient.getJSON(this.pict, '/1.0/Account/Tenants').then((pBody) =>
		{
			this._plansheetTenants = (pBody && Array.isArray(pBody.Tenants)) ? pBody.Tenants : [];
			this._plansheetsLoaded = true;
			if (this._activeSection === 'plansheets') { this._setContent(this._section_plansheets()); }
		}).catch(() =>
		{
			this._plansheetTenants = [];
			this._plansheetsLoaded = true;
			if (this._activeSection === 'plansheets') { this._setContent(this._section_plansheets()); }
		});
	}

	savePinnedCustomer()
	{
		let tmpEl = typeof document !== 'undefined' ? document.getElementById('us-PinnedCustomer') : null;
		let tmpVal = tmpEl ? parseInt(tmpEl.value, 10) : 0;
		this._setPinnedCustomer(tmpVal);
		this._setContent(this._section_plansheets());
	}

	clearPinnedCustomer()
	{
		this._setPinnedCustomer(0);
		this._setContent(this._section_plansheets());
	}

	switchToSheet(pIDCustomer)
	{
		let tmpClient = require('../providers/FrameworkClient.js');
		this._msg('us-plansheets-msg', 'Switching...', '');
		tmpClient.postJSON(this.pict, '/1.0/Session/Context/' + pIDCustomer, {}).then((pBody) =>
		{
			if (pBody && pBody.Switched)
			{
				// Reduce the record-specific route to its feature area before reloading, so the switch does
				// not land on a not-found in the new tenant (see WI #328). Plain reload if the app is absent.
				let tmpApp = this.pict && this.pict.PictApplication;
				if (tmpApp && typeof tmpApp.reloadForActivePlansheetChange === 'function') { tmpApp.reloadForActivePlansheetChange(); }
				else { window.location.reload(); }
			}
			else { this._msg('us-plansheets-msg', (pBody && pBody.Error) || 'Could not switch.', 'err'); }
		}).catch((pErr) => this._msg('us-plansheets-msg', pErr.message || 'Could not switch.', 'err'));
	}

	_loadingSection(pName)
	{
		return `<div class="cst-set-section cst-set-section-wide"><h2 class="cst-set-h">${_escapeHTML(pName)}</h2><div class="cst-set-placeholder">Loading...</div></div>`;
	}

	_setContent(pHTML)
	{
		if (typeof document === 'undefined') { return; }
		let tmpEl = document.getElementById('Settings-Content');
		if (tmpEl) { tmpEl.innerHTML = pHTML; }
	}

	_entCheckboxes(pCatalog, pSelected, pName)
	{
		let tmpSelected = Array.isArray(pSelected) ? pSelected : [];
		return (pCatalog || []).map((pEnt) => `
			<label class="cst-role-ent"><input type="checkbox" data-${pName}="${_escapeHTML(pEnt.Key)}" ${tmpSelected.indexOf(pEnt.Key) >= 0 ? 'checked' : ''}/>
				<span class="cst-role-ent-label">${_escapeHTML(pEnt.Label || pEnt.Key)}</span><span class="cst-role-ent-desc">${_escapeHTML(pEnt.Description || '')}</span></label>`).join('');
	}

	// ── Team (member management) ──────────────────────────────────────────────
	_section_team()
	{
		if (!this._teamLoaded) { return this._loadingSection('Team'); }
		let tmpMembers = (this.pict.AppData.Team && this.pict.AppData.Team.Members) || [];
		let tmpRoles = (this.pict.AppData.Team && this.pict.AppData.Team.Roles) || [];
		let tmpSelfID = (this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.IDUser) || 0;
		let tmpRows = tmpMembers.map((pMember) => this._memberRow(pMember, tmpRoles, tmpSelfID)).join('');
		return `
<div class="cst-set-section cst-set-section-wide">
	<h2 class="cst-set-h">Team</h2>
	<p class="cst-set-sub">Members of this plan sheet. Invite people, set a title, color, and the roles each person holds. Removing a member keeps their name on past work.</p>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].inviteMember()">Invite someone</button><span id="team-invite-msg" class="cst-set-msg"></span></div>
	${this._invitesHTML()}
	<h3 class="cst-set-sh">Members</h3>
	${tmpRows || '<div class="cst-set-placeholder">No members.</div>'}
</div>`;
	}

	// Pending invitations (sent, not yet accepted) with Resend / Revoke. Empty string when there are none.
	_invitesHTML()
	{
		let tmpInvites = (this.pict.AppData.Team && this.pict.AppData.Team.Invitations) || [];
		if (!tmpInvites.length) { return ''; }
		let tmpSelf = "_Pict.views['Plansheet-UserSettings']";
		let tmpRows = tmpInvites.map((pInvite) =>
		{
			let tmpID = pInvite.IDInvitation || 0;
			return `
<div class="cst-team-row" data-invite="${tmpID}">
	<div class="cst-team-head"><div class="cst-team-id"><div class="cst-team-name">${_escapeHTML(pInvite.Email || '')}</div><div class="cst-team-email">invited as ${_escapeHTML(pInvite.RoleSystemKey || 'member')} &middot; not accepted yet</div></div></div>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="${tmpSelf}.resendInvite(${tmpID})">Resend</button><button class="cst-set-btn cst-set-btn-secondary" onclick="${tmpSelf}.revokeInvite(${tmpID})">Revoke</button><span id="invite-msg-${tmpID}" class="cst-set-msg"></span></div>
</div>`;
		}).join('');
		return `<div class="cst-team-invites"><h3 class="cst-set-sh">Pending invitations</h3>${tmpRows}</div>`;
	}

	_memberRow(pMember, pRoles, pSelfID)
	{
		let tmpAvatar = libAvatarHelper.avatarHTML(pMember, { Size: 38 });
		// A removed (deactivated) member: compact row, no editing, with a Restore action.
		if (pMember.Active === false)
		{
			return `
<div class="cst-team-row cst-team-row-removed" data-user="${pMember.IDUser}">
	<div class="cst-team-head">${tmpAvatar}<div class="cst-team-id"><div class="cst-team-name">${_escapeHTML(pMember.DisplayName || pMember.Email)} <span class="cst-team-removed-badge">Removed</span></div><div class="cst-team-email">${_escapeHTML(pMember.Email)}</div></div></div>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].restoreMember(${pMember.IDUser})">Restore</button><span id="team-msg-${pMember.IDUser}" class="cst-set-msg"></span></div>
</div>`;
		}
		let tmpChecks = (pRoles || []).map((pRole) => `<label class="cst-team-role"><input type="checkbox" data-role="${pRole.IDRole}" ${pMember.Roles.some((pR) => pR.IDRole === pRole.IDRole) ? 'checked' : ''}/> ${_escapeHTML(pRole.Name)}${pRole.IsSystem ? '' : ' <span class="cst-team-role-custom">custom</span>'}</label>`).join('');
		// Remove is offered for human members other than yourself (agents use Retire). The server also guards
		// self-removal and the last owner.
		let tmpRemove = (!pMember.IsAgent && pMember.IDUser !== pSelfID)
			? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].removeMember(${pMember.IDUser})">Remove</button>`
			: '';
		return `
<div class="cst-team-row" data-user="${pMember.IDUser}">
	<div class="cst-team-head">${tmpAvatar}<div class="cst-team-id"><div class="cst-team-name">${_escapeHTML(pMember.DisplayName || pMember.Email)}${pMember.IsAgent ? ' <span class="cst-team-agent-badge">Agent</span>' : ''}</div><div class="cst-team-email">${_escapeHTML(pMember.Email)}</div></div></div>
	<div class="cst-team-fields">
		<label class="cst-set-field"><span>Title</span><input class="cst-set-input" id="team-title-${pMember.IDUser}" value="${_escapeHTML(pMember.Title || '')}" maxlength="128"/></label>
		<label class="cst-set-field"><span>Color</span><input type="color" class="cst-set-color" id="team-color-${pMember.IDUser}" value="${_escapeHTML(pMember.Color || '#3b6fd4')}"/></label>
	</div>
	<div class="cst-team-roles">${tmpChecks}</div>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveMember(${pMember.IDUser})">Save</button><button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].resetMemberTwoFactor(${pMember.IDUser})">Reset 2FA</button>${pMember.IsAgent ? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].retireAgent(${pMember.IDUser})">Retire agent</button>` : ''}${tmpRemove}<span id="team-msg-${pMember.IDUser}" class="cst-set-msg"></span></div>
</div>`;
	}

	resetMemberTwoFactor(pIDUser)
	{
		if (typeof window !== 'undefined' && typeof window.confirm === 'function'
			&& !window.confirm('Reset two-step verification for this member? They will set up a new code at their next sign-in.')) { return; }
		this._msg('team-msg-' + pIDUser, 'Resetting...', '');
		this.pict.providers.TeamAPI.resetMemberTwoFactor(pIDUser)
			.then(() => this._msg('team-msg-' + pIDUser, 'Two-step verification reset.', 'ok'))
			.catch((pErr) => this._msg('team-msg-' + pIDUser, pErr.message || 'Could not reset.', 'err'));
	}

	// Retire an agent persona (WI #465). Kept off the node workspace because a retirable agent has no node to open.
	// The server refuses (409) while the agent still has an active node; that message surfaces as the row error.
	retireAgent(pIDUser)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fRun = () =>
		{
			this._msg('team-msg-' + pIDUser, 'Retiring...', '');
			this.pict.providers.TeamAPI.retireAgent(pIDUser)
				.then(() => { this._msg('team-msg-' + pIDUser, 'Agent retired.', 'ok'); return this.pict.providers.TeamAPI.listMembers(); })
				.then(() => { if (this._activeSection === 'team') { this._setContent(this._section_team()); } })
				.catch((pErr) => this._msg('team-msg-' + pIDUser, pErr.message || 'Could not retire the agent.', 'err'));
		};
		if (tmpModal && tmpModal.confirm)
		{
			tmpModal.confirm('Retire this agent persona? It is removed once it has no active node.', { title: 'Retire agent?', confirmLabel: 'Retire', cancelLabel: 'Cancel', dangerous: true })
				.then((pOK) => { if (pOK) { fRun(); } });
		}
		else { fRun(); }
	}

	saveMember(pIDUser)
	{
		if (typeof document === 'undefined') { return; }
		let tmpRow = document.querySelector('.cst-team-row[data-user="' + pIDUser + '"]');
		if (!tmpRow) { return; }
		let tmpTitle = (document.getElementById('team-title-' + pIDUser) || {}).value || '';
		let tmpColor = (document.getElementById('team-color-' + pIDUser) || {}).value || '';
		let tmpRoleIDs = Array.prototype.slice.call(tmpRow.querySelectorAll('input[type=checkbox][data-role]:checked')).map((pBox) => parseInt(pBox.getAttribute('data-role'), 10));
		this._msg('team-msg-' + pIDUser, 'Saving...', '');
		this.pict.providers.TeamAPI.updateMember(pIDUser, { Title: tmpTitle, Color: tmpColor, Roles: tmpRoleIDs })
			.then(() => { this._msg('team-msg-' + pIDUser, 'Saved.', 'ok'); return this.pict.providers.TeamAPI.listMembers(); })
			.catch((pErr) => this._msg('team-msg-' + pIDUser, pErr.message || 'Could not save.', 'err'));
	}

	// Invite someone to this plan sheet. The email field offers a datalist of addresses the account
	// already works with (knownEmails), so a known teammate is one pick. On success the pending list refreshes.
	inviteMember()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpKnown = (this.pict.AppData.Team && this.pict.AppData.Team.KnownEmails) || [];
		let tmpOptions = tmpKnown.map((pEmail) => `<option value="${_escapeHTML(pEmail)}"></option>`).join('');
		let tmpEmail = '';
		let tmpRole = 'member';
		tmpModal.show(
			{
				title: 'Invite to this plan sheet',
				content: `<div class="cst-tenant-form">
	<label for="team-invite-email">Email</label>
	<input id="team-invite-email" type="email" list="team-invite-emails" autocomplete="off" placeholder="teammate@example.com" />
	<datalist id="team-invite-emails">${tmpOptions}</datalist>
	<label for="team-invite-role">Role</label>
	<select id="team-invite-role"><option value="member">Member</option><option value="admin">Admin</option><option value="viewer">Viewer</option><option value="owner">Owner</option></select>
	<p class="cst-tenant-form-hint">They join when they accept, with their own persona in this plan sheet.</p>
</div>`,
				buttons: [ { Hash: 'invite', Label: 'Send invitation', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' } ],
				closeable: true,
				onOpen: () =>
				{
					let tmpInput = document.getElementById('team-invite-email');
					if (tmpInput) { tmpInput.focus(); tmpInput.addEventListener('input', () => { tmpEmail = String(tmpInput.value || '').trim(); }); }
					let tmpSelect = document.getElementById('team-invite-role');
					if (tmpSelect) { tmpSelect.addEventListener('change', () => { tmpRole = tmpSelect.value; }); }
				}
			}).then((pHash) =>
			{
				if (pHash !== 'invite' || !tmpEmail) { return; }
				this._msg('team-invite-msg', 'Sending...', '');
				this.pict.providers.TeamAPI.invite(tmpEmail, tmpRole)
					.then(() => { if (tmpModal.toast) { tmpModal.toast('Invitation sent to ' + tmpEmail + '.', { type: 'success' }); } return this._reloadTeamSection(); })
					.catch((pErr) => this._msg('team-invite-msg', pErr.message || 'Could not send the invitation.', 'err'));
			});
	}

	resendInvite(pIDInvitation)
	{
		this._msg('invite-msg-' + pIDInvitation, 'Resending...', '');
		this.pict.providers.TeamAPI.resendInvitation(pIDInvitation)
			.then(() => this._msg('invite-msg-' + pIDInvitation, 'Email resent.', 'ok'))
			.catch((pErr) => this._msg('invite-msg-' + pIDInvitation, pErr.message || 'Could not resend.', 'err'));
	}

	revokeInvite(pIDInvitation)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fRun = () =>
		{
			this._msg('invite-msg-' + pIDInvitation, 'Revoking...', '');
			this.pict.providers.TeamAPI.revokeInvitation(pIDInvitation)
				.then(() => this._reloadTeamSection())
				.catch((pErr) => this._msg('invite-msg-' + pIDInvitation, pErr.message || 'Could not revoke.', 'err'));
		};
		if (tmpModal && tmpModal.confirm)
		{
			tmpModal.confirm('Cancel this invitation? The link in the email they already received will stop working.', { title: 'Revoke invitation?', confirmLabel: 'Revoke', cancelLabel: 'Keep', dangerous: true })
				.then((pOK) => { if (pOK) { fRun(); } });
		}
		else { fRun(); }
	}

	// Remove = deactivate the persona. Keeps attribution on past work; invite reactivates. The server
	// guards the last administrator and refuses self-removal (we also hide the control for the caller).
	removeMember(pIDUser)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fRun = () =>
		{
			this._msg('team-msg-' + pIDUser, 'Removing...', '');
			this.pict.providers.TeamAPI.removeMember(pIDUser)
				.then(() => this._reloadTeamSection())
				.catch((pErr) => this._msg('team-msg-' + pIDUser, pErr.message || 'Could not remove.', 'err'));
		};
		if (tmpModal && tmpModal.confirm)
		{
			tmpModal.confirm('Remove this person from the plan sheet? Their name stays on the work they did here, and you can invite them back later.', { title: 'Remove member?', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOK) => { if (pOK) { fRun(); } });
		}
		else { fRun(); }
	}

	// Restore a removed member (reactivate the persona). No confirm -- it is additive and reversible.
	restoreMember(pIDUser)
	{
		this._msg('team-msg-' + pIDUser, 'Restoring...', '');
		this.pict.providers.TeamAPI.updateMember(pIDUser, { Active: 1 })
			.then(() => this._reloadTeamSection())
			.catch((pErr) => this._msg('team-msg-' + pIDUser, pErr.message || 'Could not restore.', 'err'));
	}

	_loadTeam()
	{
		let tmpTeam = this.pict.providers.TeamAPI;
		if (!tmpTeam) { return; }
		// Members + roles are required to render; pending invitations and the known-email suggestions are
		// additive, so a failure of either leaves the page usable (listInvitations/knownEmails resolve to []).
		Promise.all([tmpTeam.listMembers(), tmpTeam.listRoles(), tmpTeam.listInvitations(), tmpTeam.knownEmails()]).then(() =>
		{
			this._teamLoaded = true;
			if (this._activeSection === 'team') { this._setContent(this._section_team()); }
		});
	}

	// Reload members + invitations and repaint the Team section (used after invite / resend / revoke /
	// remove). Returns the promise so callers can chain a status message.
	_reloadTeamSection()
	{
		let tmpTeam = this.pict.providers.TeamAPI;
		return Promise.all([tmpTeam.listMembers(), tmpTeam.listInvitations()]).then(() =>
		{
			if (this._activeSection === 'team') { this._setContent(this._section_team()); }
		});
	}

	// ── Roles ─────────────────────────────────────────────────────────────────
	_section_roles()
	{
		if (!this._rolesLoaded) { return this._loadingSection('Roles'); }
		let tmpRoles = (this.pict.AppData.Team && this.pict.AppData.Team.Roles) || [];
		let tmpCatalog = (this.pict.AppData.Team && this.pict.AppData.Team.Catalog) || [];
		let tmpCustom = tmpRoles.filter((pRole) => !pRole.IsSystem);
		let tmpSystem = tmpRoles.filter((pRole) => pRole.IsSystem);
		return `
<div class="cst-set-section cst-set-section-wide">
	<h2 class="cst-set-h">Roles</h2>
	<p class="cst-set-sub">System roles are fixed. Create custom roles to grant a specific set of entitlements.</p>
	<h3 class="cst-role-group">Custom roles</h3>
	${tmpCustom.map((pRole) => this._roleCard(pRole, tmpCatalog)).join('') || '<div class="cst-set-placeholder">No custom roles yet.</div>'}
	<h3 class="cst-role-group">Create a role</h3>
	<div class="cst-role-card">
		<label class="cst-set-field"><span>Name</span><input class="cst-set-input" id="role-new-name" placeholder="e.g. Reviewer" maxlength="128"/></label>
		<div class="cst-role-ents">${this._entCheckboxes(tmpCatalog, [], 'newent')}</div>
		<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].createRole()">Create role</button><span id="role-new-msg" class="cst-set-msg"></span></div>
	</div>
	<h3 class="cst-role-group">System roles</h3>
	${tmpSystem.map((pRole) => this._systemRoleCard(pRole, tmpCatalog)).join('')}
</div>`;
	}

	_roleCard(pRole, pCatalog)
	{
		return `
<div class="cst-role-card" data-role="${pRole.IDRole}">
	<label class="cst-set-field"><span>Name</span><input class="cst-set-input" id="role-name-${pRole.IDRole}" value="${_escapeHTML(pRole.Name)}" maxlength="128"/></label>
	<div class="cst-role-ents">${this._entCheckboxes(pCatalog, pRole.Entitlements, 'ent')}</div>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveRole(${pRole.IDRole})">Save</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].deleteRole(${pRole.IDRole})">Delete</button>
		<span id="role-msg-${pRole.IDRole}" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	_systemRoleCard(pRole, pCatalog)
	{
		let tmpBadges = (pRole.Entitlements || []).map((pKey) =>
		{
			let tmpEnt = (pCatalog || []).find((pE) => pE.Key === pKey);
			return `<span class="cst-role-badge">${_escapeHTML(tmpEnt ? tmpEnt.Label : pKey)}</span>`;
		}).join('');
		return `<div class="cst-role-card cst-role-system"><div class="cst-role-system-name">${_escapeHTML(pRole.Name)} <span class="cst-role-system-tag">system</span></div><div class="cst-role-badges">${tmpBadges}</div></div>`;
	}

	_collectEnts(pRootEl, pAttr)
	{
		return Array.prototype.slice.call(pRootEl.querySelectorAll('input[type=checkbox][data-' + pAttr + ']:checked')).map((pBox) => pBox.getAttribute('data-' + pAttr));
	}

	createRole()
	{
		if (typeof document === 'undefined') { return; }
		let tmpName = (document.getElementById('role-new-name') || {}).value || '';
		if (!tmpName.trim()) { this._msg('role-new-msg', 'A name is required.', 'err'); return; }
		let tmpCard = document.getElementById('role-new-name').closest('.cst-role-card');
		let tmpEnts = this._collectEnts(tmpCard, 'newent');
		this._msg('role-new-msg', 'Creating...', '');
		this.pict.providers.TeamAPI.createRole({ Name: tmpName, Entitlements: tmpEnts })
			.then(() => this._reloadRoles())
			.catch((pErr) => this._msg('role-new-msg', pErr.message || 'Could not create.', 'err'));
	}

	saveRole(pIDRole)
	{
		if (typeof document === 'undefined') { return; }
		let tmpCard = document.querySelector('.cst-role-card[data-role="' + pIDRole + '"]');
		if (!tmpCard) { return; }
		let tmpName = (document.getElementById('role-name-' + pIDRole) || {}).value || '';
		let tmpEnts = this._collectEnts(tmpCard, 'ent');
		this._msg('role-msg-' + pIDRole, 'Saving...', '');
		this.pict.providers.TeamAPI.updateRole(pIDRole, { Name: tmpName, Entitlements: tmpEnts })
			.then(() => { this._msg('role-msg-' + pIDRole, 'Saved.', 'ok'); return this.pict.providers.TeamAPI.listRoles(); })
			.catch((pErr) => this._msg('role-msg-' + pIDRole, pErr.message || 'Could not save.', 'err'));
	}

	deleteRole(pIDRole)
	{
		let fDelete = () => this.pict.providers.TeamAPI.deleteRole(pIDRole).then(() => this._reloadRoles())
			.catch((pErr) => this._msg('role-msg-' + pIDRole, pErr.message || 'Could not delete.', 'err'));
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Delete this role? Anyone assigned to it loses it.', { title: 'Delete role?', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fDelete(); } });
		}
		else { fDelete(); }
	}

	_reloadRoles()
	{
		this.pict.providers.TeamAPI.listRoles().then(() => { if (this._activeSection === 'roles') { this._setContent(this._section_roles()); } });
	}

	_loadRoles()
	{
		let tmpTeam = this.pict.providers.TeamAPI;
		if (!tmpTeam) { return; }
		Promise.all([tmpTeam.listRoles(), tmpTeam.getEntitlements()]).then(() =>
		{
			this._rolesLoaded = true;
			if (this._activeSection === 'roles') { this._setContent(this._section_roles()); }
		});
	}

	// ── Org Settings ──────────────────────────────────────────────────────────
	_section_org()
	{
		if (!this._orgLoaded) { return this._loadingSection('Organization'); }
		let tmpOrg = (this.pict.AppData.Org && this.pict.AppData.Org.Settings) || {};
		let tmpNavBlock = this._navSettingsBlock(this._navConfig);
		// The brand-logo id is held locally so an upload shows before the Save that persists it. Seed it
		// from the loaded org the first time, then let uploads/removes drive it.
		if (typeof this._brandLogoID !== 'number') { this._brandLogoID = tmpOrg.IDBrandLogoMedia || 0; }
		let tmpBrandPreview = this._brandLogoID
			? ('<img src="/1.0/Media/' + this._brandLogoID + '/Blob" alt="logo" style="max-height:44px;max-width:180px;object-fit:contain;background:#fff;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;padding:4px;">')
			: '<span class="cst-set-msg">No logo set</span>';
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Organization</h2>
	<p class="cst-set-sub">Your organization profile.</p>
	<label class="cst-set-field"><span>Name</span><input class="cst-set-input" id="org-Name" value="${_escapeHTML(tmpOrg.Name || '')}" maxlength="200"/></label>
	<label class="cst-set-field"><span>Code</span><input class="cst-set-input" id="org-Code" value="${_escapeHTML(tmpOrg.Code || '')}" maxlength="64" placeholder="e.g. sv-app-colors" oninput="_Pict.views['Plansheet-UserSettings'].checkOrgCode()"/></label>
	<p class="cst-set-sub">A short, unique handle for this plan sheet (letters, numbers, and hyphens). It lets you name this plan sheet from the MCP, e.g. "load the ${_escapeHTML(tmpOrg.Code || 'your-code')} plan sheet and run the prompt for Work Item 14". <span id="org-Code-status" class="cst-set-msg"></span></p>
	<label class="cst-set-field"><span>Description</span><textarea class="cst-set-input" id="org-Description" rows="3">${_escapeHTML(tmpOrg.Description || '')}</textarea></label>
	<label class="cst-set-field"><span>Contact name</span><input class="cst-set-input" id="org-ContactName" value="${_escapeHTML(tmpOrg.ContactName || '')}" maxlength="200"/></label>
	<label class="cst-set-field"><span>Contact email</span><input class="cst-set-input" id="org-ContactEmail" type="email" value="${_escapeHTML(tmpOrg.ContactEmail || '')}" maxlength="200"/></label>
	${this._coverPickerBlock(tmpOrg)}
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Blueprint branding</h2>
	<p class="cst-set-sub">A logo and a footer line stamped on every blueprint in this plan sheet, so they read as your own documents. Shared links and PDF downloads carry them too.</p>
	<div class="cst-set-field"><span>Logo</span>
		<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
			<div id="org-BrandLogo-preview">${tmpBrandPreview}</div>
			<label class="cst-set-btn cst-set-btn-secondary" style="cursor:pointer;">Upload a PNG<input type="file" accept="image/png,image/svg+xml,image/jpeg" style="display:none" onchange="_Pict.views['Plansheet-UserSettings'].uploadBrandLogo(this)"/></label>
			<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].removeBrandLogo()">Remove</button>
			<span id="org-BrandLogo-msg" class="cst-set-msg"></span>
		</div>
	</div>
	<label class="cst-set-field"><span>Footer line</span><input class="cst-set-input" id="org-BrandFooter" value="${_escapeHTML(tmpOrg.BrandFooter || '')}" maxlength="200" placeholder="e.g. your company or product name"/></label>
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Security</h2>
	<label class="cst-set-check"><input type="checkbox" id="org-Require2FA"${tmpOrg.RequireTwoFactorForLocal ? ' checked' : ''}/> <span>Require two-step verification for password sign-in</span></label>
	<p class="cst-set-sub">Members who sign in with a password are asked to turn on a one-time code before they can use the app. People who use a connected login (Google, GitHub) carry their own protection and are unaffected.</p>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveOrg()">Save</button><span id="org-msg" class="cst-set-msg"></span></div>
	${tmpNavBlock}
</div>`;
	}

	// The owner's sidebar-visibility picker for the Org Settings page: a checkbox per hideable nav entry
	// (checked = shown), grouped by its sidebar group. Returns '' when the catalog has not loaded, so a
	// non-owner (who never gets a catalog) sees nothing. saveNav() reads these checkboxes back.
	_navSettingsBlock(pCfg)
	{
		let tmpCfg = pCfg || {};
		let tmpCatalog = Array.isArray(tmpCfg.Catalog) ? tmpCfg.Catalog : [];
		if (!tmpCatalog.length) { return ''; }
		let tmpHidden = {};
		(Array.isArray(tmpCfg.HiddenNavItems) ? tmpCfg.HiddenNavItems : []).forEach((pHash) => { tmpHidden[pHash] = true; });
		let tmpOrder = [];
		let tmpByGroup = {};
		tmpCatalog.forEach((pItem) =>
		{
			if (!tmpByGroup[pItem.GroupHash]) { tmpByGroup[pItem.GroupHash] = { Name: pItem.GroupName || '', Items: [] }; tmpOrder.push(pItem.GroupHash); }
			tmpByGroup[pItem.GroupHash].Items.push(pItem);
		});
		let tmpGroups = tmpOrder.map((pGH) =>
		{
			let tmpGroup = tmpByGroup[pGH];
			let tmpItems = tmpGroup.Items.map((pItem) =>
				`<label class="cst-set-check"><input type="checkbox" class="cst-set-navitem" data-hash="${_escapeHTML(pItem.Hash)}"${tmpHidden[pItem.Hash] ? '' : ' checked'}/> <span>${_escapeHTML(pItem.Name)}</span></label>`).join('');
			return `<div><div class="cst-set-sub" style="font-weight:600;color:var(--theme-color-text-primary,#2c3140);margin-bottom:4px;">${_escapeHTML(tmpGroup.Name)}</div>${tmpItems}</div>`;
		}).join('');
		return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Navigation</h2>
	<p class="cst-set-sub">Turn off the sidebar entries this plan sheet does not use. A hidden entry leaves the sidebar and the Cmd+K launcher for everyone here; turn it back on any time. Your personal pages and the settings gear are always shown.</p>
	<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:16px 24px;margin-top:8px;">${tmpGroups}</div>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveNav()">Save navigation</button><span id="nav-msg" class="cst-set-msg"></span></div>`;
	}

	// Persist the sidebar-visibility picker: any unchecked entry is hidden. Writes through OrgAPI, then
	// re-installs the site nav so the sidebar reflects it at once for this session (other members pick it
	// up on their next CheckSession).
	saveNav()
	{
		if (typeof document === 'undefined') { return; }
		let tmpHidden = [];
		let tmpInputs = document.querySelectorAll('.cst-set-navitem');
		for (let i = 0; i < tmpInputs.length; i++)
		{
			if (!tmpInputs[i].checked) { tmpHidden.push(tmpInputs[i].getAttribute('data-hash')); }
		}
		this._msg('nav-msg', 'Saving...', '');
		this.pict.providers.OrgAPI.saveNav(tmpHidden)
			.then((pBody) =>
			{
				if (this._navConfig) { this._navConfig.HiddenNavItems = (pBody && pBody.HiddenNavItems) || tmpHidden; }
				this._msg('nav-msg', 'Saved.', 'ok');
				let tmpApp = this.pict.PictApplication;
				if (tmpApp && typeof tmpApp._installSiteNav === 'function') { tmpApp._installSiteNav(); }
			})
			.catch((pErr) => this._msg('nav-msg', pErr.message || 'Could not save.', 'err'));
	}

	saveOrg()
	{
		if (typeof document === 'undefined') { return; }
		let tmpGet = (pId) => (document.getElementById(pId) || {}).value || '';
		let tmpChk = (pId) => (document.getElementById(pId) || {}).checked || false;
		this._msg('org-msg', 'Saving...', '');
		let tmpBrandLogo = (typeof this._brandLogoID === 'number') ? this._brandLogoID : ((((this.pict.AppData.Org || {}).Settings) || {}).IDBrandLogoMedia || 0);
		this.pict.providers.OrgAPI.updateSettings({ Name: tmpGet('org-Name'), Code: tmpGet('org-Code'), Description: tmpGet('org-Description'), ContactName: tmpGet('org-ContactName'), ContactEmail: tmpGet('org-ContactEmail'), RequireTwoFactorForLocal: tmpChk('org-Require2FA'), BrandFooter: tmpGet('org-BrandFooter'), IDBrandLogoMedia: tmpBrandLogo })
			.then((pOrg) => { if (pOrg) { this._brandLogoID = pOrg.IDBrandLogoMedia || 0; } this._msg('org-msg', 'Saved.', 'ok'); })
			.catch((pErr) => this._msg('org-msg', pErr.message || 'Could not save.', 'err'));
	}

	// Upload a brand logo (team media owned by this plan sheet; the server stamps the tenant, so IDOwner 0 and
	// no IDCustomer leaves the client). Holds the id locally until Save persists it onto the plan sheet.
	uploadBrandLogo(pInput)
	{
		let tmpFile = pInput && pInput.files && pInput.files[0];
		if (!tmpFile) { return; }
		this._msg('org-BrandLogo-msg', 'Uploading...', '');
		this.pict.providers.MediaAPI.upload('Customer', 0, tmpFile, {})
			.then((pRef) =>
			{
				this._brandLogoID = (pRef && pRef.IDMedia) || 0;
				let tmpPrev = (typeof document !== 'undefined') ? document.getElementById('org-BrandLogo-preview') : null;
				if (tmpPrev && this._brandLogoID)
				{
					tmpPrev.innerHTML = '<img src="/1.0/Media/' + this._brandLogoID + '/Blob?t=' + Date.now() + '" alt="logo" style="max-height:44px;max-width:180px;object-fit:contain;background:#fff;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;padding:4px;">';
				}
				this._msg('org-BrandLogo-msg', 'Uploaded. Click Save to apply.', 'ok');
			})
			.catch((pErr) => this._msg('org-BrandLogo-msg', (pErr && pErr.message) || 'Upload failed.', 'err'));
	}

	removeBrandLogo()
	{
		this._brandLogoID = 0;
		let tmpPrev = (typeof document !== 'undefined') ? document.getElementById('org-BrandLogo-preview') : null;
		if (tmpPrev) { tmpPrev.innerHTML = '<span class="cst-set-msg">No logo set</span>'; }
		this._msg('org-BrandLogo-msg', 'Removed. Click Save to apply.', '');
	}

	// The cover-blueprint picker (WI #339): a select of this plan sheet's standalone blueprints, current
	// one selected, plus None. Composed on the Blueprints page; saved through PUT /1.0/Plansheet/Cover
	// (saveCover), separate from the org profile Save above.
	_coverPickerBlock(pOrg)
	{
		let tmpCurrent = (pOrg && pOrg.IDPrimaryBlueprint) || 0;
		let tmpList = Array.isArray(this._coverBlueprints) ? this._coverBlueprints : [];
		let tmpOptions = '<option value="0"' + (tmpCurrent ? '' : ' selected') + '>None</option>';
		tmpList.forEach((pBP) =>
		{
			let tmpID = pBP.IDBlueprint || 0;
			let tmpNum = pBP.BlueprintNumber ? (' (BP-' + String(pBP.BlueprintNumber).padStart(3, '0') + ')') : '';
			tmpOptions += '<option value="' + tmpID + '"' + (tmpID === tmpCurrent ? ' selected' : '') + '>' + _escapeHTML((pBP.Name || ('Blueprint #' + tmpID)) + tmpNum) + '</option>';
		});
		let tmpHint = tmpList.length
			? 'Choose a blueprint to front this plan sheet when you browse your plan sheets. Compose blueprints on the Blueprints page.'
			: 'No standalone blueprints yet. Compose one on the Blueprints page, then pick it here.';
		return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Cover blueprint</h2>
	<p class="cst-set-sub">${tmpHint}</p>
	<label class="cst-set-field"><span>Cover</span><select class="cst-set-input" id="org-Cover">${tmpOptions}</select></label>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveCover()">Save cover</button><span id="cover-msg" class="cst-set-msg"></span></div>`;
	}

	saveCover()
	{
		if (typeof document === 'undefined') { return; }
		let tmpSel = document.getElementById('org-Cover');
		let tmpID = tmpSel ? (parseInt(tmpSel.value, 10) || 0) : 0;
		this._msg('cover-msg', 'Saving...', '');
		let tmpClient = require('../providers/FrameworkClient.js');
		tmpClient.putJSON(this.pict, '/1.0/Plansheet/Cover', { IDBlueprint: tmpID })
			.then((pBody) =>
			{
				if (pBody && pBody.Success)
				{
					if (this.pict.AppData.Org && this.pict.AppData.Org.Settings) { this.pict.AppData.Org.Settings.IDPrimaryBlueprint = tmpID; }
					this._msg('cover-msg', 'Saved.', 'ok');
					return;
				}
				this._msg('cover-msg', (pBody && pBody.Error) || 'Could not save.', 'err');
			})
			.catch((pErr) => this._msg('cover-msg', (pErr && pErr.message) || 'Could not save.', 'err'));
	}

	// Live availability check for the plan sheet Code field (debounced). The server normalizes and
	// excludes this plan sheet's own code, so keeping the current code always reads as available.
	checkOrgCode()
	{
		if (typeof document === 'undefined') { return; }
		let tmpRaw = ((document.getElementById('org-Code') || {}).value || '').trim();
		let tmpStatus = document.getElementById('org-Code-status');
		if (this._codeCheckTimer) { clearTimeout(this._codeCheckTimer); }
		if (!tmpRaw) { if (tmpStatus) { tmpStatus.textContent = ''; tmpStatus.className = 'cst-set-msg'; } return; }
		this._codeCheckTimer = setTimeout(() =>
		{
			this.pict.providers.OrgAPI.checkCode(tmpRaw)
				.then((pBody) =>
				{
					let tmpEl = document.getElementById('org-Code-status');
					if (!tmpEl) { return; }
					if (pBody && pBody.Available) { tmpEl.textContent = 'Available' + (pBody.Code && pBody.Code !== tmpRaw ? ' as "' + pBody.Code + '"' : '') + '.'; tmpEl.className = 'cst-set-msg ok'; }
					else { tmpEl.textContent = (pBody && pBody.Reason) || 'That code is taken.'; tmpEl.className = 'cst-set-msg err'; }
				})
				.catch(() => {});
		}, 300);
	}

	_loadOrg()
	{
		let tmpOrg = this.pict.providers.OrgAPI;
		if (!tmpOrg) { return; }
		// Load the org profile and the nav-visibility config together; getNav is owner-gated, so a non-owner
		// (who cannot reach this section anyway) just falls back to an empty catalog and renders no picker.
		// Also load the standalone blueprints for the cover picker (WI #339); non-fatal if it fails.
		let tmpClient = require('../providers/FrameworkClient.js');
		Promise.all(
			[
				tmpOrg.getSettings(),
				tmpOrg.getNav().catch(() => ({ HiddenNavItems: [], Catalog: [] })),
				tmpClient.getJSON(this.pict, '/1.0/Blueprints/0/250').catch(() => [])
			]).then((pResults) =>
		{
			this._navConfig = pResults[1] || { HiddenNavItems: [], Catalog: [] };
			this._coverBlueprints = Array.isArray(pResults[2]) ? pResults[2] : [];
			this._orgLoaded = true;
			if (this._activeSection === 'org') { this._setContent(this._section_org()); }
		});
	}

	// -- Top Bar (the plan sheet's own decoration, WI #360) --------------------
	// The author arranges a moodboard inside a LOCKED crop shaped like the top bar, then "Set as top bar"
	// bakes that crop to a standalone SVG (pict-section-moodboard bakeCropToSVG) and stores it with its
	// source board, so it stays re-editable. The point is identity: with a backdrop of its own, a plan
	// sheet's chrome stops looking like every other plan sheet's, and landing in the wrong one is obvious.
	_section_topbar()
	{
		if (!this._topBarLoaded) { return this._loadingSection('Top Bar'); }
		let tmpHas = !!(this._topBarData && this._topBarData.TopBarBackdropSVG);
		let tmpClear = tmpHas
			? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].clearTopBar()">Remove</button>`
			: '';
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Top Bar</h2>
	<p class="cst-set-sub">Give this plan sheet its own look. Arrange stickers, drawings, and images inside the frame below, then set it as the top bar. The frame is the exact shape of the bar, so what you compose inside it is what lands across the top of every page. ${tmpHas ? 'This plan sheet has a top bar set.' : 'No top bar is set yet, so the bar shows its plain background.'}</p>
	<div class="cst-topbar-board" id="Settings-TopBar-Board"></div>
	<p class="cst-set-sub">The chrome sits on top: the brand mark and your account chip at the two ends, the page name in between. Those ends stay covered by the theme, so keep anything you want seen toward the middle.</p>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].setTopBar()">Set as top bar</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].fitTopBarFrame()">Fit frame to art</button>
		${tmpClear}
		<span id="topbar-msg" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	_loadTopBar()
	{
		let tmpOrg = this.pict.providers.OrgAPI;
		if (!tmpOrg) { return; }
		// showSection paints the section HTML (wiping the board host) every time it runs, and it runs twice
		// on a settings open -- once from cache, once after getMe. Once the data is in hand the answer is
		// simply to remount into the fresh host rather than refetch and leave the canvas blank meanwhile.
		if (this._topBarLoaded) { this._mountTopBar(); return; }
		// The composer wants the source board too, not just the baked SVG, so it reopens what was set
		// rather than starting from an empty canvas.
		tmpOrg.getTopBar(true).then((pBody) =>
		{
			this._topBarData = pBody || { TopBarBackdropSVG: '' };
			this._topBarLoaded = true;
			if (this._activeSection === 'topbar')
			{
				this._setContent(this._section_topbar());
				this._mountTopBar();
			}
		});
	}

	// Mount the composer board into the section, seed it from the stored source board, and lock its crop
	// frame to the bar's shape. The frame has to wait for the flow underneath to finish mounting and for a
	// seeded board to finish applying (isBoardReady), otherwise setCropFrame sizes itself against an empty
	// board and the author gets a frame in the wrong place.
	_mountTopBar()
	{
		let tmpBoardView = this.pict.views['TopBar-Board-Edit'];
		if (typeof document === 'undefined') { return; }
		let tmpHost = document.getElementById('Settings-TopBar-Board');
		if (!tmpBoardView)
		{
			if (tmpHost) { tmpHost.innerHTML = '<div class="cst-set-placeholder">The board composer is not available in this build.</div>'; }
			return;
		}
		tmpBoardView.render();
		// Always set a board, even an empty one. This view is a reused singleton, so whatever was last
		// composed is still in it: without the empty case a removed top bar would come back on screen as if
		// it were still stored, and the next "Set as top bar" would quietly restore what was just discarded.
		let tmpBoard = (this._topBarData && this._topBarData.TopBarBoardJSON) || { Nodes: [], Connections: [], ViewState: {} };
		tmpBoardView.setBoard(tmpBoard);
		this._armTopBarCropFrame(tmpBoardView, 0);
	}

	// Poll (bounded) for the board to be ready, then lock the crop. Shown as a guide only -- the frame's
	// resize handles stay off, so the author arranges art WITHIN a fixed shape instead of redefining it.
	_armTopBarCropFrame(pBoardView, pAttempt)
	{
		if (this._activeSection !== 'topbar') { return; }
		if (pAttempt > 40) { return; }
		if (typeof pBoardView.isBoardReady === 'function' && pBoardView.isBoardReady() && typeof pBoardView.setCropFrame === 'function')
		{
			let tmpAspect = this._topBarAspect();
			pBoardView.setCropFrame(tmpAspect, { Width: this._topBarCropWidth(pBoardView, tmpAspect) });
			return;
		}
		setTimeout(() => this._armTopBarCropFrame(pBoardView, pAttempt + 1), 50);
	}

	// How wide to make the locked crop. setCropFrame's own default sizes the frame to the content's WIDTH,
	// which is right for an ordinary crop and wrong for one this elongated: at a 23:1 bar aspect, a board
	// holding one default 120px card would get a frame 120 wide and barely 5 tall, and the card would spill
	// out of the very frame meant to hold it. So the band is sized by HEIGHT instead -- tall enough for
	// card-sized art, with a floor for an empty board -- and the width follows from the aspect. A board whose
	// art is already laid out wider than that keeps its own width, so nothing composed gets cropped away.
	_topBarCropWidth(pBoardView, pAspect)
	{
		let tmpBoard = (typeof pBoardView.getBoard === 'function') ? pBoardView.getBoard() : null;
		let tmpNodes = (tmpBoard && Array.isArray(tmpBoard.Nodes)) ? tmpBoard.Nodes : [];
		let tmpBox = null;
		tmpNodes.forEach((pNode) =>
		{
			let tmpX = Number(pNode.X) || 0, tmpY = Number(pNode.Y) || 0;
			let tmpW = Number(pNode.Width) || 0, tmpH = Number(pNode.Height) || 0;
			if (!tmpBox) { tmpBox = { MinX: tmpX, MinY: tmpY, MaxX: tmpX + tmpW, MaxY: tmpY + tmpH }; return; }
			tmpBox.MinX = Math.min(tmpBox.MinX, tmpX); tmpBox.MinY = Math.min(tmpBox.MinY, tmpY);
			tmpBox.MaxX = Math.max(tmpBox.MaxX, tmpX + tmpW); tmpBox.MaxY = Math.max(tmpBox.MaxY, tmpY + tmpH);
		});
		let tmpContentWidth = tmpBox ? (tmpBox.MaxX - tmpBox.MinX) : 0;
		let tmpContentHeight = tmpBox ? (tmpBox.MaxY - tmpBox.MinY) : 0;
		// A little air above and below the art, and a floor so an empty board opens on a usable band.
		let tmpBand = Math.max(tmpContentHeight * 1.3, _TOPBAR_MIN_BAND);
		return Math.max(tmpBand * pAspect, tmpContentWidth * 1.1);
	}

	// Re-derive the locked crop from what is on the board now. The frame deliberately does NOT follow every
	// card the author drops -- art that shifted under them while composing would be worse than a frame that
	// no longer fits -- so refitting is an explicit action, for after the arranging is done.
	fitTopBarFrame()
	{
		let tmpBoardView = this.pict.views['TopBar-Board-Edit'];
		if (!tmpBoardView || typeof tmpBoardView.setCropFrame !== 'function') { return; }
		let tmpAspect = this._topBarAspect();
		tmpBoardView.setCropFrame(tmpAspect, { Width: this._topBarCropWidth(tmpBoardView, tmpAspect) });
		this._msg('topbar-msg', 'Frame refit around the art.', 'ok');
	}

	// The bar's Width / Height. Measured off the live bar so the crop matches the window the author is
	// actually looking at, falling back to a representative wide desktop bar (1344 / 56) when it cannot be
	// measured. An exact match is not required either way: the shell paints the bake with a slice fit, so a
	// wider or narrower window crops the art rather than distorting it.
	_topBarAspect()
	{
		let tmpBar = (typeof document !== 'undefined') ? document.getElementById('Theme-TopBar') : null;
		if (tmpBar && tmpBar.clientWidth > 0 && tmpBar.clientHeight > 0) { return tmpBar.clientWidth / tmpBar.clientHeight; }
		return 24;
	}

	// Bake the locked crop and store it with the board it came from, then paint it on the live bar so the
	// author sees the result on the real chrome instead of a mockup.
	setTopBar()
	{
		let tmpBoardView = this.pict.views['TopBar-Board-Edit'];
		if (!tmpBoardView) { return; }
		if (typeof tmpBoardView.isBoardReady === 'function' && !tmpBoardView.isBoardReady())
		{
			this._msg('topbar-msg', 'The board is still loading.', 'err');
			return;
		}
		let tmpSVG = (typeof tmpBoardView.bakeCropToSVG === 'function') ? tmpBoardView.bakeCropToSVG() : '';
		if (!tmpSVG)
		{
			this._msg('topbar-msg', 'There is nothing in the frame to set yet.', 'err');
			return;
		}
		this._msg('topbar-msg', 'Saving...', '');
		let tmpBoard = (typeof tmpBoardView.getBoard === 'function') ? tmpBoardView.getBoard() : null;
		this.pict.providers.OrgAPI.saveTopBar(tmpSVG, tmpBoard)
			.then(() =>
			{
				this._topBarData = { TopBarBackdropSVG: tmpSVG, TopBarBoardJSON: tmpBoard };
				this._applyTopBarToShell(tmpSVG);
				this._msg('topbar-msg', 'Set. It is on the bar now.', 'ok');
			})
			.catch((pErr) => this._msg('topbar-msg', (pErr && pErr.message) || 'Could not save.', 'err'));
	}

	clearTopBar()
	{
		let fClear = () =>
		{
			this._msg('topbar-msg', 'Removing...', '');
			// Clearing drops the stored source board with the SVG, so the section reopens on a blank canvas
			// rather than offering a board whose bake is no longer anywhere.
			this.pict.providers.OrgAPI.saveTopBar('', null)
				.then(() =>
				{
					this._topBarData = { TopBarBackdropSVG: '' };
					this._applyTopBarToShell('');
					if (this._activeSection === 'topbar') { this._setContent(this._section_topbar()); this._mountTopBar(); }
				})
				.catch((pErr) => this._msg('topbar-msg', (pErr && pErr.message) || 'Could not remove.', 'err'));
		};
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Remove this plan sheet\'s top bar? The bar goes back to its plain background, and the board you composed is discarded.',
				{ title: 'Remove top bar?', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fClear(); } });
		}
		else { fClear(); }
	}

	_applyTopBarToShell(pSVG)
	{
		let tmpShell = this.pict.views['Plansheet-Shell'];
		if (tmpShell && typeof tmpShell.applyTopBarBackdrop === 'function') { tmpShell.applyTopBarBackdrop(pSVG); }
	}

	// ── Documentation Sync ────────────────────────────────────────────────────
	// Connect a GitHub repo and sync a docs subfolder into this plan sheet's library (or push it
	// back out). The GitHub App installations and the stored bindings load together in _loadDocSync;
	// until then the section shows the loading placeholder, mirroring the Org section.
	_section_docsync()
	{
		if (!this._docsyncLoaded) { return this._loadingSection('Documentation Sync'); }
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Documentation Sync</h2>
	<p class="cst-set-sub">Connect a GitHub repository and sync a docs subfolder into this plan sheet's library, or push the library back out. Authorize it with an account GitHub token (below) or, if your server runs the Plansheet GitHub App, an App installation.</p>
	${this._docsyncCredentialsBlock()}
	${this._docsyncBindingForm()}
	${this._docsyncBindingsBlock()}
</div>`;
	}

	// Account-owned GitHub tokens: the fine-grained PATs this account syncs with (shared across all of
	// its plan sheets). Lists each stored token (never the secret) with Test/Revoke, and an add form.
	// A write-capable token unlocks bidirectional sync; a read-only one still pulls private repos.
	_docsyncCredentialsBlock()
	{
		let tmpTokens = Array.isArray(this._docsyncTokens) ? this._docsyncTokens : [];
		let tmpRows = tmpTokens.map((pToken) =>
		{
			let tmpBadge = pToken.CanWrite
				? `<span style="display:inline-block;padding:1px 7px;border-radius:9px;font-size:11px;font-weight:600;background:var(--theme-color-success-soft,#e4f3e8);color:var(--theme-color-success,#1f7a3d);">read + write</span>`
				: `<span style="display:inline-block;padding:1px 7px;border-radius:9px;font-size:11px;font-weight:600;background:var(--theme-color-neutral-soft,#eceef2);color:var(--theme-color-text-secondary,#5b6372);">read only</span>`;
			let tmpMeta = [];
			if (pToken.Scope) { tmpMeta.push('scope ' + _escapeHTML(pToken.Scope)); }
			if (pToken.Kind) { tmpMeta.push(_escapeHTML(pToken.Kind)); }
			let tmpExpires = _formatDate(pToken.ExpiresAt); if (tmpExpires) { tmpMeta.push('expires ' + tmpExpires); }
			let tmpUsed = _formatDate(pToken.LastUsedAt); if (tmpUsed) { tmpMeta.push('last used ' + tmpUsed); }
			return `<div class="cst-set-sub" style="display:flex;align-items:center;gap:10px;margin:8px 0;flex-wrap:wrap;">
		<strong style="color:var(--theme-color-text-primary,#2c3140);">${_escapeHTML(pToken.Label || 'token')}</strong>
		<span style="font-family:monospace;opacity:0.7;">...${_escapeHTML(pToken.Last4 || '')}</span>
		${tmpBadge}
		${tmpMeta.length ? `<span style="opacity:0.75;">${tmpMeta.join(' | ')}</span>` : ''}
		<span style="flex:1 1 auto;"></span>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].testGithubToken(${parseInt(pToken.IDAccountGithubToken, 10) || 0})">Test</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].revokeGithubToken(${parseInt(pToken.IDAccountGithubToken, 10) || 0})">Revoke</button>
	</div>`;
		}).join('');

		let tmpAddForm;
		if (!this._docsyncTokenEncryption)
		{
			tmpAddForm = `<div class="cst-set-placeholder">Token encryption is not configured on this server, so a GitHub token cannot be stored here yet. An administrator needs to set <code>DocSync.TokenEncryptionKey</code> in the server config.</div>`;
		}
		else
		{
			tmpAddForm = `
	<label class="cst-set-field"><span>Label</span><input class="cst-set-input" id="ghtok-Label" maxlength="128" placeholder="e.g. fable-retold docs bot"/></label>
	<label class="cst-set-field"><span>Token</span><span style="display:flex;gap:6px;align-items:center;"><input class="cst-set-input" id="ghtok-Token" type="password" autocomplete="off" spellcheck="false" placeholder="github_pat_..." style="flex:1 1 auto;"/><button type="button" class="cst-set-btn cst-set-btn-secondary" id="ghtok-Token-toggle" onclick="_Pict.views['Plansheet-UserSettings'].toggleTokenVisibility()">Show</button></span></label>
	<p class="cst-set-sub">Masked by default. Show it to check the paste; leading and trailing spaces are trimmed automatically.</p>
	<label class="cst-set-field"><span>Scope</span><input class="cst-set-input" id="ghtok-Scope" maxlength="256" placeholder="owner or org, e.g. fable-retold"/></label>
	<label class="cst-set-field"><span>Test repo</span><input class="cst-set-input" id="ghtok-Repo" placeholder="optional, e.g. fable-retold/meadow"/></label>
	<p class="cst-set-sub">The token is validated against GitHub and encrypted at rest. It is never shown again; only the last four characters are kept for identification.</p>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].addGithubToken()">Add token</button><span id="ghtok-msg" class="cst-set-msg"></span></div>
	<div id="ghtok-result" class="cst-set-sub" style="margin-top:6px;"></div>`;
		}

		return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">GitHub credentials</h2>
	<p class="cst-set-sub">Fine-grained personal access tokens this account syncs with. A token is owned by your account and shared across all of its plan sheets.</p>
	${tmpRows || '<div class="cst-set-placeholder">No tokens yet.</div>'}
	${tmpAddForm}`;
	}

	// Credential options for the binding's "Authorize with" picker: every account token, then every
	// GitHub App installation. Value encodes the kind so saveDocSyncBinding can route it. A write
	// token is preferred as the default (it unlocks bidirectional sync).
	_docsyncCredentialOptions()
	{
		let tmpTokens = Array.isArray(this._docsyncTokens) ? this._docsyncTokens : [];
		let tmpData = this._docsyncData || {};
		let tmpInstallations = Array.isArray(tmpData.Installations) ? tmpData.Installations : [];
		let tmpDefaultIndex = tmpTokens.findIndex((pT) => pT.CanWrite);
		if (tmpDefaultIndex < 0) { tmpDefaultIndex = (tmpTokens.length ? 0 : -1); }
		let tmpOut = [];
		tmpTokens.forEach((pToken, pIndex) =>
		{
			let tmpLabel = (pToken.Label || 'token') + ' (...' + (pToken.Last4 || '') + (pToken.CanWrite ? ', write' : ', read only') + ')';
			tmpOut.push(`<option value="token:${parseInt(pToken.IDAccountGithubToken, 10) || 0}"${pIndex === tmpDefaultIndex ? ' selected' : ''}>${_escapeHTML(tmpLabel)}</option>`);
		});
		tmpInstallations.forEach((pInstall) =>
		{
			let tmpLabel = 'GitHub App: ' + (pInstall.Account || ('installation ' + pInstall.InstallationID));
			tmpOut.push(`<option value="app:${_escapeHTML(String(pInstall.InstallationID))}"${tmpDefaultIndex < 0 ? ' selected' : ''}>${_escapeHTML(tmpLabel)}</option>`);
		});
		return tmpOut.join('');
	}

	// The connect-a-repo form. Shows whenever at least one credential (an account token OR a GitHub App
	// installation) can authorize a sync; otherwise it explains what is needed (with the App install
	// link when the App is set up but not installed).
	_docsyncBindingForm()
	{
		let tmpTokens = Array.isArray(this._docsyncTokens) ? this._docsyncTokens : [];
		let tmpData = this._docsyncData || { Configured: false, InstallURL: '', Installations: [] };
		let tmpInstallations = Array.isArray(tmpData.Installations) ? tmpData.Installations : [];
		let tmpHasCredential = (tmpTokens.length > 0) || (tmpInstallations.length > 0);

		if (!tmpHasCredential)
		{
			let tmpHint = this._docsyncTokenEncryption
				? 'Add a GitHub token above to connect a repository.'
				: 'Add a GitHub token above (once token encryption is configured) to connect a repository.';
			if (tmpData.Configured && tmpData.InstallURL)
			{
				tmpHint += ' You can also install the Plansheet GitHub App.';
				return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Connect a repository</h2>
	<p class="cst-set-sub">${tmpHint}</p>
	<div class="cst-set-actions"><a class="cst-set-btn cst-set-btn-secondary" href="${_escapeHTML(tmpData.InstallURL)}" target="_blank" rel="noopener">Install the Plansheet GitHub App</a></div>`;
			}
			return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Connect a repository</h2>
	<p class="cst-set-sub">${tmpHint}</p>`;
		}

		// A datalist of repo names the GitHub App can see, as an autocomplete convenience; the field is
		// free text so a token-authorized repo the App does not enumerate can still be typed.
		let tmpSeen = {};
		let tmpRepoList = '';
		tmpInstallations.forEach((pInstall) =>
		{
			(Array.isArray(pInstall.Repos) ? pInstall.Repos : []).forEach((pRepo) =>
			{
				if (pRepo.FullName && !tmpSeen[pRepo.FullName]) { tmpSeen[pRepo.FullName] = true; tmpRepoList += `<option value="${_escapeHTML(pRepo.FullName)}"></option>`; }
			});
		});

		return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Connect a repository</h2>
	<label class="cst-set-field"><span>Authorize with</span><select class="cst-set-input" id="docsync-cred">${this._docsyncCredentialOptions()}</select></label>
	<label class="cst-set-field"><span>Repository</span><input class="cst-set-input" id="docsync-repo" list="docsync-repo-list" placeholder="owner/name"/><datalist id="docsync-repo-list">${tmpRepoList}</datalist></label>
	<label class="cst-set-field"><span>Branch</span><input class="cst-set-input" id="docsync-Ref" placeholder="default branch"/></label>
	<label class="cst-set-field"><span>Folder</span><input class="cst-set-input" id="docsync-SourcePath" placeholder="docs"/></label>
	<p class="cst-set-sub">The repo subfolder to sync.</p>
	<label class="cst-set-field"><span>Target</span><input class="cst-set-input" id="docsync-TargetPath" placeholder="optional"/></label>
	<label class="cst-set-field"><span>Direction</span><select class="cst-set-input" id="docsync-Direction"><option value="pull">Pull (repo to plan sheet)</option><option value="push">Push (plan sheet to repo)</option><option value="both" selected>Both</option></select></label>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveDocSyncBinding()">Save connection</button><button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].runDocSync()">Sync now</button><span id="docsync-msg" class="cst-set-msg"></span></div>`;
	}

	// The stored bindings, listed below the form: repo, direction, which credential authorizes it, and
	// the last sync outcome. Empty state shows a placeholder so the section reads the same either way.
	_docsyncBindingsBlock()
	{
		let tmpBindings = Array.isArray(this._docsyncBindings) ? this._docsyncBindings : [];
		let tmpTokens = Array.isArray(this._docsyncTokens) ? this._docsyncTokens : [];
		let tmpRows = tmpBindings.map((pBinding) =>
		{
			let tmpBits = [];
			if (pBinding.LastStatus) { tmpBits.push(String(pBinding.LastStatus)); }
			if (pBinding.LastMessage) { tmpBits.push(String(pBinding.LastMessage)); }
			let tmpWhen = _formatDate(pBinding.LastSyncDate);
			if (tmpWhen) { tmpBits.push(tmpWhen); }
			let tmpStatus = tmpBits.length ? _escapeHTML(tmpBits.join(' | ')) : 'Not synced yet.';
			// Which credential authorizes this binding (a pinned account token wins over the App).
			let tmpAuth = '';
			if (pBinding.IDAccountGithubToken)
			{
				let tmpToken = tmpTokens.find((pT) => (parseInt(pT.IDAccountGithubToken, 10) || 0) === (parseInt(pBinding.IDAccountGithubToken, 10) || 0));
				tmpAuth = 'token ' + _escapeHTML(tmpToken ? (tmpToken.Label || ('...' + tmpToken.Last4)) : ('#' + pBinding.IDAccountGithubToken));
			}
			else if (pBinding.GithubInstallationID) { tmpAuth = 'GitHub App'; }
			return `<div class="cst-set-sub" style="margin:8px 0;"><strong style="color:var(--theme-color-text-primary,#2c3140);">${_escapeHTML(pBinding.RepoURL || '')}</strong> (${_escapeHTML(pBinding.Direction || 'both')})${tmpAuth ? ' via ' + tmpAuth : ''}<br/>${tmpStatus}</div>`;
		}).join('');
		return `
	<hr class="cst-set-rule" />
	<h2 class="cst-set-h">Connected repositories</h2>
	${tmpRows || '<div class="cst-set-placeholder">No connections yet.</div>'}`;
	}

	_loadDocSync()
	{
		let tmpAPI = this.pict.providers.DocSyncAPI;
		if (!tmpAPI) { return; }
		// The GitHub App state, the account's GitHub tokens, and the stored bindings load together;
		// each call falls back to an empty shape so one failure does not blank the whole section.
		Promise.all(
			[
				tmpAPI.listInstallations().catch(() => ({ Configured: false, InstallURL: '', Installations: [] })),
				tmpAPI.getBindings().catch(() => []),
				tmpAPI.listGithubTokens().catch(() => ({ Configured: false, Tokens: [] }))
			]).then((pResults) =>
		{
			this._docsyncData = pResults[0] || { Configured: false, InstallURL: '', Installations: [] };
			this._docsyncBindings = Array.isArray(pResults[1]) ? pResults[1] : [];
			let tmpTokens = pResults[2] || { Configured: false, Tokens: [] };
			this._docsyncTokens = Array.isArray(tmpTokens.Tokens) ? tmpTokens.Tokens : [];
			this._docsyncTokenEncryption = !!tmpTokens.Configured;
			this._docsyncLoaded = true;
			if (this._activeSection === 'docsync') { this._setContent(this._section_docsync()); }
		});
	}

	// Refetch just the account's GitHub tokens (after an add/revoke) and re-render if still on the
	// section, then run an optional callback so a status message on the fresh DOM sticks.
	_reloadGithubTokens(fAfter)
	{
		let tmpAPI = this.pict.providers.DocSyncAPI;
		if (!tmpAPI) { if (typeof fAfter === 'function') { fAfter(); } return; }
		tmpAPI.listGithubTokens()
			.then((pResult) =>
			{
				let tmpResult = pResult || { Configured: false, Tokens: [] };
				this._docsyncTokens = Array.isArray(tmpResult.Tokens) ? tmpResult.Tokens : [];
				this._docsyncTokenEncryption = !!tmpResult.Configured;
				if (this._activeSection === 'docsync') { this._setContent(this._section_docsync()); }
				if (typeof fAfter === 'function') { fAfter(); }
			})
			.catch(() => { if (typeof fAfter === 'function') { fAfter(); } });
	}

	// Refetch just the stored bindings (after a save or a sync) and re-render the section if still on
	// it, then run an optional callback so a status message set on the freshly rendered span sticks.
	_reloadDocSyncBindings(fAfter)
	{
		let tmpAPI = this.pict.providers.DocSyncAPI;
		if (!tmpAPI) { if (typeof fAfter === 'function') { fAfter(); } return; }
		tmpAPI.getBindings()
			.then((pBindings) =>
			{
				this._docsyncBindings = Array.isArray(pBindings) ? pBindings : [];
				if (this._activeSection === 'docsync') { this._setContent(this._section_docsync()); }
				if (typeof fAfter === 'function') { fAfter(); }
			})
			.catch(() => { if (typeof fAfter === 'function') { fAfter(); } });
	}

	saveDocSyncBinding()
	{
		if (typeof document === 'undefined') { return; }
		let tmpGet = (pId) => (document.getElementById(pId) || {}).value || '';
		let tmpGithub = tmpGet('docsync-repo').trim();
		if (!tmpGithub) { this._msg('docsync-msg', 'Enter a repository (owner/name).', 'err'); return; }
		let tmpPatch = { Github: tmpGithub };
		// The chosen credential routes to one of the two mutually-exclusive binding pins. Always set
		// BOTH (the chosen one, and the other cleared) so switching a binding's credential is clean.
		let tmpCred = tmpGet('docsync-cred');
		if (tmpCred.indexOf('token:') === 0) { tmpPatch.IDAccountGithubToken = parseInt(tmpCred.slice(6), 10) || 0; tmpPatch.GithubInstallationID = ''; }
		else if (tmpCred.indexOf('app:') === 0) { tmpPatch.GithubInstallationID = tmpCred.slice(4); tmpPatch.IDAccountGithubToken = 0; }
		let tmpRef = tmpGet('docsync-Ref'); if (tmpRef) { tmpPatch.Ref = tmpRef; }
		let tmpSource = tmpGet('docsync-SourcePath'); if (tmpSource) { tmpPatch.SourcePath = tmpSource; }
		let tmpTarget = tmpGet('docsync-TargetPath'); if (tmpTarget) { tmpPatch.TargetPath = tmpTarget; }
		let tmpDirection = tmpGet('docsync-Direction'); if (tmpDirection) { tmpPatch.Direction = tmpDirection; }
		this._msg('docsync-msg', 'Saving...', '');
		this.pict.providers.DocSyncAPI.saveBinding(tmpPatch)
			.then(() => this._reloadDocSyncBindings(() => this._msg('docsync-msg', 'Saved.', 'ok')))
			.catch((pErr) => this._msg('docsync-msg', pErr.message || 'Could not save.', 'err'));
	}

	runDocSync()
	{
		if (typeof document === 'undefined') { return; }
		let tmpGet = (pId) => (document.getElementById(pId) || {}).value || '';
		let tmpGithub = tmpGet('docsync-repo').trim();
		// Every Run field is optional; only send the ones the user filled in so an empty box does not
		// override a stored binding's value. Omitting Github entirely re-runs all stored bindings.
		let tmpBody = {};
		if (tmpGithub) { tmpBody.Github = tmpGithub; }
		let tmpRef = tmpGet('docsync-Ref'); if (tmpRef) { tmpBody.Ref = tmpRef; }
		let tmpSource = tmpGet('docsync-SourcePath'); if (tmpSource) { tmpBody.SourcePath = tmpSource; }
		let tmpTarget = tmpGet('docsync-TargetPath'); if (tmpTarget) { tmpBody.TargetPath = tmpTarget; }
		this._msg('docsync-msg', 'Syncing...', 'ok');
		this.pict.providers.DocSyncAPI.runSync(tmpBody)
			.then((pResults) =>
			{
				let tmpResults = Array.isArray(pResults) ? pResults : [];
				let tmpFiles = 0;
				let tmpErrors = [];
				tmpResults.forEach((pResult) =>
				{
					if (pResult && pResult.Error) { tmpErrors.push((pResult.Repo || 'repo') + ': ' + pResult.Error); }
					else if (pResult && typeof pResult.FilesSynced === 'number') { tmpFiles += pResult.FilesSynced; }
				});
				let tmpSummary = 'Synced ' + tmpFiles + ' file' + (tmpFiles === 1 ? '' : 's') + '.';
				if (tmpErrors.length) { tmpSummary += ' ' + tmpErrors.join('; '); }
				this._reloadDocSyncBindings(() => this._msg('docsync-msg', tmpSummary, tmpErrors.length ? 'err' : 'ok'));
			})
			.catch((pErr) => this._msg('docsync-msg', pErr.message || 'Could not sync.', 'err'));
	}

	// Reveal or re-mask the token input so the operator can check a paste (whitespace, wrong value)
	// without the secret being masked the whole time. Masked by default; this is an explicit choice.
	toggleTokenVisibility()
	{
		if (typeof document === 'undefined') { return; }
		let tmpInput = document.getElementById('ghtok-Token');
		let tmpButton = document.getElementById('ghtok-Token-toggle');
		if (!tmpInput) { return; }
		let tmpReveal = (tmpInput.type === 'password');
		tmpInput.type = tmpReveal ? 'text' : 'password';
		if (tmpButton) { tmpButton.textContent = tmpReveal ? 'Hide' : 'Show'; }
	}

	// Add a GitHub token: the server validates it against GitHub, encrypts it, and never echoes it
	// back. On success the token list reloads (which clears the form, including the password field).
	addGithubToken()
	{
		if (typeof document === 'undefined') { return; }
		let tmpGet = (pId) => (document.getElementById(pId) || {}).value || '';
		let tmpToken = tmpGet('ghtok-Token').trim();
		if (!tmpToken) { this._msg('ghtok-msg', 'Paste a GitHub token first.', 'err'); return; }
		let tmpBody = { Token: tmpToken };
		let tmpLabel = tmpGet('ghtok-Label').trim(); if (tmpLabel) { tmpBody.Label = tmpLabel; }
		let tmpScope = tmpGet('ghtok-Scope').trim(); if (tmpScope) { tmpBody.Scope = tmpScope; }
		let tmpRepo = tmpGet('ghtok-Repo').trim(); if (tmpRepo) { tmpBody.Repo = tmpRepo; }
		this._msg('ghtok-msg', 'Validating with GitHub...', '');
		this.pict.providers.DocSyncAPI.addGithubToken(tmpBody)
			.then((pResult) =>
			{
				let tmpProbe = (pResult && pResult.Probe) || {};
				let tmpSummary = 'Added' + (tmpProbe.Login ? (' as ' + tmpProbe.Login) : '')
					+ ((typeof tmpProbe.RepoCount === 'number') ? (', ' + tmpProbe.RepoCount + ' repo' + (tmpProbe.RepoCount === 1 ? '' : 's') + ' reachable') : '')
					+ (tmpProbe.CanWrite ? ', write access.' : ', read only.');
				this._reloadGithubTokens(() => this._msg('ghtok-msg', tmpSummary, 'ok'));
			})
			.catch((pErr) => this._msg('ghtok-msg', pErr.message || 'Could not add the token.', 'err'));
	}

	// Re-run the GitHub self-test for a stored token and report what it sees (and refresh CanWrite).
	testGithubToken(pID)
	{
		let tmpID = parseInt(pID, 10) || 0;
		if (!tmpID) { return; }
		this._msg('ghtok-msg', 'Testing with GitHub...', '');
		this.pict.providers.DocSyncAPI.testGithubToken(tmpID, {})
			.then((pResult) =>
			{
				let tmpSummary = (pResult.Login ? ('Valid as ' + pResult.Login) : 'Valid')
					+ ((typeof pResult.RepoCount === 'number') ? (', ' + pResult.RepoCount + ' repo' + (pResult.RepoCount === 1 ? '' : 's')) : '')
					+ (pResult.CanWrite ? ', write access.' : ', read only.');
				this._reloadGithubTokens(() => this._msg('ghtok-msg', tmpSummary, 'ok'));
			})
			.catch((pErr) => this._msg('ghtok-msg', pErr.message || 'Test failed.', 'err'));
	}

	// Revoke (soft-delete) a stored token, after a confirm. Any binding pinned to it falls back to the
	// GitHub App on its next sync. The human should also delete the token on GitHub.
	revokeGithubToken(pID)
	{
		let tmpID = parseInt(pID, 10) || 0;
		if (!tmpID) { return; }
		let fDo = () => this.pict.providers.DocSyncAPI.revokeGithubToken(tmpID)
			.then(() => this._reloadGithubTokens(() => this._msg('ghtok-msg', 'Revoked.', 'ok')))
			.catch((pErr) => this._msg('ghtok-msg', pErr.message || 'Could not revoke.', 'err'));
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Revoke this GitHub token? Any connection using it falls back to the GitHub App, and sync may stop until you add a new token. Remember to delete the token on GitHub too.',
				{ title: 'Revoke token?', confirmLabel: 'Revoke', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fDo(); } });
		}
		else { fDo(); }
	}

	// ── Drafting Kit (the plan sheet's language) ──────────────────────────────
	// The Drafting Kit surface: a library of the tenant's kits (list mode), or one kit's editor when a
	// kit id is in the route (#/settings/draftingkit/<id>, parsed by showSection into _editingKitID).
	_section_draftingkit()
	{
		if (this._editingKitID) { return this._section_kitEditor(); }
		return this._section_kitLibrary();
	}

	// List mode: the tenant's own kits + the platform defaults offered as starting templates.
	_section_kitLibrary()
	{
		if (!this._kitLibraryLoaded) { return this._loadingSection('Drafting Kits'); }
		let tmpLib = this._kitLibrary || { Owned: [], Templates: [], IDDefaultVisionType: 0 };
		let tmpEnts = (this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.Entitlements) || [];
		let tmpCanHouse = Array.isArray(tmpEnts) && tmpEnts.indexOf('platform.manage') >= 0;
		let tmpOwned = Array.isArray(tmpLib.Owned) ? tmpLib.Owned : [];
		let tmpCards = tmpOwned.map((pKit) => this._kitCard(pKit, tmpCanHouse)).join('') ||
			'<div class="cst-set-placeholder">No kits yet. Create one to start, or seed it from a platform default below.</div>';
		let tmpTemplateOptions = (Array.isArray(tmpLib.Templates) ? tmpLib.Templates : [])
			.map((pT) => `<option value="${pT.ID}">${_escapeHTML(pT.Name)}</option>`).join('');
		return `
	<div class="cst-set-section cst-set-section-wide">
		<h2 class="cst-set-h">Drafting Kits</h2>
		<p class="cst-set-sub">A kit is your craft's language and look: the words, the connection style, the sticker sets, and the loop checklists. Build your own, or start from a platform default and reshape it. Make one the house default, or point any vision at a kit from its Craft picker.</p>
		<div class="dk-newkit">
			<input id="dk-newkit-name" class="cst-set-input dk-newkit-name" type="text" placeholder="New kit name (e.g. Our Product, Hardware, Garden)" autocomplete="off"/>
			<select id="dk-newkit-template" class="cst-set-input"><option value="0">Start blank</option>${tmpTemplateOptions}</select>
			<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].createKit()">Create kit</button>
			<span id="dk-lib-msg" class="cst-set-msg"></span>
		</div>
		<div class="dk-kitgrid">${tmpCards}</div>
	</div>`;
	}

	_kitCard(pKit, pCanHouse)
	{
		let tmpFacets = [];
		if (pKit.TermCount) { tmpFacets.push(pKit.TermCount + (pKit.TermCount === 1 ? ' word' : ' words') + ' renamed'); }
		if (pKit.DefaultStickerSet) { tmpFacets.push('stickers ' + _escapeHTML(pKit.DefaultStickerSet)); }
		if (pKit.LoopCount) { tmpFacets.push(pKit.LoopCount + (pKit.LoopCount === 1 ? ' loop step' : ' loop steps')); }
		let tmpConn = pKit.ConnectionStyle || {};
		let tmpSwatch = `<span class="dk-swatch" style="background:${_escapeHTML(tmpConn.StrokeColor || '#5b6376')}"></span>`;
		let tmpSummary = tmpFacets.length ? tmpFacets.join(' &middot; ') : 'Using the defaults so far';
		let tmpHouse = pKit.IsHouse ? '<span class="dk-housebadge">House default</span>' : '';
		let tmpHouseBtn = (!pKit.IsHouse && pCanHouse) ? `<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].makeHouseKit(${pKit.ID})">Make house default</button>` : '';
		return `
		<div class="dk-kitcard">
			<div class="dk-kitcard-head">${tmpSwatch}<strong>${_escapeHTML(pKit.Name)}</strong>${tmpHouse}</div>
			<div class="dk-kitcard-sub">${tmpSummary}</div>
			<div class="dk-kitcard-actions">
				<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].openKit(${pKit.ID})">Open</button>
				<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].duplicateKit(${pKit.ID})">Duplicate</button>
				<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].renameKitPrompt(${pKit.ID})">Rename</button>
				${tmpHouseBtn}
				<button class="cst-set-btn cst-set-btn-danger" onclick="_Pict.views['Plansheet-UserSettings'].deleteKitPrompt(${pKit.ID})">Delete</button>
			</div>
		</div>`;
	}

	// Editor mode: one kit's words, connection style, sticker sets, and loops.
	_section_kitEditor()
	{
		if (!this._draftingKitLoaded) { return this._loadingSection('Drafting Kit'); }
		let tmpKit = this._draftingKit || {};
		let tmpKeys = tmpKit.Keys || [];
		let tmpDefaults = tmpKit.Defaults || {};
		let tmpResolved = tmpKit.Resolved || {};
		let tmpHouse = tmpKit.IsHouse ? '<span class="dk-housebadge">House default</span>' : '';
		let tmpHeader = `
	<div class="dk-editor-head">
		<a class="dk-back" href="#" onclick="_Pict.views['Plansheet-UserSettings'].openKitLibrary(); return false;">&larr; All kits</a>
		<div class="dk-editor-title"><input id="dk-name" class="cst-set-input dk-name-in" type="text" value="${_escapeHTML(tmpKit.Name || '')}"/><button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].renameKitInline()">Rename</button>${tmpHouse}<span id="dk-name-msg" class="cst-set-msg"></span></div>
	</div>`;
		let tmpRows = tmpKeys.map((pKey) =>
		{
			let tmpR = tmpResolved[pKey] || tmpDefaults[pKey] || {};
			let tmpD = tmpDefaults[pKey] || {};
			let tmpOnInput = `oninput="_Pict.views['Plansheet-UserSettings'].previewTerms()"`;
			return `<div class="dk-row"><div class="dk-row-head">${_escapeHTML(tmpD.Singular || pKey)}</div>` +
				`<div class="dk-fields">` +
					`<label class="dk-field"><span>Singular</span><input id="dk-${pKey}-s" class="cst-set-input dk-in" value="${_escapeHTML(tmpR.Singular || '')}" placeholder="${_escapeHTML(tmpD.Singular || '')}" ${tmpOnInput}/></label>` +
					`<label class="dk-field"><span>Plural</span><input id="dk-${pKey}-p" class="cst-set-input dk-in" value="${_escapeHTML(tmpR.Plural || '')}" placeholder="${_escapeHTML(tmpD.Plural || '')}" ${tmpOnInput}/></label>` +
				`</div>` +
				`<label class="dk-field dk-field-desc"><span>Description</span><input id="dk-${pKey}-d" class="cst-set-input dk-in" value="${_escapeHTML(tmpR.Description || '')}" placeholder="${_escapeHTML(tmpD.Description || '')}" ${tmpOnInput}/></label>` +
				`</div>`;
		}).join('');
		let tmpWordsBlock = `
	<h3 class="cst-set-h3">Words</h3>
	<p class="cst-set-sub">Rename the product words to fit this craft.</p>
	<div class="dk-rows">${tmpRows}</div>
	<div class="dk-preview" id="dk-preview"></div>
	<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveTerms()">Save words</button><span id="dk-msg" class="cst-set-msg"></span></div>`;
		let tmpConn = tmpKit.ConnectionStyle || {};
		let fSel = (pVal, pOpt) => (pVal === pOpt) ? ' selected' : '';
		let tmpConnBlock = `
	<div class="dk-conn" style="margin-top:22px;border-top:1px solid var(--theme-color-border-default,#e6e9ee);padding-top:16px;">
		<div class="cst-set-sub" style="font-weight:600;color:var(--theme-color-text-primary,#2c3140);">Connection style</div>
		<p class="cst-set-sub">The default look of a link drawn on this craft's boards. New links use it; you can still restyle any single link.</p>
		<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
			<label class="dk-field"><span>Color</span><input type="color" id="dk-conn-color" class="cst-set-input" value="${_escapeHTML(tmpConn.StrokeColor || '#5b6376')}"/></label>
			<label class="dk-field"><span>Width</span><input type="range" id="dk-conn-width" min="1" max="8" step="1" value="${parseInt(tmpConn.StrokeWidth, 10) || 2}"/></label>
			<label class="dk-field"><span>Line</span><select id="dk-conn-line" class="cst-set-input"><option value="solid"${fSel(tmpConn.StrokeStyle, 'solid')}>Solid</option><option value="dashed"${fSel(tmpConn.StrokeStyle, 'dashed')}>Dashed</option><option value="dotted"${fSel(tmpConn.StrokeStyle, 'dotted')}>Dotted</option></select></label>
			<label class="dk-field"><span>Curve</span><select id="dk-conn-curve" class="cst-set-input"><option value="Bezier"${fSel(tmpConn.EdgeTheme, 'Bezier')}>Curved</option><option value="Orthogonal"${fSel(tmpConn.EdgeTheme, 'Orthogonal')}>Elbow</option><option value="Straight"${fSel(tmpConn.EdgeTheme, 'Straight')}>Straight</option></select></label>
			<label class="dk-field"><span>Start end</span><select id="dk-conn-src" class="cst-set-input"><option value="none"${fSel(tmpConn.SourceMarker, 'none')}>None</option><option value="arrow"${fSel(tmpConn.SourceMarker, 'arrow')}>Arrow</option><option value="dot"${fSel(tmpConn.SourceMarker, 'dot')}>Dot</option><option value="square"${fSel(tmpConn.SourceMarker, 'square')}>Square</option></select></label>
			<label class="dk-field"><span>Finish end</span><select id="dk-conn-tgt" class="cst-set-input"><option value="none"${fSel(tmpConn.TargetMarker, 'none')}>None</option><option value="arrow"${fSel(tmpConn.TargetMarker, 'arrow')}>Arrow</option><option value="dot"${fSel(tmpConn.TargetMarker, 'dot')}>Dot</option><option value="square"${fSel(tmpConn.TargetMarker, 'square')}>Square</option></select></label>
		</div>
		<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveConnectionStyle()">Save connection style</button><span id="dk-conn-msg" class="cst-set-msg"></span></div>
	</div>`;
		let tmpStickerSetOptions = tmpKit.StickerSetOptions || [];
		let fSetOptions = (pCurrent, pDefaultLabel) => `<option value=""${pCurrent ? '' : ' selected'}>${pDefaultLabel}</option>` +
			tmpStickerSetOptions.map((pSet) => `<option value="${_escapeHTML(pSet.Key)}"${pSet.Key === pCurrent ? ' selected' : ''}>${_escapeHTML(pSet.Label)} (${pSet.Count})</option>`).join('');
		let tmpStickerOpts = fSetOptions(tmpKit.DefaultStickerSet || '', 'Board default (design / moodboard)');
		let tmpBrandOpts = fSetOptions(tmpKit.BrandStickerSet || '', 'Expressive default (moodboard)');
		let tmpStickerBlock = `
	<div class="dk-stickerset" style="margin-top:22px;border-top:1px solid var(--theme-color-border-default,#e6e9ee);padding-top:16px;">
		<div class="cst-set-sub" style="font-weight:600;color:var(--theme-color-text-primary,#2c3140);">Default sticker set</div>
		<p class="cst-set-sub">The stickers a board on this craft starts with before anyone searches. People can still search the whole library.</p>
		<label class="dk-field"><span>Set</span><select id="dk-stickerset" class="cst-set-input">${tmpStickerOpts}</select></label>
		<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveStickerSet()">Save sticker set</button><a class="cst-set-sub" href="#/settings/stickers" style="margin-left:10px;">Build a custom set</a><span id="dk-stickerset-msg" class="cst-set-msg"></span></div>
		<div class="cst-set-sub" style="font-weight:600;color:var(--theme-color-text-primary,#2c3140);margin-top:18px;">Profile / brand set</div>
		<p class="cst-set-sub">The stickers a teammate's profile moodboard starts with. Point this at a custom set of your brand and culture marks.</p>
		<label class="dk-field"><span>Set</span><select id="dk-brandset" class="cst-set-input">${tmpBrandOpts}</select></label>
		<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveBrandStickerSet()">Save brand set</button><a class="cst-set-sub" href="#/settings/stickers" style="margin-left:10px;">Build a custom set</a><span id="dk-brandset-msg" class="cst-set-msg"></span></div>
	</div>`;
		let tmpLoopMeta = tmpKit.LoopMeta || [];
		let tmpLoopRows = tmpLoopMeta.map((pLoop) => `
		<div class="dk-loop-block">
			<label class="dk-field dk-loop-label"><span>Loop name</span><input id="dk-looplabel-${_escapeHTML(pLoop.Key)}" class="cst-set-input" type="text" value="${_escapeHTML(pLoop.Label || '')}" placeholder="${_escapeHTML(pLoop.DefaultLabel || '')}"/></label>
			<div class="ps-md-host dk-loops-host" id="dk-loop-mdhost-${_escapeHTML(pLoop.Key)}"></div>
		</div>`).join('');
		let tmpLoopsBlock = `
	<div class="dk-loops" style="margin-top:22px;border-top:1px solid var(--theme-color-border-default,#e6e9ee);padding-top:16px;">
		<div class="cst-set-sub" style="font-weight:600;color:var(--theme-color-text-primary,#2c3140);">Loops</div>
		<p class="cst-set-sub">The three refinement loops every vision works through. Rename each to your craft's words, and write its checklist as markdown: <code>- [ ] a step</code>, indent two spaces for sub-steps. Empty a loop to fall back to the platform defaults.</p>
		${tmpLoopRows}
		<div class="cst-set-actions"><button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].saveLoops()">Save loops</button><span id="dk-loops-msg" class="cst-set-msg"></span></div>
	</div>`;
		return `
	<div class="cst-set-section cst-set-section-wide">
		${tmpHeader}
		${tmpWordsBlock}
		${tmpConnBlock}
		${tmpStickerBlock}
		${tmpLoopsBlock}
	</div>`;
	}

	_loadDraftingKit()
	{
		if (this._editingKitID) { return this._loadKitEditor(this._editingKitID); }
		return this._loadKitLibrary();
	}

	_loadKitLibrary()
	{
		let tmpWF = this.pict.providers.WorkflowAPI;
		let tmpSelf = this;
		this._kitLibraryLoaded = false;
		let fApply = (pLib) =>
		{
			tmpSelf._kitLibrary = pLib || { Owned: [], Templates: [], IDDefaultVisionType: 0 };
			tmpSelf._kitLibraryLoaded = true;
			if (tmpSelf._activeSection === 'draftingkit' && !tmpSelf._editingKitID) { tmpSelf._setContent(tmpSelf._section_draftingkit()); }
		};
		if (!tmpWF || typeof tmpWF.getLibrary !== 'function') { return fApply(null); }
		tmpWF.getLibrary().then(fApply).catch(() => fApply(null));
	}

	_loadKitEditor(pID)
	{
		let tmpWF = this.pict.providers.WorkflowAPI;
		let tmpSetAPI = this.pict.providers.StickerSetAPI;
		let tmpSelf = this;
		this._draftingKitLoaded = false;
		let fRender = () =>
		{
			tmpSelf._draftingKitLoaded = true;
			if (tmpSelf._activeSection === 'draftingkit' && tmpSelf._editingKitID === pID) { tmpSelf._setContent(tmpSelf._section_draftingkit()); tmpSelf.previewTerms(); tmpSelf._mountLoopsEditor(); }
		};
		if (!tmpWF || typeof tmpWF.getKit !== 'function') { this._draftingKit = null; return fRender(); }
		Promise.all([ tmpWF.getKit(pID), tmpSetAPI ? tmpSetAPI.listSets() : Promise.resolve(null) ]).then((pResults) =>
		{
			tmpSelf._draftingKit = pResults[0] || null;
			// Offer this tenant's custom sets in the sticker pickers alongside the built-in system sets.
			let tmpCustom = (pResults[1] && Array.isArray(pResults[1].Custom)) ? pResults[1].Custom : [];
			if (tmpSelf._draftingKit && tmpCustom.length)
			{
				let tmpOptions = Array.isArray(tmpSelf._draftingKit.StickerSetOptions) ? tmpSelf._draftingKit.StickerSetOptions.slice() : [];
				tmpCustom.forEach((pSet) => tmpOptions.push({ Key: pSet.Key, Label: pSet.Name + ' (yours)', Count: pSet.Count || 0 }));
				tmpSelf._draftingKit.StickerSetOptions = tmpOptions;
			}
			fRender();
		}).catch(() => fRender());
	}

	// ── kit library actions ──────────────────────────────────────────────────
	openKit(pID) { this._editingKitID = parseInt(pID, 10) || 0; this.showSection('draftingkit/' + this._editingKitID); }
	openKitLibrary() { this._editingKitID = 0; this.showSection('draftingkit'); }

	createKit()
	{
		if (typeof document === 'undefined') { return; }
		let tmpName = ((document.getElementById('dk-newkit-name') || {}).value || '').trim();
		let tmpTemplate = parseInt((document.getElementById('dk-newkit-template') || {}).value, 10) || 0;
		if (!tmpName) { this._msg('dk-lib-msg', 'Give the kit a name first.', 'err'); return; }
		this._msg('dk-lib-msg', 'Creating...', '');
		this.pict.providers.WorkflowAPI.createKit(tmpName, tmpTemplate)
			.then((pKit) => { if (pKit && pKit.ID) { this.openKit(pKit.ID); } else { this._loadKitLibrary(); } })
			.catch((pErr) => this._msg('dk-lib-msg', pErr.message || 'Could not create the kit.', 'err'));
	}

	duplicateKit(pID)
	{
		this._msg('dk-lib-msg', 'Duplicating...', '');
		this.pict.providers.WorkflowAPI.duplicateKit(pID)
			.then(() => this._loadKitLibrary())
			.catch((pErr) => this._msg('dk-lib-msg', pErr.message || 'Could not duplicate the kit.', 'err'));
	}

	makeHouseKit(pID)
	{
		this._msg('dk-lib-msg', 'Setting house default...', '');
		this.pict.providers.OrgAPI.setDraftingKit(pID)
			.then(() =>
			{
				let tmpApp = this.pict.PictApplication;
				if (tmpApp && typeof tmpApp._installSiteNav === 'function') { tmpApp._installSiteNav(); }
				this._loadKitLibrary();
			})
			.catch((pErr) => this._msg('dk-lib-msg', pErr.message || 'Could not set the house default.', 'err'));
	}

	renameKitPrompt(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		let tmpInputId = 'dk-rename-input';
		let tmpSelf = this;
		tmpModal.show({
			title: 'Rename kit',
			content: '<div class="cst-tenant-form"><label for="' + tmpInputId + '">Name</label><input id="' + tmpInputId + '" type="text" autocomplete="off"/></div>',
			buttons: [{ Hash: 'save', Label: 'Save', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' }],
			closeable: true,
			onOpen: () => { let tmpEl = document.getElementById(tmpInputId); if (tmpEl) { tmpEl.focus(); } }
		}).then((pHash) =>
		{
			if (pHash !== 'save') { return; }
			let tmpName = ((document.getElementById(tmpInputId) || {}).value || '').trim();
			if (!tmpName) { return; }
			tmpSelf.pict.providers.WorkflowAPI.renameKit(pID, tmpName)
				.then(() => tmpSelf._loadKitLibrary())
				.catch((pErr) => tmpSelf._msg('dk-lib-msg', pErr.message || 'Could not rename.', 'err'));
		});
	}

	renameKitInline()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let tmpName = ((document.getElementById('dk-name') || {}).value || '').trim();
		if (!tmpName) { this._msg('dk-name-msg', 'Name cannot be empty.', 'err'); return; }
		this._msg('dk-name-msg', 'Saving...', '');
		this.pict.providers.WorkflowAPI.renameKit(this._editingKitID, tmpName)
			.then((pBody) => { if (this._draftingKit) { this._draftingKit.Name = (pBody && pBody.Name) || tmpName; } this._msg('dk-name-msg', 'Saved.', 'ok'); })
			.catch((pErr) => this._msg('dk-name-msg', pErr.message || 'Could not rename.', 'err'));
	}

	deleteKitPrompt(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpSelf = this;
		if (!tmpModal)
		{
			this.pict.providers.WorkflowAPI.deleteKit(pID).then(() => this._loadKitLibrary());
			return;
		}
		tmpModal.show({
			title: 'Delete kit',
			content: '<p>Delete this kit? Boards keep their placed stickers and any vision using it falls back to the house words. This cannot be undone.</p>',
			buttons: [{ Hash: 'delete', Label: 'Delete', Style: 'danger' }, { Hash: 'cancel', Label: 'Cancel' }],
			closeable: true
		}).then((pHash) =>
		{
			if (pHash !== 'delete') { return; }
			tmpSelf.pict.providers.WorkflowAPI.deleteKit(pID)
				.then(() =>
				{
					let tmpApp = tmpSelf.pict.PictApplication;
					if (tmpApp && typeof tmpApp._installSiteNav === 'function') { tmpApp._installSiteNav(); }
					tmpSelf._loadKitLibrary();
				})
				.catch((pErr) => tmpSelf._msg('dk-lib-msg', pErr.message || 'Could not delete.', 'err'));
		});
	}

	// When the edited kit is the house default, the server refreshed the session; rebuild the nav so the
	// rail relabels live.
	_afterKitFacetSave(pBody)
	{
		if (pBody && pBody.IsHouse)
		{
			let tmpApp = this.pict.PictApplication;
			if (tmpApp && typeof tmpApp._installSiteNav === 'function') { tmpApp._installSiteNav(); }
		}
	}

	saveTerms()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let tmpKit = this._draftingKit || {};
		let tmpKeys = tmpKit.Keys || [];
		let tmpDefaults = tmpKit.Defaults || {};
		let tmpTerms = {};
		for (let i = 0; i < tmpKeys.length; i++)
		{
			let tmpKey = tmpKeys[i];
			let tmpSg = ((document.getElementById('dk-' + tmpKey + '-s') || {}).value || '').trim();
			let tmpPl = ((document.getElementById('dk-' + tmpKey + '-p') || {}).value || '').trim();
			let tmpDe = ((document.getElementById('dk-' + tmpKey + '-d') || {}).value || '').trim();
			let tmpD = tmpDefaults[tmpKey] || {};
			let tmpEntry = {};
			if (tmpSg && tmpSg !== tmpD.Singular) { tmpEntry.Singular = tmpSg; }
			if (tmpPl && tmpPl !== tmpD.Plural) { tmpEntry.Plural = tmpPl; }
			if (tmpDe && tmpDe !== tmpD.Description) { tmpEntry.Description = tmpDe; }
			if (tmpEntry.Singular || tmpEntry.Plural || tmpEntry.Description) { tmpTerms[tmpKey] = tmpEntry; }
		}
		this._msg('dk-msg', 'Saving...', '');
		this.pict.providers.WorkflowAPI.saveKitTerms(this._editingKitID, tmpTerms)
			.then((pBody) =>
			{
				if (this._draftingKit) { this._draftingKit.Terms = (pBody && pBody.Terms) || tmpTerms; this._draftingKit.Resolved = (pBody && pBody.Resolved) || this._draftingKit.Resolved; }
				this._msg('dk-msg', 'Saved.', 'ok');
				this._afterKitFacetSave(pBody);
			})
			.catch((pErr) => this._msg('dk-msg', pErr.message || 'Could not save the words.', 'err'));
	}

	// Save the kit's default connection (link) style from the Connection-style controls.
	saveConnectionStyle()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let fVal = (pId) => { let tmpEl = document.getElementById(pId); return tmpEl ? tmpEl.value : ''; };
		let tmpStyle =
		{
			StrokeColor: fVal('dk-conn-color'),
			StrokeWidth: parseInt(fVal('dk-conn-width'), 10) || 2,
			StrokeStyle: fVal('dk-conn-line'),
			EdgeTheme: fVal('dk-conn-curve'),
			SourceMarker: fVal('dk-conn-src'),
			TargetMarker: fVal('dk-conn-tgt')
		};
		this._msg('dk-conn-msg', 'Saving...', '');
		this.pict.providers.WorkflowAPI.saveKitConnectionStyle(this._editingKitID, tmpStyle)
			.then((pBody) => { if (this._draftingKit) { this._draftingKit.ConnectionStyle = (pBody && pBody.ConnectionStyle) || tmpStyle; } this._msg('dk-conn-msg', 'Saved.', 'ok'); this._afterKitFacetSave(pBody); })
			.catch((pErr) => this._msg('dk-conn-msg', pErr.message || 'Could not save the connection style.', 'err'));
	}

	// Save the kit's chosen default sticker set from the picker.
	saveStickerSet()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let tmpEl = document.getElementById('dk-stickerset');
		let tmpKey = tmpEl ? tmpEl.value : '';
		this._msg('dk-stickerset-msg', 'Saving...', '');
		this.pict.providers.WorkflowAPI.saveKitDefaultStickerSet(this._editingKitID, tmpKey)
			.then((pBody) => { if (this._draftingKit) { this._draftingKit.DefaultStickerSet = (pBody && pBody.DefaultStickerSet) || ''; } this._msg('dk-stickerset-msg', 'Saved.', 'ok'); this._afterKitFacetSave(pBody); })
			.catch((pErr) => this._msg('dk-stickerset-msg', pErr.message || 'Could not save the sticker set.', 'err'));
	}

	// Save the kit's chosen brand / profile sticker set from the picker.
	saveBrandStickerSet()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let tmpEl = document.getElementById('dk-brandset');
		let tmpKey = tmpEl ? tmpEl.value : '';
		this._msg('dk-brandset-msg', 'Saving...', '');
		this.pict.providers.WorkflowAPI.saveKitBrandStickerSet(this._editingKitID, tmpKey)
			.then((pBody) => { if (this._draftingKit) { this._draftingKit.BrandStickerSet = (pBody && pBody.BrandStickerSet) || ''; } this._msg('dk-brandset-msg', 'Saved.', 'ok'); this._afterKitFacetSave(pBody); })
			.catch((pErr) => this._msg('dk-brandset-msg', pErr.message || 'Could not save the brand set.', 'err'));
	}

	// Mount one rich markdown editor per loop, each seeded from that loop's items as a checklist.
	_mountLoopsEditor()
	{
		if (typeof document === 'undefined') { return; }
		let tmpKit = this._draftingKit || {};
		let tmpMeta = tmpKit.LoopMeta || [];
		let tmpLoops = tmpKit.Loops || {};
		let tmpSelf = this;
		tmpMeta.forEach((pLoop) =>
		{
			let tmpHostEl = document.getElementById('dk-loop-mdhost-' + pLoop.Key);
			if (!tmpHostEl) { return; }
			let tmpMarkdown = libLoopMarkdown.itemsToMarkdown(Array.isArray(tmpLoops[pLoop.Key]) ? tmpLoops[pLoop.Key] : []);
			let tmpEditor = tmpSelf.pict.views['Plansheet-LoopEditor-' + pLoop.Key];
			if (!tmpEditor)
			{
				// The editor is not in this build: plain textarea fallback holding the same markdown.
				tmpHostEl.innerHTML = '<textarea class="cst-set-input dk-loops-fallback" id="dk-loop-fallback-' + pLoop.Key + '" rows="6"></textarea>';
				let tmpFb = document.getElementById('dk-loop-fallback-' + pLoop.Key);
				if (tmpFb) { tmpFb.value = tmpMarkdown; }
				return;
			}
			let tmpAddr = 'LoopEditor_' + pLoop.Key;
			tmpSelf.pict.AppData[tmpAddr] = tmpSelf.pict.AppData[tmpAddr] || {};
			tmpSelf.pict.AppData[tmpAddr].Segments = [{ Content: tmpMarkdown }];
			try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
			tmpEditor.render();
			if (typeof tmpEditor.marshalToView === 'function') { tmpEditor.marshalToView(); }
			if (typeof tmpEditor.setPreviewMode === 'function') { tmpEditor.setPreviewMode('off'); }
		});
	}

	// Save the loops: read each loop's editor (-> checklist tree) and its name field, and persist both
	// the items and the (non-default) labels. The server re-sanitizes each.
	saveLoops()
	{
		if (typeof document === 'undefined' || !this._editingKitID) { return; }
		let tmpMeta = (this._draftingKit && this._draftingKit.LoopMeta) || [];
		let tmpLoops = {};
		let tmpLabels = {};
		tmpMeta.forEach((pLoop) =>
		{
			let tmpEditor = this.pict.views['Plansheet-LoopEditor-' + pLoop.Key];
			let tmpMarkdown = '';
			if (tmpEditor && typeof tmpEditor.getAllContent === 'function') { tmpMarkdown = tmpEditor.getAllContent('\n\n'); }
			else { let tmpFb = document.getElementById('dk-loop-fallback-' + pLoop.Key); tmpMarkdown = tmpFb ? tmpFb.value : ''; }
			tmpLoops[pLoop.Key] = libLoopMarkdown.markdownToItems(tmpMarkdown);
			let tmpLabelEl = document.getElementById('dk-looplabel-' + pLoop.Key);
			let tmpLabelVal = tmpLabelEl ? String(tmpLabelEl.value || '').trim() : '';
			if (tmpLabelVal && tmpLabelVal !== (pLoop.DefaultLabel || '')) { tmpLabels[pLoop.Key] = tmpLabelVal; }
		});
		this._msg('dk-loops-msg', 'Saving...', '');
		let tmpWF = this.pict.providers.WorkflowAPI;
		Promise.all([ tmpWF.saveKitLoops(this._editingKitID, tmpLoops), tmpWF.saveKitLoopLabels(this._editingKitID, tmpLabels) ])
			.then((pResults) =>
			{
				if (this._draftingKit) { this._draftingKit.Loops = tmpLoops; }
				this._msg('dk-loops-msg', 'Saved.', 'ok');
				this._afterKitFacetSave(pResults[0] || pResults[1]);
			})
			.catch((pErr) => this._msg('dk-loops-msg', pErr.message || 'Could not save the loops.', 'err'));
	}

	// Re-resolve the shell lexicon updated the AppData live; rebuild the nav so the
	// rail relabels at once, and re-render this section to show the new words.
	_afterKitChange()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._installSiteNav === 'function') { tmpApp._installSiteNav(); }
		this._draftingKitLoaded = false;
		this._loadDraftingKit();
	}

	previewTerms()
	{
		if (typeof document === 'undefined') { return; }
		let tmpKeys = (this._draftingKit && this._draftingKit.Keys) || [];
		let tmpParts = [];
		for (let i = 0; i < tmpKeys.length; i++)
		{
			let tmpPl = ((document.getElementById('dk-' + tmpKeys[i] + '-p') || {}).value || '').trim();
			if (tmpPl) { tmpParts.push(_escapeHTML(tmpPl)); }
		}
		let tmpEl = document.getElementById('dk-preview');
		if (tmpEl) { tmpEl.innerHTML = tmpParts.length ? ('In your words: ' + tmpParts.join(' &middot; ')) : ''; }
	}

	// ── Stickers (admin-managed moodboard sticker library) ────────────────────
	_section_stickers()
	{
		let tmpStore = this.pict.AppData.StickerSettings || {};
		let tmpSets = tmpStore.Sets || { System: [], Custom: [] };
		let tmpUploads = tmpStore.Custom || [];
		let tmpAPI = this.pict.providers.StickerAPI;
		let tmpShapes = this.pict.providers.ShapesAPI;
		let tmpActiveId = tmpStore.ActiveSetId || 0;
		let tmpChips = (tmpSets.Custom || []).map((pSet) =>
			`<button class="cst-setchip${pSet.IDStickerSet === tmpActiveId ? ' is-active' : ''}" onclick="_Pict.views['Plansheet-UserSettings'].selectStickerSet(${pSet.IDStickerSet})">${_escapeHTML(pSet.Name)} <span class="cst-setchip-n">${pSet.Count}</span></button>`).join('');
		let tmpActiveSet = (tmpSets.Custom || []).find((pSet) => pSet.IDStickerSet === tmpActiveId);
		let tmpEditor;
		if (!tmpActiveSet)
		{
			tmpEditor = tmpStore.SetsLoaded ? '<div class="cst-set-placeholder">Pick a set above, or create one to start curating.</div>' : '<div class="cst-set-placeholder">Loading...</div>';
		}
		else
		{
			let tmpItems = (tmpActiveSet.Items || []).map((pItem, pIdx) =>
			{
				let tmpUrl = pItem.IDSticker ? (tmpAPI ? tmpAPI.blobURL(pItem.IDSticker) : '') : (tmpShapes ? tmpShapes.shapeURL(pItem.Collection, pItem.Name) : '');
				return `<div class="cst-sticker-tile" title="${_escapeHTML(pItem.Title || pItem.Name)}"><img src="${tmpUrl}" alt=""><span class="cst-sticker-name">${_escapeHTML(pItem.Title || pItem.Name)}</span><button class="cst-sticker-del" onclick="_Pict.views['Plansheet-UserSettings'].removeSetItem(${pIdx})">Remove</button></div>`;
			}).join('') || '<div class="cst-set-placeholder">Empty. Add stickers from the library or your uploads below.</div>';
			let tmpUploadPick = tmpUploads.length
				? tmpUploads.map((pRow) => `<button class="cst-sticker-tile cst-tile-pick" title="Add ${_escapeHTML(pRow.Name)}" onclick="_Pict.views['Plansheet-UserSettings'].addUploadToSet(${pRow.IDSticker})"><img src="${tmpAPI ? tmpAPI.blobURL(pRow.IDSticker) : ''}" alt=""><span class="cst-sticker-name">${_escapeHTML(pRow.Name)}</span></button>`).join('')
				: '<div class="cst-set-placeholder">No uploaded art yet. Upload PNG or SVG cutouts below.</div>';
			tmpEditor = `
	<div class="cst-setedit-head"><strong>${_escapeHTML(tmpActiveSet.Name)}</strong>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].renameStickerSet(${tmpActiveSet.IDStickerSet})">Rename</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="_Pict.views['Plansheet-UserSettings'].removeStickerSet(${tmpActiveSet.IDStickerSet})">Delete</button>
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].addLibraryToSet()">Add from library</button>
		<span id="us-set-msg" class="cst-set-msg"></span>
	</div>
	<div class="cst-sticker-grid">${tmpItems}</div>
	<h3 class="cst-set-h3">Add one of your uploads</h3>
	<div class="cst-sticker-grid">${tmpUploadPick}</div>`;
		}
		return `
<div class="cst-set-section cst-set-section-wide">
	<h2 class="cst-set-h">Stickers</h2>
	<p class="cst-set-sub">Curate sets your team places on boards, then assign one to a craft in <a href="#/settings/draftingkit">Drafting Kit</a>. Browse the <a href="#/sticker-sets-preview">built-in sets</a>. Upload PNG or SVG cutouts to add your own art.</p>
	<div class="cst-setchips">${tmpChips}<button class="cst-setchip cst-setchip-new" onclick="_Pict.views['Plansheet-UserSettings'].newStickerSet()">+ New set</button></div>
	<div class="cst-setedit">${tmpEditor}</div>
	<div class="cst-set-actions" style="margin-top:18px;border-top:1px solid var(--theme-color-border-default,#e6e9ee);padding-top:14px;">
		<button class="cst-set-btn cst-set-btn-secondary" onclick="document.getElementById('us-sticker-file').click()">Upload sticker art</button>
		<input type="file" id="us-sticker-file" accept="image/svg+xml,image/png" multiple style="display:none" onchange="_Pict.views['Plansheet-UserSettings'].uploadStickers(this.files); this.value='';">
		<span id="us-sticker-msg" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	// ── Sticker sets (curated collections backed by StickerSetAPI) ─────────────
	_loadStickerSets()
	{
		let tmpSetAPI = this.pict.providers.StickerSetAPI;
		this.pict.AppData.StickerSettings = this.pict.AppData.StickerSettings || { Custom: [] };
		if (!tmpSetAPI) { return; }
		let tmpSelf = this;
		tmpSetAPI.listSets().then((pBody) =>
		{
			tmpSelf.pict.AppData.StickerSettings.Sets = pBody || { System: [], Custom: [] };
			tmpSelf.pict.AppData.StickerSettings.SetsLoaded = true;
			if (tmpSelf._activeSection === 'stickers') { tmpSelf._setContent(tmpSelf._section_stickers()); }
		});
	}

	selectStickerSet(pId)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSettingsSub === 'function'
			&& tmpApp.showSettingsSub('stickers', parseInt(pId, 10) || 0)) { return; }
		this._applySelectStickerSet(pId);
	}

	_applySelectStickerSet(pId)
	{
		this.pict.AppData.StickerSettings = this.pict.AppData.StickerSettings || {};
		this.pict.AppData.StickerSettings.ActiveSetId = parseInt(pId, 10) || 0;
		if (this._activeSection === 'stickers') { this._setContent(this._section_stickers()); }
	}

	newStickerSet()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		let tmpInputId = 'us-newset-name';
		let tmpSelf = this;
		tmpModal.show({
			title: 'New sticker set',
			content: '<div class="cst-tenant-form"><label for="' + tmpInputId + '">Name</label><input id="' + tmpInputId + '" type="text" autocomplete="off" placeholder="e.g. Acme brand, Garden icons"/></div>',
			buttons: [{ Hash: 'create', Label: 'Create', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' }],
			closeable: true,
			onOpen: () => { let tmpEl = document.getElementById(tmpInputId); if (tmpEl) { tmpEl.focus(); } }
		}).then((pHash) =>
		{
			if (pHash !== 'create') { return; }
			let tmpName = ((document.getElementById(tmpInputId) || {}).value || '').trim();
			if (!tmpName || !tmpSelf.pict.providers.StickerSetAPI) { return; }
			tmpSelf.pict.providers.StickerSetAPI.createSet(tmpName, []).then((pBody) =>
			{
				tmpSelf.pict.AppData.StickerSettings.ActiveSetId = (pBody && pBody.StickerSet && pBody.StickerSet.IDStickerSet) || 0;
				tmpSelf._loadStickerSets();
			}).catch(() => { /* the message slot is per-set; ignore */ });
		});
	}

	renameStickerSet(pId)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		let tmpSet = (((this.pict.AppData.StickerSettings || {}).Sets || {}).Custom || []).find((pCandidate) => pCandidate.IDStickerSet === pId);
		let tmpInputId = 'us-renameset-name';
		let tmpSelf = this;
		tmpModal.show({
			title: 'Rename set',
			content: '<div class="cst-tenant-form"><label for="' + tmpInputId + '">Name</label><input id="' + tmpInputId + '" type="text" value="' + _escapeHTML((tmpSet && tmpSet.Name) || '') + '"/></div>',
			buttons: [{ Hash: 'save', Label: 'Save', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' }],
			closeable: true,
			onOpen: () => { let tmpEl = document.getElementById(tmpInputId); if (tmpEl) { tmpEl.focus(); tmpEl.select(); } }
		}).then((pHash) =>
		{
			if (pHash !== 'save') { return; }
			let tmpName = ((document.getElementById(tmpInputId) || {}).value || '').trim();
			if (!tmpName || !tmpSelf.pict.providers.StickerSetAPI) { return; }
			tmpSelf.pict.providers.StickerSetAPI.renameSet(pId, tmpName).then(() => tmpSelf._loadStickerSets()).catch(() => {});
		});
	}

	removeStickerSet(pId)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpSelf = this;
		let fDo = () => tmpSelf.pict.providers.StickerSetAPI.deleteSet(pId).then(() =>
		{
			if (tmpSelf.pict.AppData.StickerSettings.ActiveSetId === pId) { tmpSelf.pict.AppData.StickerSettings.ActiveSetId = 0; }
			tmpSelf._loadStickerSets();
		}).catch(() => {});
		if (!tmpModal || typeof tmpModal.confirm !== 'function') { return fDo(); }
		tmpModal.confirm('Delete this sticker set? Boards already using its stickers keep them; the set just stops being offered.',
			{ title: 'Delete set?', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true })
			.then((pOk) => { if (pOk) { fDo(); } });
	}

	addLibraryToSet()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || !(this.pict.AppData.StickerSettings || {}).ActiveSetId) { return; }
		let tmpSelf = this;
		tmpModal.show({
			title: 'Add from the library',
			content: '<div class="cst-libpick"><input id="us-libpick-q" class="cst-set-input" placeholder="Search thousands of stickers..." oninput="_Pict.views[\'Plansheet-UserSettings\']._libpickSearch(this.value)"><div id="us-libpick-grid" class="cst-sticker-grid" style="margin-top:10px;max-height:340px;overflow:auto;"></div></div>',
			buttons: [{ Hash: 'done', Label: 'Done', Style: 'primary' }],
			closeable: true,
			width: '520px',
			onOpen: () => { tmpSelf._libpickSearch(''); let tmpEl = document.getElementById('us-libpick-q'); if (tmpEl) { tmpEl.focus(); } }
		});
	}

	_libpickSearch(pQuery)
	{
		let tmpShapes = this.pict.providers.ShapesAPI;
		let tmpGrid = (typeof document !== 'undefined') ? document.getElementById('us-libpick-grid') : null;
		if (!tmpGrid || !tmpShapes) { return; }
		let tmpQ = String(pQuery || '').trim();
		if (tmpQ.length < 2) { tmpGrid.innerHTML = '<div class="cst-set-placeholder">Type to search the library.</div>'; return; }
		tmpShapes.search(tmpQ, '', 48).then((pRows) =>
		{
			let tmpEl = document.getElementById('us-libpick-grid');
			if (!tmpEl) { return; }
			tmpEl.innerHTML = (Array.isArray(pRows) && pRows.length)
				? pRows.map((pRow) => `<button class="cst-sticker-tile cst-tile-pick" title="${_escapeHTML(pRow.Title || pRow.Name)}" onclick="_Pict.views['Plansheet-UserSettings'].pickLibraryItem('${pRow.Collection}','${pRow.Name}','${_escapeHTML(pRow.Title || pRow.Name)}')"><img src="${tmpShapes.shapeURL(pRow.Collection, pRow.Name)}" alt=""><span class="cst-sticker-name">${_escapeHTML(pRow.Title || pRow.Name)}</span></button>`).join('')
				: '<div class="cst-set-placeholder">No matches.</div>';
		}).catch(() => {});
	}

	pickLibraryItem(pCollection, pName, pTitle) { this._addItemToActiveSet({ Collection: pCollection, Name: pName, Title: pTitle }); }

	addUploadToSet(pIDSticker)
	{
		let tmpRow = ((this.pict.AppData.StickerSettings || {}).Custom || []).find((pCandidate) => pCandidate.IDSticker === pIDSticker);
		this._addItemToActiveSet({ IDSticker: pIDSticker, Name: (tmpRow && tmpRow.Name) || ('Sticker ' + pIDSticker) });
	}

	_addItemToActiveSet(pItem)
	{
		let tmpStore = this.pict.AppData.StickerSettings || {};
		let tmpSet = ((tmpStore.Sets && tmpStore.Sets.Custom) || []).find((pCandidate) => pCandidate.IDStickerSet === tmpStore.ActiveSetId);
		if (!tmpSet || !this.pict.providers.StickerSetAPI) { return; }
		let tmpItems = (tmpSet.Items || []).concat([pItem]);
		let tmpSelf = this;
		this.pict.providers.StickerSetAPI.saveItems(tmpSet.IDStickerSet, tmpItems).then(() => tmpSelf._loadStickerSets()).catch(() => {});
	}

	removeSetItem(pIndex)
	{
		let tmpStore = this.pict.AppData.StickerSettings || {};
		let tmpSet = ((tmpStore.Sets && tmpStore.Sets.Custom) || []).find((pCandidate) => pCandidate.IDStickerSet === tmpStore.ActiveSetId);
		if (!tmpSet || !this.pict.providers.StickerSetAPI) { return; }
		let tmpItems = (tmpSet.Items || []).slice();
		tmpItems.splice(pIndex, 1);
		let tmpSelf = this;
		this.pict.providers.StickerSetAPI.saveItems(tmpSet.IDStickerSet, tmpItems).then(() => tmpSelf._loadStickerSets()).catch(() => {});
	}

	_loadStickers()
	{
		let tmpAPI = this.pict.providers.StickerAPI;
		this.pict.AppData.StickerSettings = this.pict.AppData.StickerSettings || { Custom: [], Loaded: false };
		this._loadStickerSets();
		let tmpSelf = this;
		let fDone = () =>
		{
			tmpSelf.pict.AppData.StickerSettings.Loaded = true;
			if (tmpSelf._activeSection === 'stickers') { tmpSelf._setContent(tmpSelf._section_stickers()); }
		};
		if (!tmpAPI) { return fDone(); }
		tmpAPI.listLibrary().then((pRows) =>
		{
			let tmpCustom = (Array.isArray(pRows) ? pRows : []).filter((pRow) => !pRow.IsBuiltIn);
			tmpSelf.pict.AppData.StickerSettings = { Custom: tmpCustom, Loaded: true };
			if (tmpSelf._activeSection === 'stickers') { tmpSelf._setContent(tmpSelf._section_stickers()); }
		}).catch(() => fDone());
	}

	uploadStickers(pFileList)
	{
		let tmpAPI = this.pict.providers.StickerAPI;
		if (!tmpAPI || !pFileList || !pFileList.length) { return; }
		let tmpFiles = Array.prototype.slice.call(pFileList).filter((pFile) => pFile && (pFile.type === 'image/png' || pFile.type === 'image/svg+xml'));
		if (!tmpFiles.length) { this._msg('us-sticker-msg', 'Only PNG or SVG stickers are allowed.', 'err'); return; }
		this._msg('us-sticker-msg', 'Uploading ' + tmpFiles.length + ' sticker' + (tmpFiles.length > 1 ? 's' : '') + '...', '');
		let tmpSelf = this;
		Promise.all(tmpFiles.map((pFile) => tmpAPI.upload(pFile, { MimeType: pFile.type }).then(() => true).catch(() => false)))
			.then((pResults) =>
			{
				let tmpOk = pResults.filter(Boolean).length;
				tmpSelf._msg('us-sticker-msg', tmpOk + ' uploaded.' + (tmpOk < tmpFiles.length ? ' Some failed.' : ''), tmpOk ? 'ok' : 'err');
				tmpSelf._loadStickers();
			});
	}

	deleteSticker(pIDSticker)
	{
		let tmpAPI = this.pict.providers.StickerAPI;
		if (!tmpAPI) { return; }
		let tmpStore = this.pict.AppData.StickerSettings || { Custom: [] };
		let tmpRow = (tmpStore.Custom || []).find((pCandidate) => pCandidate.IDSticker === pIDSticker);
		let tmpName = (tmpRow && tmpRow.Name) || ('#' + pIDSticker);
		let tmpSelf = this;
		let fDelete = () => tmpAPI.remove(pIDSticker).then(() => tmpSelf._loadStickers());
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return fDelete(); }
		tmpModal.confirm('Remove sticker "' + tmpName + '"? Boards already using it keep their copy.',
			{ title: 'Remove sticker?', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
			.then((pOk) => { if (pOk) { fDelete(); } });
	}

	// ── Board Lanes (admin-managed Kanban lanes) ─────────────────────────────
	_section_lanes()
	{
		if (!this._lanesLoaded) { return this._loadingSection('Board Lanes'); }
		let tmpLanes = this._lanes || [];
		let tmpRows = tmpLanes.map((pLane) => this._laneRowHTML(pLane)).join('') ||
			'<div class="cst-set-placeholder">No lanes yet. Add one below.</div>';
		return `
<div class="cst-set-section cst-set-section-wide">
	<h2 class="cst-set-h">Board Lanes</h2>
	<p class="cst-set-sub">The columns on your <a href="#/board">board</a>. Rename, recolor, drag to reorder, and add or remove lanes. Mark the lane that means "done" as the Done lane. A lane that still holds cards cannot be removed until you move them.</p>
	<div id="us-lane-list" class="cst-lane-list">${tmpRows}</div>
	<div class="cst-lane-add">
		<input id="us-lane-new" class="cst-set-input" type="text" maxlength="128" placeholder="New lane name" onkeydown="if(event.key==='Enter'){_Pict.views['Plansheet-UserSettings'].addLane();}"/>
		<button class="cst-set-btn" onclick="_Pict.views['Plansheet-UserSettings'].addLane()">Add lane</button>
		<span id="us-lane-msg" class="cst-set-msg"></span>
	</div>
</div>`;
	}

	_laneRowHTML(pLane)
	{
		let tmpID = pLane.IDBoardLane;
		let tmpColor = pLane.Color || '#8a94a6';
		let tmpView = "_Pict.views['Plansheet-UserSettings']";
		return `
	<div class="cst-lane-row" draggable="true" data-lane-id="${tmpID}"
		ondragstart="${tmpView}.laneDragStart(event)" ondragend="${tmpView}.laneDragEnd(event)"
		ondragover="${tmpView}.laneDragOver(event)" ondrop="${tmpView}.laneDrop(event)">
		<span class="cst-lane-grip" title="Drag to reorder">::</span>
		<input class="cst-lane-color" type="color" value="${_escapeHTML(tmpColor)}" title="Lane color"
			onchange="${tmpView}.recolorLane(${tmpID}, this.value)"/>
		<input class="cst-set-input cst-lane-label" type="text" maxlength="128" value="${_escapeHTML(pLane.Label || '')}"
			onchange="${tmpView}.renameLane(${tmpID}, this.value)"/>
		<label class="cst-lane-terminal" title="The Done-equivalent lane">
			<input type="checkbox" ${pLane.IsTerminal ? 'checked' : ''} onchange="${tmpView}.setLaneTerminal(${tmpID}, this.checked)"/> Done lane
		</label>
		<button class="cst-lane-del" title="Remove lane" onclick="${tmpView}.removeLane(${tmpID})">Remove</button>
	</div>`;
	}

	_laneMsg(pText, pKind) { this._msg('us-lane-msg', pText, pKind); }

	// Find the lane object the settings view holds by id (kept in step with in-place edits).
	_laneByID(pID)
	{
		let tmpID = parseInt(pID, 10) || 0;
		return (this._lanes || []).find((pLane) => pLane.IDBoardLane === tmpID) || null;
	}

	_loadLanes()
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		if (!tmpAPI) { this._lanesLoaded = true; this._lanes = []; return; }
		let tmpSelf = this;
		tmpAPI.listLanes().then((pLanes) =>
		{
			tmpSelf._lanes = Array.isArray(pLanes) ? pLanes : [];
			tmpSelf._lanesLoaded = true;
			if (tmpSelf._activeSection === 'lanes') { tmpSelf._setContent(tmpSelf._section_lanes()); }
		}).catch(() =>
		{
			tmpSelf._lanesLoaded = true;
			if (tmpSelf._activeSection === 'lanes') { tmpSelf._setContent(tmpSelf._section_lanes()); }
		});
	}

	// Re-fetch and re-render the whole section (used after a change that reshapes the list).
	_reloadLanes()
	{
		this._lanesLoaded = false;
		this._loadLanes();
	}

	addLane()
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		if (!tmpAPI) { return; }
		let tmpInput = (typeof document !== 'undefined') ? document.getElementById('us-lane-new') : null;
		let tmpName = tmpInput ? String(tmpInput.value || '').trim() : '';
		if (!tmpName) { this._laneMsg('Type a lane name first.', 'err'); return; }
		this._laneMsg('Adding...', '');
		let tmpSelf = this;
		tmpAPI.createLane(tmpName, '', false)
			.then(() => { if (tmpInput) { tmpInput.value = ''; } tmpSelf._reloadLanes(); tmpSelf._laneMsg('Lane added.', 'ok'); })
			.catch((pErr) => tmpSelf._laneMsg(pErr.message || 'Could not add the lane.', 'err'));
	}

	renameLane(pID, pValue)
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		let tmpLane = this._laneByID(pID);
		let tmpValue = String(pValue == null ? '' : pValue).trim();
		if (!tmpAPI || !tmpLane) { return; }
		if (!tmpValue) { this._laneMsg('A lane needs a name.', 'err'); return; }
		let tmpSelf = this;
		tmpAPI.updateLane(pID, { Label: tmpValue })
			.then((pJSON) => { if (pJSON && pJSON.Lane) { tmpLane.Label = pJSON.Lane.Label; } tmpSelf._laneMsg('Renamed.', 'ok'); })
			.catch((pErr) => tmpSelf._laneMsg(pErr.message || 'Could not rename.', 'err'));
	}

	recolorLane(pID, pValue)
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		let tmpLane = this._laneByID(pID);
		if (!tmpAPI || !tmpLane) { return; }
		let tmpSelf = this;
		tmpAPI.updateLane(pID, { Color: String(pValue || '') })
			.then((pJSON) => { if (pJSON && pJSON.Lane) { tmpLane.Color = pJSON.Lane.Color; } tmpSelf._laneMsg('Color saved.', 'ok'); })
			.catch((pErr) => tmpSelf._laneMsg(pErr.message || 'Could not recolor.', 'err'));
	}

	setLaneTerminal(pID, pChecked)
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		let tmpLane = this._laneByID(pID);
		if (!tmpAPI || !tmpLane) { return; }
		let tmpSelf = this;
		tmpAPI.updateLane(pID, { IsTerminal: !!pChecked })
			.then((pJSON) => { if (pJSON && pJSON.Lane) { tmpLane.IsTerminal = pJSON.Lane.IsTerminal; } tmpSelf._laneMsg('Saved.', 'ok'); })
			.catch((pErr) =>
			{
				// Refused (e.g. clearing the last Done lane): revert the checkbox to the stored state.
				tmpSelf._laneMsg(pErr.message || 'Could not save.', 'err');
				tmpSelf._reloadLanes();
			});
	}

	removeLane(pID)
	{
		let tmpAPI = this.pict.providers.LaneAPI;
		let tmpLane = this._laneByID(pID);
		if (!tmpAPI || !tmpLane) { return; }
		let tmpSelf = this;
		let fDelete = () => tmpAPI.deleteLane(pID)
			.then(() => { tmpSelf._reloadLanes(); })
			.catch((pErr) => tmpSelf._laneMsg(pErr.message || 'Could not remove the lane.', 'err'));
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.confirm !== 'function') { return fDelete(); }
		tmpModal.confirm('Remove the "' + (tmpLane.Label || '') + '" lane?',
			{ title: 'Remove lane?', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
			.then((pOk) => { if (pOk) { fDelete(); } });
	}

	// ── lane drag-to-reorder (in-place, no full re-render, preserves scroll) ──
	// Mirrors the board's card drag: the dragged row is physically moved in the DOM
	// during dragover; on dragend the new DOM order is read and persisted.
	laneDragStart(pEvent)
	{
		let tmpRow = pEvent && pEvent.currentTarget;
		if (tmpRow && tmpRow.classList) { tmpRow.classList.add('is-dragging'); }
		if (pEvent && pEvent.dataTransfer)
		{
			pEvent.dataTransfer.effectAllowed = 'move';
			try { pEvent.dataTransfer.setData('text/plain', tmpRow ? (tmpRow.getAttribute('data-lane-id') || '') : ''); } catch (pErr) { /* ignore */ }
		}
	}

	laneDragOver(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		if (typeof document === 'undefined') { return; }
		let tmpList = document.getElementById('us-lane-list');
		if (!tmpList) { return; }
		let tmpDragging = tmpList.querySelector('.cst-lane-row.is-dragging');
		if (!tmpDragging) { return; }
		let tmpAfter = this._laneAfterElement(tmpList, pEvent.clientY);
		if (tmpAfter == null) { tmpList.appendChild(tmpDragging); }
		else if (tmpAfter !== tmpDragging) { tmpList.insertBefore(tmpDragging, tmpAfter); }
	}

	laneDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
	}

	laneDragEnd(pEvent)
	{
		let tmpRow = pEvent && pEvent.currentTarget;
		if (tmpRow && tmpRow.classList) { tmpRow.classList.remove('is-dragging'); }
		this._persistLaneOrder();
	}

	// The row the cursor sits above (the dragged element goes before it), or null for the end.
	_laneAfterElement(pList, pY)
	{
		let tmpRows = Array.prototype.slice.call(pList.querySelectorAll('.cst-lane-row:not(.is-dragging)'));
		let tmpClosest = { Offset: -Infinity, Element: null };
		for (let i = 0; i < tmpRows.length; i++)
		{
			let tmpBox = tmpRows[i].getBoundingClientRect();
			let tmpOffset = pY - tmpBox.top - (tmpBox.height / 2);
			if (tmpOffset < 0 && tmpOffset > tmpClosest.Offset) { tmpClosest = { Offset: tmpOffset, Element: tmpRows[i] }; }
		}
		return tmpClosest.Element;
	}

	// Read the current DOM row order and, if it changed, persist it (sparse SortOrder server-side).
	_persistLaneOrder()
	{
		if (typeof document === 'undefined') { return; }
		let tmpList = document.getElementById('us-lane-list');
		let tmpAPI = this.pict.providers.LaneAPI;
		if (!tmpList || !tmpAPI) { return; }
		let tmpRows = Array.prototype.slice.call(tmpList.querySelectorAll('.cst-lane-row'));
		let tmpOrder = tmpRows.map((pRow) => parseInt(pRow.getAttribute('data-lane-id'), 10) || 0).filter((pID) => pID);
		let tmpCurrent = (this._lanes || []).map((pLane) => pLane.IDBoardLane);
		if (tmpOrder.length === tmpCurrent.length && tmpOrder.every((pID, pIdx) => pID === tmpCurrent[pIdx])) { return; }
		// Reorder the in-memory list to match the DOM so a later edit maps to the right lane.
		let tmpByID = {};
		(this._lanes || []).forEach((pLane) => { tmpByID[pLane.IDBoardLane] = pLane; });
		this._lanes = tmpOrder.map((pID) => tmpByID[pID]).filter(Boolean);
		let tmpSelf = this;
		this._laneMsg('Saving order...', '');
		tmpAPI.reorderLanes(tmpOrder)
			.then((pJSON) => { if (pJSON && Array.isArray(pJSON.Lanes)) { tmpSelf._lanes = pJSON.Lanes; } tmpSelf._laneMsg('Order saved.', 'ok'); })
			.catch((pErr) => { tmpSelf._laneMsg(pErr.message || 'Could not save the order.', 'err'); tmpSelf._reloadLanes(); });
	}

	// ── Message Templates (org email/SMS) ────────────────────────────────────
	_section_messages()
	{
		if (!this._messagesLoaded) { return this._loadingSection('Message Templates'); }
		let tmpList = this._messages || [];
		if (!tmpList.length)
		{
			return '<div class="cst-set-section"><h2 class="cst-set-h">Message Templates</h2><p class="cst-set-sub">No message templates are available.</p></div>';
		}
		let tmpSel = Math.min(this._messagesSelected || 0, tmpList.length - 1);
		let tmpT = tmpList[tmpSel];
		let tmpOverride = tmpT.Override || null;
		let tmpSubject = tmpOverride ? (tmpOverride.Subject || '') : '';
		let tmpBody = tmpOverride ? (tmpOverride.BodyText || '') : '';
		let tmpEnabled = tmpOverride ? !!tmpOverride.Enabled : true;
		let tmpIsEmail = (tmpT.Channel !== 'sms');
		let tmpSelf = "_Pict.views['Plansheet-UserSettings']";
		let tmpOptions = tmpList.map((pT, pI) => `<option value="${pI}"${pI === tmpSel ? ' selected' : ''}>${_escapeHTML(pT.Label)}${pT.Channel === 'sms' ? ' (text)' : ''}</option>`).join('');
		let tmpVars = (tmpT.Variables || []).map((pV) => `<button type="button" class="msg-var" title="${_escapeHTML(pV.Label)}" onclick="${tmpSelf}.insertVar('${pV.Name}')">{{${pV.Name}}}</button>`).join(' ');
		let tmpSubjectField = tmpIsEmail ? `
	<label class="cst-set-field"><span>Subject</span>
		<input id="msg-subject" class="cst-set-input" onfocus="${tmpSelf}._msgFocus='msg-subject'" oninput="${tmpSelf}.previewMessage()" value="${_escapeHTML(tmpSubject)}" placeholder="${_escapeHTML(tmpT.Default.Subject || '')}"/>
	</label>` : '';
		return `
<div class="cst-set-section">
	<h2 class="cst-set-h">Message Templates</h2>
	<p class="cst-set-sub">The email and text messages your organization sends. Each works from a built-in default; edit one to override it. With no provider configured, sends go to a dev log instead of a real inbox.</p>
	<label class="cst-set-field"><span>Message</span>
		<select id="msg-select" class="cst-set-input" onchange="${tmpSelf}.selectMessage(this.value)">${tmpOptions}</select>
	</label>
	<div class="msg-vars">Variables: ${tmpVars}</div>
	${tmpSubjectField}
	<label class="cst-set-field"><span>${tmpIsEmail ? 'Body' : 'Message'}</span>
		<textarea id="msg-body" class="cst-set-input msg-body" rows="6" onfocus="${tmpSelf}._msgFocus='msg-body'" oninput="${tmpSelf}.previewMessage()" placeholder="${_escapeHTML(tmpT.Default.BodyText || '')}">${_escapeHTML(tmpBody)}</textarea>
	</label>
	<label class="cst-set-check"><input type="checkbox" id="msg-enabled"${tmpEnabled ? ' checked' : ''}/> <span>Send this message (uncheck to turn it off for everyone in your org)</span></label>
	<div class="msg-preview" id="msg-preview"></div>
	<div class="cst-set-actions">
		<button class="cst-set-btn" onclick="${tmpSelf}.saveMessage()">Save</button>
		<button class="cst-set-btn cst-set-btn-secondary" onclick="${tmpSelf}.resetMessage()">Use default</button>
		<span id="msg-msg" class="cst-set-msg"></span>
	</div>
	<div class="msg-test">
		<label class="cst-set-field"><span>Send a test to</span>
			<input id="msg-test-to" class="cst-set-input" placeholder="${tmpIsEmail ? 'you@example.com' : '+1 555 0100'}"/>
		</label>
		<div class="cst-set-actions"><button class="cst-set-btn cst-set-btn-secondary" onclick="${tmpSelf}.sendTestMessage()">Send test</button><span id="msg-test-msg" class="cst-set-msg"></span></div>
	</div>
</div>`;
	}

	_loadMessages()
	{
		let tmpAPI = this.pict.providers.MessageAPI;
		if (!tmpAPI) { return; }
		let fRender = () => { if (this._activeSection === 'messages') { this._setContent(this._section_messages()); this.previewMessage(); } };
		tmpAPI.getTemplates().then((pRows) =>
		{
			this._messages = Array.isArray(pRows) ? pRows : [];
			// A template named in the URL wins over the remembered position.
			if (this._pendingMessageKey)
			{
				let tmpAt = this._messages.findIndex((pRow) => String(pRow.Key) === this._pendingMessageKey);
				if (tmpAt >= 0) { this._messagesSelected = tmpAt; }
				this._pendingMessageKey = '';
			}
			this._messagesLoaded = true;
			if (this._messagesSelected == null) { this._messagesSelected = 0; }
			fRender();
		}).catch(() => { this._messages = []; this._messagesLoaded = true; fRender(); });
	}

	_currentMessage()
	{
		let tmpList = this._messages || [];
		return tmpList[Math.min(this._messagesSelected || 0, tmpList.length - 1)] || null;
	}

	selectMessage(pIndex)
	{
		let tmpIndex = parseInt(pIndex, 10) || 0;
		let tmpTemplate = (this._messages || [])[tmpIndex];
		let tmpApp = this.pict.PictApplication;
		if (tmpTemplate && tmpTemplate.Key && tmpApp && typeof tmpApp.showSettingsSub === 'function'
			&& tmpApp.showSettingsSub('messages', encodeURIComponent(tmpTemplate.Key))) { return; }
		this._applySelectMessage(tmpIndex);
	}

	_applySelectMessage(pIndex)
	{
		this._messagesSelected = parseInt(pIndex, 10) || 0;
		this._setContent(this._section_messages());
		this.previewMessage();
	}

	insertVar(pName)
	{
		if (typeof document === 'undefined') { return; }
		let tmpFieldId = (this._msgFocus === 'msg-subject') ? 'msg-subject' : 'msg-body';
		let tmpEl = document.getElementById(tmpFieldId);
		if (!tmpEl) { return; }
		let tmpToken = '{{' + pName + '}}';
		let tmpStart = (typeof tmpEl.selectionStart === 'number') ? tmpEl.selectionStart : tmpEl.value.length;
		let tmpEnd = (typeof tmpEl.selectionEnd === 'number') ? tmpEl.selectionEnd : tmpEl.value.length;
		tmpEl.value = tmpEl.value.slice(0, tmpStart) + tmpToken + tmpEl.value.slice(tmpEnd);
		let tmpCaret = tmpStart + tmpToken.length;
		try { tmpEl.focus(); tmpEl.setSelectionRange(tmpCaret, tmpCaret); } catch (pErr) { /* ignore */ }
		this.previewMessage();
	}

	_readMessageInputs()
	{
		let fVal = (pId) => { let tmpEl = document.getElementById(pId); return tmpEl ? tmpEl.value : ''; };
		let fChecked = (pId) => { let tmpEl = document.getElementById(pId); return tmpEl ? !!tmpEl.checked : true; };
		return { Subject: fVal('msg-subject'), BodyText: fVal('msg-body'), Enabled: fChecked('msg-enabled') };
	}

	previewMessage()
	{
		if (typeof document === 'undefined') { return; }
		let tmpMessage = this._currentMessage();
		let tmpAPI = this.pict.providers.MessageAPI;
		if (!tmpMessage || !tmpAPI) { return; }
		let tmpInputs = this._readMessageInputs();
		if (this._msgPreviewTimer) { clearTimeout(this._msgPreviewTimer); }
		this._msgPreviewTimer = setTimeout(() =>
		{
			tmpAPI.previewTemplate(tmpMessage.Key, { Channel: tmpMessage.Channel, Subject: tmpInputs.Subject, BodyText: tmpInputs.BodyText })
				.then((pBody) =>
				{
					let tmpEl = document.getElementById('msg-preview');
					if (!tmpEl) { return; }
					let tmpSubjectLine = (tmpMessage.Channel !== 'sms') ? `<div class="msg-preview-subject">${_escapeHTML(pBody.Subject || '')}</div>` : '';
					let tmpBodyHTML = (tmpMessage.Channel !== 'sms' && pBody.BodyHTML) ? pBody.BodyHTML : _escapeHTML(pBody.BodyText || '').replace(/\n/g, '<br>');
					tmpEl.innerHTML = `<div class="msg-preview-label">Preview (sample data)</div>${tmpSubjectLine}<div class="msg-preview-body">${tmpBodyHTML}</div>`;
				}).catch(() => {});
		}, 250);
	}

	saveMessage()
	{
		let tmpMessage = this._currentMessage();
		let tmpAPI = this.pict.providers.MessageAPI;
		if (!tmpMessage || !tmpAPI) { return; }
		let tmpInputs = this._readMessageInputs();
		this._msg('msg-msg', 'Saving...', '');
		tmpAPI.saveTemplate(tmpMessage.Key, { Channel: tmpMessage.Channel, Subject: tmpInputs.Subject, BodyText: tmpInputs.BodyText, Enabled: tmpInputs.Enabled })
			.then(() => tmpAPI.getTemplates())
			.then((pRows) => { this._messages = Array.isArray(pRows) ? pRows : this._messages; this._setContent(this._section_messages()); this._msg('msg-msg', 'Saved.', 'ok'); this.previewMessage(); })
			.catch((pErr) => this._msg('msg-msg', pErr.message || 'Could not save.', 'err'));
	}

	resetMessage()
	{
		let tmpMessage = this._currentMessage();
		let tmpAPI = this.pict.providers.MessageAPI;
		if (!tmpMessage || !tmpAPI) { return; }
		this._msg('msg-msg', 'Restoring the default...', '');
		tmpAPI.saveTemplate(tmpMessage.Key, { Channel: tmpMessage.Channel, Subject: '', BodyText: '', BodyHTML: '', Enabled: true })
			.then(() => tmpAPI.getTemplates())
			.then((pRows) => { this._messages = Array.isArray(pRows) ? pRows : this._messages; this._setContent(this._section_messages()); this._msg('msg-msg', 'Using the default.', 'ok'); this.previewMessage(); })
			.catch((pErr) => this._msg('msg-msg', pErr.message || 'Could not reset.', 'err'));
	}

	sendTestMessage()
	{
		let tmpMessage = this._currentMessage();
		let tmpAPI = this.pict.providers.MessageAPI;
		if (!tmpMessage || !tmpAPI) { return; }
		let tmpEl = document.getElementById('msg-test-to');
		let tmpTo = tmpEl ? tmpEl.value.trim() : '';
		if (!tmpTo) { this._msg('msg-test-msg', 'Enter a destination first.', 'err'); return; }
		this._msg('msg-test-msg', 'Sending...', '');
		tmpAPI.testSend(tmpMessage.Key, tmpMessage.Channel, tmpTo)
			.then((pBody) => this._msg('msg-test-msg', pBody.Skipped ? 'This message is turned off.' : 'Sent (it goes to the dev log unless a provider is configured).', 'ok'))
			.catch((pErr) => this._msg('msg-test-msg', pErr.message || 'Could not send.', 'err'));
	}

	// ── section switching ────────────────────────────────────────────────────
	showSection(pSection)
	{
		// Drafting Kit carries an optional kit id in a sub-path: 'draftingkit/<id>' opens that kit's editor.
		let tmpRaw = String(pSection || '');
		let tmpSlash = tmpRaw.indexOf('/');
		let tmpBase = (tmpSlash >= 0) ? tmpRaw.slice(0, tmpSlash) : tmpRaw;
		let tmpSection = (tmpBase && _KNOWN_SECTIONS[tmpBase]) ? tmpBase : 'profile';
		let tmpSub = (tmpSlash >= 0) ? tmpRaw.slice(tmpSlash + 1) : '';
		this._editingKitID = (tmpSection === 'draftingkit' && tmpSub) ? (parseInt(tmpSub, 10) || 0) : 0;
		// Stickers and Message Templates carry a sub-path too, so the pane you are actually working in
		// can be linked and reloaded into rather than always reopening on the placeholder / first row.
		if (tmpSection === 'stickers' && tmpSub)
		{
			this.pict.AppData.StickerSettings = this.pict.AppData.StickerSettings || {};
			this.pict.AppData.StickerSettings.ActiveSetId = parseInt(tmpSub, 10) || 0;
		}
		// By KEY, not by position: the templates are a server-defined list, and a link that silently
		// opened a DIFFERENT template after a reorder would be worse than no link at all.
		this._pendingMessageKey = (tmpSection === 'messages' && tmpSub) ? decodeURIComponent(tmpSub) : '';
		this._activeSection = tmpSection;
		let tmpNav = this.pict.providers['Pict-Navigation'];
		if (tmpNav)
		{
			tmpNav.setActiveByRoute('#/settings/' + tmpSection);
			let tmpDocked = this.pict.views['Navigation-Sidebar-Docked'];
			if (tmpDocked && typeof tmpDocked.syncActive === 'function') { tmpDocked.syncActive(); }
		}
		let tmpBuilder = this['_section_' + tmpSection];
		let tmpHTML = (typeof tmpBuilder === 'function') ? tmpBuilder.call(this) : '<div class="cst-set-section">Not found.</div>';
		if (typeof document !== 'undefined')
		{
			let tmpEl = document.getElementById('Settings-Content');
			if (tmpEl) { tmpEl.innerHTML = tmpHTML; }
		}
		if (tmpSection === 'tokens') { this._loadTokensOnce(); this._mountAgentPicker(); }
		if (tmpSection === 'about') { this._mountAboutEditor(); }
		if (tmpSection === 'appearance') { this._mountAppearance(); }
		if (tmpSection === 'team') { this._loadTeam(); }
		if (tmpSection === 'roles') { this._loadRoles(); }
		if (tmpSection === 'org') { this._loadOrg(); }
		if (tmpSection === 'topbar') { this._loadTopBar(); }
		if (tmpSection === 'docsync') { this._loadDocSync(); }
		if (tmpSection === 'draftingkit') { this._loadDraftingKit(); }
		if (tmpSection === 'messages') { this._loadMessages(); }
		if (tmpSection === 'stickers') { this._loadStickers(); }
		if (tmpSection === 'lanes') { this._loadLanes(); }
		if (tmpSection === 'account') { this._loadTwoFactor(); }
		if (tmpSection === 'notifications') { this._loadNotifications(); }
		if (tmpSection === 'connections') { this._loadConnectClaudeGuide(); }
		if (tmpSection === 'plansheets') { this._plansheetsLoaded = false; this._loadPlansheets(); }
	}

	// ── handlers: profile / account ──────────────────────────────────────────
	saveProfile()
	{
		let tmpGet = (pId) => (typeof document !== 'undefined') ? (document.getElementById(pId) || {}).value : '';
		let tmpPatch = { DisplayName: tmpGet('us-DisplayName'), Title: tmpGet('us-Title'), Color: tmpGet('us-Color') };
		this._msg('us-profile-msg', 'Saving...', '');
		this.pict.providers.UserAPI.updateMe(tmpPatch).then(() =>
		{
			this._msg('us-profile-msg', 'Saved.', 'ok');
			this._syncAuthUser();
		}).catch((pErr) => this._msg('us-profile-msg', pErr.message || 'Could not save.', 'err'));
	}

	saveEmail()
	{
		let tmpEmail = (typeof document !== 'undefined') ? (document.getElementById('us-Email') || {}).value : '';
		this._msg('us-email-msg', 'Saving...', '');
		this.pict.providers.UserAPI.changeEmail(tmpEmail).then(() =>
		{
			this._msg('us-email-msg', 'Email updated.', 'ok');
			this._syncAuthUser();
		}).catch((pErr) => this._msg('us-email-msg', pErr.message || 'Could not update.', 'err'));
	}

	savePassword()
	{
		let tmpGet = (pId) => (typeof document !== 'undefined') ? (document.getElementById(pId) || {}).value : '';
		this._msg('us-pw-msg', 'Saving...', '');
		this.pict.providers.UserAPI.changePassword(tmpGet('us-PwCurrent'), tmpGet('us-PwNew')).then(() =>
		{
			this._msg('us-pw-msg', 'Password changed.', 'ok');
			if (typeof document !== 'undefined')
			{
				['us-PwCurrent', 'us-PwNew'].forEach((pId) => { let e = document.getElementById(pId); if (e) { e.value = ''; } });
			}
		}).catch((pErr) => this._msg('us-pw-msg', pErr.message || 'Could not change.', 'err'));
	}

	_msg(pId, pText, pKind)
	{
		if (typeof document === 'undefined') { return; }
		let tmpEl = document.getElementById(pId);
		if (!tmpEl) { return; }
		tmpEl.textContent = pText;
		tmpEl.className = 'cst-set-msg' + (pKind ? ' cst-set-msg-' + pKind : '');
	}

	// Keep the topbar name + AppData.Auth.User in step after a self edit.
	_syncAuthUser()
	{
		let tmpP = this._profile();
		if (this.pict.AppData.Auth && this.pict.AppData.Auth.User && tmpP)
		{
			this.pict.AppData.Auth.User.DisplayName = tmpP.DisplayName || this.pict.AppData.Auth.User.DisplayName;
			this.pict.AppData.Auth.User.Email = tmpP.Email || this.pict.AppData.Auth.User.Email;
			this.pict.AppData.Auth.User.Title = tmpP.Title;
			this.pict.AppData.Auth.User.Color = tmpP.Color;
		}
		let tmpTopBar = this.pict.views['Plansheet-TopBar-User'];
		if (tmpTopBar && typeof tmpTopBar.render === 'function') { tmpTopBar.render(); }
	}

	// ── handlers: profile photo ──────────────────────────────────────────────
	pickPhoto()
	{
		if (typeof document === 'undefined') { return; }
		let tmpInput = document.getElementById('us-photo-input');
		if (tmpInput) { tmpInput.click(); }
	}

	onPhotoPicked(pInput)
	{
		let tmpFile = pInput && pInput.files && pInput.files[0];
		if (!tmpFile) { return; }
		if (tmpFile.size > 8 * 1024 * 1024)
		{
			this._msg('us-photo-msg', 'That image is larger than 8 MB.', 'err');
			pInput.value = '';
			return;
		}
		let tmpProvider = this.pict.providers.PhotoAPI;
		if (!tmpProvider) { return; }
		this._msg('us-photo-msg', 'Uploading...', '');
		tmpProvider.uploadPhoto(tmpFile)
			.then((pResult) =>
			{
				this._applyPhotoHash(pResult.PhotoHash);
				this._msg('us-photo-msg', 'Photo updated.', 'ok');
			})
			.catch((pErr) => this._msg('us-photo-msg', pErr.message || 'Upload failed.', 'err'))
			.then(() => { if (pInput) { pInput.value = ''; } });
	}

	removePhoto()
	{
		let tmpProvider = this.pict.providers.PhotoAPI;
		if (!tmpProvider) { return; }
		this._msg('us-photo-msg', 'Removing...', '');
		tmpProvider.deletePhoto()
			.then(() =>
			{
				this._applyPhotoHash('');
				this._msg('us-photo-msg', 'Photo removed.', 'ok');
			})
			.catch((pErr) => this._msg('us-photo-msg', pErr.message || 'Could not remove.', 'err'));
	}

	// Update the cached profile + session, then repaint just the photo block and
	// the topbar avatar (leaving the other profile fields untouched).
	_applyPhotoHash(pHash)
	{
		let tmpP = this._profile();
		if (tmpP) { tmpP.PhotoHash = pHash || ''; }
		if (this.pict.AppData.Auth && this.pict.AppData.Auth.User) { this.pict.AppData.Auth.User.PhotoHash = pHash || ''; }
		if (typeof document !== 'undefined')
		{
			let tmpBlock = document.getElementById('us-photo-block');
			if (tmpBlock) { tmpBlock.innerHTML = this._photoBlockHTML(); }
		}
		let tmpTopBar = this.pict.views['Plansheet-TopBar-User'];
		if (tmpTopBar && typeof tmpTopBar.paintAvatar === 'function') { tmpTopBar.paintAvatar(); }
	}

	// ── tokens (relocated from the gear panel) ───────────────────────────────
	_refreshTokens()
	{
		let tmpStore = this.pict.AppData.APITokens || { List: [] };
		let tmpView = this.pict.AppData.TokenSettings;
		tmpView.Rows = (tmpStore.List || []).map((pRow) =>
		{
			let tmpCreated = _formatDate(pRow.CreateDate);
			return (
			{
				IDUserToken: pRow.IDUserToken,
				Label: _escapeHTML(pRow.Label || ('Token #' + pRow.IDUserToken)),
				CreatedLabel: tmpCreated ? ('Created ' + tmpCreated) : 'Created',
				LastUsedLabel: pRow.LastUsedAt ? (' · Last used ' + _formatDate(pRow.LastUsedAt)) : ' · Never used'
			});
		});
	}

	_loadTokensOnce()
	{
		if (this._tokensRequested) { return; }
		this._tokensRequested = true;
		let tmpProvider = this.pict.providers.TokenAPI;
		if (!tmpProvider) { return; }
		tmpProvider.listTokens().then(() => { if (this._activeSection === 'tokens') { this.showSection('tokens'); } });
	}

	generateToken()
	{
		let tmpProvider = this.pict.providers.TokenAPI;
		if (!tmpProvider) { return; }
		let tmpInput = (typeof document !== 'undefined') ? document.getElementById('us-TokenLabel') : null;
		let tmpLabel = (tmpInput && tmpInput.value) ? tmpInput.value.trim() : '';
		tmpProvider.createToken(tmpLabel).then((pToken) =>
		{
			if (tmpInput) { tmpInput.value = ''; }
			this.showSection('tokens');
			this._showTokenOnce(pToken);
		}).catch((pErr) =>
		{
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal) { tmpModal.show({ title: 'Could not create token', content: '<p>' + _escapeHTML(pErr && pErr.message ? pErr.message : 'Unknown error.') + '</p>', closeable: true, buttons: [{ Hash: 'ok', Label: 'OK', Style: 'primary' }] }); }
		});
	}

	// Mount the account picker (pict-section-picker) for the admin "mint a token for another user" control.
	// Options are the tenant's team members; the chosen IDUser is what mintAgentToken sends. Loads the team
	// once, then mounts. Mirrors the blueprint pickers in PictView-EntityDetail (this.pict.PictApplication.mountPicker).
	_mountAgentPicker()
	{
		let tmpIsAdmin = !!(this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.IsPlatformAdmin);
		if (!tmpIsAdmin || typeof document === 'undefined' || !document.getElementById('us-agent-picker-host')) { return; }
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp.mountPicker !== 'function') { return; }
		let tmpMembers = (this.pict.AppData.Team && this.pict.AppData.Team.Members) || [];
		if (!tmpMembers.length && this.pict.providers.TeamAPI && !this._agentPickerLoading)
		{
			this._agentPickerLoading = true;
			this.pict.providers.TeamAPI.listMembers().then(() =>
			{
				this._agentPickerLoading = false;
				if (this._activeSection === 'tokens') { this._mountAgentPicker(); }
			});
			return;
		}
		let tmpSelf = this;
		let tmpOptions = tmpMembers.map((pM) => (
		{
			Value: String(pM.IDUser),
			Text: (pM.DisplayName || pM.Email || ('User #' + pM.IDUser)) + (pM.IsAgent ? ' (agent)' : '') + (pM.Email ? ' -- ' + pM.Email : '')
		}));
		tmpApp.mountPicker('#us-agent-picker-host', 'Settings-AgentTokenPicker', tmpOptions,
			function (pVal) { tmpSelf._agentTokenIDUser = parseInt(pVal, 10) || 0; },
			{ Placeholder: 'Select an account', Value: this._agentTokenIDUser ? String(this._agentTokenIDUser) : '' });
	}

	mintAgentToken()
	{
		let tmpProvider = this.pict.providers.TokenAPI;
		if (!tmpProvider) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpIDUser = this._agentTokenIDUser || 0;
		if (!tmpIDUser)
		{
			if (tmpModal) { tmpModal.show({ title: 'Select an account', content: '<p>Choose the account to mint a token for.</p>', closeable: true, buttons: [{ Hash: 'ok', Label: 'OK', Style: 'primary' }] }); }
			return;
		}
		let tmpLabelEl = (typeof document !== 'undefined') ? document.getElementById('us-AgentTokenLabel') : null;
		let tmpLabel = (tmpLabelEl && tmpLabelEl.value) ? tmpLabelEl.value.trim() : '';
		tmpProvider.mintTokenForUser(tmpIDUser, tmpLabel).then((pToken) =>
		{
			this._showTokenOnce(pToken);
		}).catch((pErr) =>
		{
			if (tmpModal) { tmpModal.show({ title: 'Could not mint token', content: '<p>' + _escapeHTML(pErr && pErr.message ? pErr.message : 'Unknown error.') + '</p>', closeable: true, buttons: [{ Hash: 'ok', Label: 'OK', Style: 'primary' }] }); }
		});
	}

	_showTokenOnce(pToken)
	{
		this._lastRevealedToken = pToken || '';
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		let tmpContent =
			'<div class="cst-token-reveal">' +
				'<p class="cst-token-reveal-warn">Copy this token now. For security it is shown only once and cannot be retrieved again.</p>' +
				'<div class="cst-token-value-row"><code id="Plansheet-Token-Reveal-Value" class="cst-token-value">' + (pToken || '') + '</code>' +
				'<button class="cst-token-copy-btn" onclick="_Pict.views[\'Plansheet-UserSettings\'].copyRevealedToken()">Copy</button></div>' +
				'<p class="cst-token-reveal-hint">Send it as <code>Authorization: Bearer &lt;token&gt;</code>, or set it as <code>token</code> in the plansheet CLI config.</p>' +
			'</div>';
		tmpModal.show({ title: 'New API token', content: tmpContent, closeable: true, buttons: [{ Hash: 'done', Label: 'Done', Style: 'primary' }] });
	}

	copyRevealedToken()
	{
		let tmpValue = this._lastRevealedToken || '';
		if (typeof document !== 'undefined')
		{
			let tmpEl = document.getElementById('Plansheet-Token-Reveal-Value');
			if (tmpEl && tmpEl.textContent) { tmpValue = tmpEl.textContent; }
		}
		if (!tmpValue || typeof navigator === 'undefined' || !navigator.clipboard) { return; }
		navigator.clipboard.writeText(tmpValue).then(() =>
		{
			if (typeof document !== 'undefined') { let b = document.querySelector('.cst-token-copy-btn'); if (b) { b.textContent = 'Copied'; } }
		}).catch(() => {});
	}

	revokeToken(pIDUserToken)
	{
		let tmpProvider = this.pict.providers.TokenAPI;
		if (!tmpProvider) { return; }
		let tmpStore = this.pict.AppData.APITokens || { List: [] };
		let tmpRow = (tmpStore.List || []).find((pCandidate) => pCandidate.IDUserToken === pIDUserToken);
		let tmpLabel = (tmpRow && tmpRow.Label) || ('#' + pIDUserToken);
		let fRevoke = () => tmpProvider.revokeToken(pIDUserToken).then(() => this.showSection('tokens'));
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return fRevoke(); }
		tmpModal.confirm('Revoke token "' + tmpLabel + '"? Any CLI or automation using it stops working immediately.',
			{ title: 'Revoke token?', confirmLabel: 'Revoke', cancelLabel: 'Cancel', dangerous: true })
			.then((pOk) => { if (pOk) { fRevoke(); } });
	}

	// ── lifecycle ────────────────────────────────────────────────────────────
	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// The settings nav lives in the shared left sidebar now (the docked Settings group, SiteMap.js),
		// not an in-page rail -- so there is no #Settings-Nav to render here. The route hands us the
		// section to open via _pendingSection; fall back to the last active section or Profile.
		let tmpSection = this._pendingSection || this._activeSection || 'profile';
		this._pendingSection = null;
		// Show immediately from cache, then refresh from the server.
		this.showSection(tmpSection);
		if (this.pict.providers.UserAPI)
		{
			this.pict.providers.UserAPI.getMe().then(() => this.showSection(this._activeSection));
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewUserSettings;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-UserSettings',
	DefaultRenderable: 'UserSettings-Display',
	CSS: /*css*/`
		.cst-set-page { display: flex; gap: 0; height: 100%; min-height: 0; }
		.cst-set-rail { flex: 0 0 240px; min-width: 0; border-right: 1px solid var(--theme-color-border-default, #e3e6ec); height: 100%; overflow: auto; }
		.cst-set-content { flex: 1 1 auto; min-width: 0; overflow: auto; }
		.cst-set-section { max-width: 560px; }
			/* Two builders can stack sections on one screen (Sign-in + Two-step verification). Give adjacent
			   sections a clear break -- generous space and a hairline rule -- so they read as separate functions. */
			.cst-set-section + .cst-set-section { margin-top: 40px; padding-top: 34px; border-top: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.cst-set-h { margin: 0 0 2px; font-size: 1.3em; color: var(--theme-color-text-primary, #1d2230); }
		.cst-set-sub { margin: 0 0 18px; font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-input { padding: 8px 11px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); font-size: 0.95em; }
		.cst-set-color-row { display: flex; align-items: center; gap: 10px; }
		.cst-set-color { width: 44px; height: 32px; padding: 0; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: none; cursor: pointer; }
		.cst-set-color-hint { font-size: 0.92em; }
		.cst-set-photo { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
		.cst-set-photo-preview { flex: 0 0 auto; }
		.cst-set-photo-actions { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
		.cst-set-photo-btns { display: flex; gap: 8px; flex-wrap: wrap; }
		.cst-set-photo-hint { font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-photo-remove { padding: 8px 14px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; font-size: 0.9em; }
		.cst-set-photo-remove:hover { background: var(--theme-color-status-error-background, #fdecec); border-color: var(--theme-color-status-error, #c0353a); color: var(--theme-color-status-error, #c0353a); }
		.cst-team-row { border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; padding: 14px 16px; margin-bottom: 12px; display: flex; flex-direction: column; gap: 10px; }
		.cst-team-head { display: flex; align-items: center; gap: 10px; }
		.cst-team-id { min-width: 0; }
		.cst-team-name { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.cst-team-email { font-size: 0.8em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-team-agent-badge { display: inline-block; font-size: 0.68em; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; padding: 1px 7px; margin-left: 7px; border-radius: 10px; color: var(--theme-color-brand-primary, #3b6fd4); border: 1px solid var(--theme-color-brand-primary, #3b6fd4); background: var(--theme-color-background-panel, #fff); vertical-align: middle; }
		.cst-team-removed-badge { display: inline-block; font-size: 0.68em; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; padding: 1px 7px; margin-left: 7px; border-radius: 10px; color: var(--theme-color-text-muted, #8a8f99); border: 1px solid var(--theme-color-border-default, #e3e6ec); background: var(--theme-color-background-secondary, #f7f8fb); vertical-align: middle; }
		.cst-team-row-removed { flex-direction: row; align-items: center; justify-content: space-between; opacity: 0.72; }
		.cst-team-fields { display: flex; gap: 14px; flex-wrap: wrap; }
		.cst-team-fields .cst-set-field { margin-bottom: 0; }
		.cst-team-roles { display: flex; flex-wrap: wrap; gap: 8px 16px; }
		.cst-team-role { display: inline-flex; align-items: center; gap: 5px; font-size: 0.85em; color: var(--theme-color-text-primary, #1d2230); }
		.cst-team-role-custom { font-size: 0.68em; color: var(--theme-color-brand-primary, #3b6fd4); text-transform: uppercase; letter-spacing: 0.03em; }
		.cst-role-group { margin: 22px 0 10px; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); text-transform: uppercase; letter-spacing: 0.04em; }
		.cst-role-card { border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; padding: 14px 16px; margin-bottom: 12px; }
		.cst-role-ents { display: flex; flex-direction: column; gap: 8px; margin: 10px 0; }
		.cst-role-ent { display: flex; align-items: baseline; gap: 8px; font-size: 0.85em; }
		.cst-role-ent-label { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.cst-role-ent-desc { color: var(--theme-color-text-secondary, #5b6376); font-size: 0.92em; }
		.cst-role-system { background: var(--theme-color-background-secondary, #f3f5f9); }
		.cst-role-system-name { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.cst-role-system-tag { font-size: 0.68em; color: var(--theme-color-text-secondary, #5b6376); text-transform: uppercase; letter-spacing: 0.03em; }
		.cst-role-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
		.cst-role-badge { font-size: 0.75em; padding: 2px 8px; border-radius: 10px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border: 1px solid var(--theme-color-border-default, #d8dde6); }
		/* The top-bar composer's canvas. Tall enough to arrange in, while the LOCKED crop frame inside it
		   (setCropFrame) is what actually ships -- the host is a workspace, not a preview of the bar. */
		.cst-topbar-board { height: 460px; min-height: 320px; margin: 14px 0; border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; overflow: hidden; }
		.cst-set-actions { display: flex; align-items: center; gap: 12px; margin-top: 6px; }
		.cst-set-btn { padding: 8px 16px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.9em; }
		.cst-set-btn:hover { filter: brightness(1.05); }
		.cst-set-btn-secondary { background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); border-color: var(--theme-color-border-default, #d8dde6); }
		.cst-set-msg { font-size: 0.85em; }
		.cst-set-msg-ok { color: var(--theme-color-status-success, #2e7a3a); }
		.cst-set-msg-err { color: var(--theme-color-status-error, #c0353a); }
		.cst-set-rule { border: none; border-top: 1px solid var(--theme-color-border-default, #e3e6ec); margin: 22px 0; }
		.cst-set-placeholder { padding: 16px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; color: var(--theme-color-text-secondary, #5b6376); font-size: 0.88em; }
		.cst-set-note-warn { margin: 0 0 14px; padding: 10px 12px; border-radius: 8px; font-size: 0.85em; background: var(--theme-color-status-warning-background, #fff6e5); color: var(--theme-color-status-warning, #8a5a00); border: 1px solid var(--theme-color-status-warning-border, #f0d9a8); }
		.cst-set-pill-ok { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 0.78em; font-weight: 600; background: var(--theme-color-status-success-background, #e7f6ec); color: var(--theme-color-status-success, #2e7a3a); }
		.cst-set-codes { list-style: none; padding: 12px 14px; margin: 10px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 6px 18px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.95em; letter-spacing: 0.04em; background: var(--theme-color-background-secondary, #f7f8fb); border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 8px; }
		.cst-notif-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 0; border-bottom: 1px solid var(--theme-color-border-default, #eef0f4); }
		.cst-notif-label { font-size: 0.9em; color: var(--theme-color-text-primary, #2c3140); }
		.cst-notif-toggles { display: flex; gap: 16px; flex: none; }
		.cst-notif-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; }
		.cst-notif-sms { border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; padding: 14px 16px; margin: 14px 0 20px; background: var(--theme-color-background-secondary, #f7f8fb); }
		.cst-notif-sms .cst-set-sh { margin-top: 0; }
		.cst-notif-consent { font-size: 0.78em; color: var(--theme-color-text-muted, #8a8f99); line-height: 1.5; margin: 10px 0 0; }
		.cst-notif-consent-check { display: flex; gap: 8px; align-items: flex-start; font-size: 0.8em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.4; margin: 10px 0 2px; cursor: pointer; }
		.cst-notif-consent-check input { margin-top: 2px; flex: none; }
		.cst-set-field-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 6px; }
		/* Appearance: pict-section-theme stamps unstyled .pict-theme-mount rows; space + label them. */
		#Settings-Appearance-Theme .pict-theme-mount { display: flex; flex-direction: column; gap: 18px; max-width: 420px; }
		#Settings-Appearance-Theme .pict-theme-mount-row { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
		#Settings-Appearance-Theme .pict-theme-mount-row::before { font-size: 0.85em; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		#Settings-Appearance-Theme .pict-theme-mount-row-theme-picker::before { content: "Theme"; }
		#Settings-Appearance-Theme .pict-theme-mount-row-theme-modetoggle::before { content: "Color mode"; }
		#Settings-Appearance-Theme .pict-theme-mount-row-theme-scaleselect::before { content: "Text size"; }
		.cst-set-appearance-footer { margin-top: 26px; padding-top: 14px; border-top: 1px solid var(--theme-color-border-default, #e3e6ec); font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-navtoggle { display: flex; align-items: flex-start; gap: 10px; margin-top: 22px; cursor: pointer; }
		.cst-set-navtoggle input { margin-top: 3px; flex: 0 0 auto; }
		.cst-set-navtoggle-txt { display: flex; flex-direction: column; }
		.cst-set-navtoggle .cst-set-sub { margin: 4px 0 0; }
		.cst-set-tabs { margin-top: 26px; padding-top: 20px; border-top: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.cst-set-tabs .cst-set-h3 { margin-top: 0; }
		.cst-set-tabs-list { max-width: 420px; margin-top: 10px; border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 8px; overflow: hidden; }
		.cst-set-tabrow { display: flex; align-items: center; gap: 12px; padding: 9px 12px; }
		.cst-set-tabrow + .cst-set-tabrow { border-top: 1px solid var(--theme-color-border-light, #eef0f4); }
		.cst-set-tabrow-show { flex: 1 1 auto; display: flex; align-items: center; gap: 10px; cursor: pointer; min-width: 0; }
		.cst-set-tabrow-show input { flex: 0 0 auto; margin: 0; }
		.cst-set-tabrow-ico { flex: 0 0 auto; display: inline-flex; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-tabrow-ico .ps-nb-tab-ico { width: 18px; height: 18px; }
		.cst-set-tabrow-lbl { font-size: 0.9em; color: var(--theme-color-text-primary, #1d2230); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-set-tabrow-def { flex: 0 0 auto; display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-set-tabrow-def input { margin: 0; }
		.cst-set-tabrow-def input:disabled { cursor: default; }
		.cst-set-tabrow-def:has(input:disabled) { opacity: 0.4; cursor: default; }
		.cst-set-section-wide { max-width: none; }
		.cst-set-h3 { font-size: 0.92em; margin: 16px 0 8px; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-sticker-grid { display: flex; flex-wrap: wrap; gap: 10px; }
		.cst-sticker-tile { position: relative; width: 84px; height: 84px; box-sizing: border-box; border: 1px solid var(--theme-color-border-default, #e2e6ec); border-radius: 8px; background: var(--theme-color-background-panel, #fff); display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 6px; }
		.cst-sticker-tile img { max-width: 52px; max-height: 50px; object-fit: contain; }
		.cst-sticker-name { font-size: 10px; color: var(--theme-color-text-secondary, #8a93a5); margin-top: 4px; max-width: 74px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-setchips { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0 14px; }
		.cst-setchip { padding: 6px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 16px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); cursor: pointer; font-size: 13px; }
		.cst-setchip.is-active { background: var(--theme-color-background-selected, rgba(55,138,221,0.12)); border-color: var(--theme-color-brand, #378add); font-weight: 600; }
		.cst-setchip-n { color: var(--theme-color-text-secondary, #8a93a5); font-size: 11px; }
		.cst-setchip-new { border-style: dashed; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-setedit { min-height: 40px; }
		.cst-setedit-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
		.cst-setedit-head strong { font-size: 15px; margin-right: 4px; }
		.cst-tile-pick { cursor: pointer; }
		.cst-tile-pick:hover { border-color: var(--theme-color-brand, #378add); }
		.cst-libpick .cst-set-input { width: 100%; box-sizing: border-box; }
		.cst-sticker-del { position: absolute; top: 2px; right: 2px; border: none; background: rgba(0,0,0,0.05); color: var(--theme-color-status-error, #c0392b); border-radius: 4px; font-size: 9px; padding: 1px 5px; cursor: pointer; }
		.cst-sticker-del:hover { background: rgba(192,57,43,0.14); }
		.cst-lane-list { display: flex; flex-direction: column; gap: 8px; max-width: 640px; margin-bottom: 16px; }
		.cst-lane-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; background: var(--theme-color-background-panel, #fff); }
		.cst-lane-row.is-dragging { opacity: 0.45; outline: 2px dashed var(--theme-color-brand-primary, #3b6fd4); outline-offset: -2px; }
		.cst-lane-grip { cursor: grab; color: var(--theme-color-text-secondary, #8a93a5); font-weight: 700; letter-spacing: -1px; user-select: none; flex: 0 0 auto; }
		.cst-lane-color { width: 34px; height: 30px; padding: 0; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; background: none; cursor: pointer; flex: 0 0 auto; }
		.cst-lane-label { flex: 1 1 auto; min-width: 0; }
		.cst-lane-terminal { display: inline-flex; align-items: center; gap: 5px; font-size: 0.82em; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; flex: 0 0 auto; }
		.cst-lane-del { padding: 5px 11px; font-size: 0.8em; background: transparent; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; cursor: pointer; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; flex: 0 0 auto; }
		.cst-lane-del:hover { background: var(--theme-color-status-error-background, #fdecec); border-color: var(--theme-color-status-error, #c0353a); color: var(--theme-color-status-error, #c0353a); }
		/* Plan-sheets settings section */
		.cst-set-divider { border: none; border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); margin: 20px 0; }
		.cst-set-sh { font-size: 13px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 10px; text-transform: uppercase; letter-spacing: 0.04em; }
		.cst-ps-settings-list { display: flex; flex-direction: column; gap: 6px; }
		.cst-ps-settings-row { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border: 1px solid var(--theme-color-border-subtle, #eef1f6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); }
		.cst-ps-settings-row.is-current { background: var(--theme-color-background-selected, rgba(55,138,221,0.08)); border-color: var(--theme-color-brand, rgba(55,138,221,0.3)); }
		.cst-ps-settings-main { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
		.cst-ps-settings-name { font-size: 13px; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-ps-settings-role { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-lane-add { display: flex; align-items: center; gap: 10px; max-width: 640px; }
		.cst-lane-add .cst-set-input { flex: 1 1 auto; min-width: 0; }
		.cst-set-about-host { min-height: 440px; margin-bottom: 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-panel, #fff); }
		.cst-set-token-new { display: flex; gap: 8px; margin-bottom: 14px; }
		.cst-set-token-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 8px; background: var(--theme-color-background-panel, #fff); margin-bottom: 8px; }
		.cst-set-token-main { min-width: 0; }
		.cst-set-token-label { font-weight: 600; font-size: 0.9em; color: var(--theme-color-text-primary, #1d2230); }
		.cst-set-token-meta { font-size: 0.75em; color: var(--theme-color-text-secondary, #5b6376); margin-top: 2px; }
		.cst-set-token-revoke { padding: 5px 11px; font-size: 0.8em; background: transparent; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; cursor: pointer; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; }
		.cst-set-token-revoke:hover { background: var(--theme-color-status-error-background, #fdecec); border-color: var(--theme-color-status-error, #c0353a); color: var(--theme-color-status-error, #c0353a); }
		.cst-token-reveal { display: flex; flex-direction: column; gap: 10px; }
		.cst-token-reveal-warn { margin: 0; font-size: 0.9em; color: var(--theme-color-status-error, #c0353a); }
		.cst-token-value-row { display: flex; gap: 8px; align-items: stretch; }
		.cst-token-value { flex: 1; min-width: 0; padding: 8px 10px; background: var(--theme-color-background-secondary, #f3f5f9); border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; font-family: ui-monospace, Menlo, monospace; font-size: 0.85em; word-break: break-all; color: var(--theme-color-text-primary, #1d2230); }
		.cst-token-copy-btn { padding: 6px 14px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 6px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.85em; white-space: nowrap; }
		.cst-token-reveal-hint { margin: 0; font-size: 0.8em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.5; }
		.cst-token-reveal-hint code, .cst-token-reveal-warn code { font-family: ui-monospace, Menlo, monospace; font-size: 0.92em; background: var(--theme-color-background-secondary, #f3f5f9); padding: 1px 4px; border-radius: 4px; }
		/* The reveal dialog renders inside pict-section-modal (portaled), where the drafting theme's dark
		   variables can fall back to the light hexes above. The mode class lives on <html>, so pair the base
		   rules with explicit html.theme-dark overrides that reach the modal (mirrors PictView-Shell). */
		html.theme-dark .cst-token-reveal-warn { color: var(--theme-color-status-error, #e8896f); }
		html.theme-dark .cst-token-reveal-hint { color: var(--theme-color-text-secondary, #98a2ae); }
		html.theme-dark .cst-token-value { background: var(--theme-color-background-primary, #14171c); border-color: var(--theme-color-border-default, rgba(233,236,240,0.22)); color: var(--theme-color-text-primary, #e9ecf0); }
		html.theme-dark .cst-token-reveal-hint code, html.theme-dark .cst-token-reveal-warn code { background: var(--theme-color-background-primary, #14171c); color: var(--theme-color-text-primary, #e9ecf0); }
		.dk-picker { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
		.dk-current { font-size: 0.9em; color: var(--theme-color-text-secondary, #5b6376); }
		.dk-rows { display: flex; flex-direction: column; gap: 12px; }
		.dk-row { border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
		.dk-row-head { font-weight: 600; font-size: 0.78em; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.dk-fields { display: flex; gap: 12px; flex-wrap: wrap; }
		.dk-field { display: flex; flex-direction: column; gap: 4px; font-size: 0.8em; color: var(--theme-color-text-secondary, #5b6376); flex: 1 1 140px; min-width: 0; }
		.dk-field-desc { flex-basis: 100%; }
		.dk-preview { margin: 14px 0; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); }
		.msg-vars { font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); margin: -4px 0 12px; line-height: 2.1; }
		.msg-var { font-family: ui-monospace, Menlo, monospace; font-size: 0.82em; padding: 2px 7px; margin: 0 3px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); cursor: pointer; }
		.msg-var:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.msg-body { font-family: inherit; resize: vertical; }
		.cst-set-check { display: flex; align-items: center; gap: 8px; margin: 10px 0; font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); }
		.msg-preview { margin: 12px 0; padding: 12px 14px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-secondary, #f7f8fb); }
		.msg-preview:empty { display: none; }
		.msg-preview-label { font-size: 0.72em; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 6px; }
		.msg-preview-subject { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); margin-bottom: 6px; }
		.msg-preview-body { font-size: 0.92em; color: var(--theme-color-text-primary, #1d2230); line-height: 1.5; word-break: break-word; }
		.msg-test { margin-top: 16px; border-top: 1px solid var(--theme-color-border-default, #e6e9ee); padding-top: 12px; }
		.cst-set-btn-danger { background: #c0392b; border-color: #c0392b; }
		.cst-set-placeholder { padding: 18px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; color: var(--theme-color-text-secondary, #5b6376); font-size: 0.9em; }
		.dk-newkit { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin: 6px 0 18px; }
		.dk-newkit-name { flex: 1 1 240px; }
		.dk-kitgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
		.dk-kitcard { border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; background: var(--theme-color-background-secondary, #f7f8fb); }
		.dk-kitcard-head { display: flex; align-items: center; gap: 8px; font-size: 1.02em; color: var(--theme-color-text-primary, #1d2230); }
		.dk-kitcard-sub { font-size: 0.82em; color: var(--theme-color-text-secondary, #5b6376); }
		.dk-kitcard-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
		.dk-kitcard-actions .cst-set-btn { padding: 5px 11px; font-size: 0.82em; }
		.dk-swatch { width: 14px; height: 14px; border-radius: 4px; display: inline-block; flex: none; border: 1px solid rgba(0,0,0,0.12); }
		.dk-housebadge { font-size: 0.68em; text-transform: uppercase; letter-spacing: 0.05em; padding: 2px 8px; border-radius: 999px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; }
		.dk-editor-head { display: flex; flex-direction: column; gap: 10px; margin-bottom: 8px; }
		.dk-back { font-size: 0.85em; color: var(--theme-color-brand-primary, #3b6fd4); text-decoration: none; }
		.dk-editor-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
		.dk-name-in { font-size: 1.1em; font-weight: 600; flex: 1 1 220px; max-width: 360px; }
		.dk-loop-block { margin: 14px 0; padding: 12px 14px; border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; }
		.dk-loop-label { margin-bottom: 8px; }
		.dk-loop-label input { font-weight: 600; }
		.dk-loops-host { min-height: 120px; margin: 4px 0; }
		.dk-loops-fallback { resize: vertical; font-family: ui-monospace, Menlo, monospace; width: 100%; }
		.dk-loops p code { font-family: ui-monospace, Menlo, monospace; font-size: 0.85em; background: var(--theme-color-background-secondary, #f3f5f9); padding: 1px 5px; border-radius: 4px; }
	`,
	Templates:
	[
		{
			Hash: 'UserSettings-Display',
			Template: /*html*/`
<div class="cst-set-page">
	<div class="ps-page-bleed cst-set-content" id="Settings-Content"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'UserSettings-Display',
			TemplateHash: 'UserSettings-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
